import { useEffect, useLayoutEffect, useState, useRef, useMemo, useCallback } from "react";
import DropArrow from "../../../Assets/images/select-arrow.svg";
import { useGlobalContext } from "../../../Context/GlobalContext";
import styles from "../../RightSidebar/rightsidebar.module.css";
import { getTherapistList, getRoomsForCalendar, getServicesList,} from "../../../APICalling/APICalling";
import { calculateEndTime, convertToMinutes, formatDateToDDMMYYYY, convertTo24HourFormat, buildTimeSelection, resolveNearestTimeSelection, getViewRelativeAbsoluteStartMinutes, isServiceEligibleForCrossDayTimeSelection, isTimeBefore6PM, isWeekday, parseExtendedDropdownValue, syncMemberRateAppliedFromFirstServices, scrollToBookingCard, scrollToNewBookingCard } from "../../../Utils/utils";
import { buildProjectedDraftSlot } from "../../../Utils/calendarProjection";
import {
	scheduleServiceMove,
	ensureServiceLinkedFlags,
	isScheduleChainService,
	resolveServiceScheduleKey,
} from "../../../Utils/serviceScheduleChain";
import Guesticon from "../../../Assets/images/guestIcon.svg";
import ServiceDropdown from "./ServiceDropdown";
import ServiceCard from "./ServiceCard";
import TaggingDropdown, { BELOW_12_TAG_OPTION, isBelow12Tag } from "../../DropDown/TaggingDropdown/TaggingDropdown";
import TrashIcon from "../../Icons/TrashIcon";
import { getBookingPreventChanges } from "../../../Utils/BookingPreventChanges";
import logger from "../../../Utils/logger";

const ServiceSelector = ({
	error,
	setError,
	interval,
	fromEdit = false,
	isOutletExtended,
	bookingChanged,
	editBooking,
	handleGetGuestCheckinList,
	handleSelectGuestCheckin,
	getSelectedGuestCheckin,
	guestCheckinList,
	guestCheckinRemoteList = false,
	onGuestCheckinSearchQueryChange = null,
	onGuestCheckinLoadMore = null,
	guestCheckinHasMore = false,
	guestCheckinLoadingMore = false,
	onRoomConflictChange,
	onPaymentInProgressAttempt,
}) => {
	const {
		bookingData,
		setBookingData,
		itemData,
		setItemData,
		setItems,
		customerData,
		selectedOutlet,
		setBookingType,
		setNewAppointmentSlots,
		newSlot,
		setNewSlot,
		homeStaffList,
		selectedServices,
		setSelectedServices,
		paxes,
		setPaxes,
		setEditedItemData,
		setDeletedBookingItemIds,
		selectedOutletData,
		selectedDate,
		selectedTime,
		setSelectedTime,
		isMembershipApplied,
		isEditBookingActive,
		setBookingChanged,
		setTherapistLoading,
		setNoRoomSelected,
		homeRoomList,
		isRoomFilterActive,
		newAppointmentSlots,
		selectedRooms,
		setSelectedRooms,
		publicHoliday,
		editableBooking,
		selectedGuestCheckin,
		setSelectedGuestCheckin,
		filteredItemIds,
		calendarDateSyncSignal,
		setCalendarDateSyncSignal,
	} = useGlobalContext();

	const { preventChanges} = getBookingPreventChanges(editableBooking);
	const isPremiumOutlet = selectedOutletData?.outlet_type_id === 1; // 1 = Premium, 2 = Standard

	const getGuestCheckinPayload = useCallback((key) => {
		const selectedEntry = selectedGuestCheckin?.[key];
		const selected = selectedEntry?.item;
		if (!selected) {
			return {
				hasSelection: !!selectedEntry?.cleared,
				guestCheckinId: null,
				guestCheckinName: null,
				below12: 0,
			};
		}

		const below12 = isBelow12Tag(selected);
		const guestCheckinName = below12
			? null
			: `${selected.first_name || selected.name || ""} ${selected.last_name || selected.lastname || ""}`.trim() || null;

		return {
			hasSelection: true,
			guestCheckinId: below12 ? null : selected.id || null,
			guestCheckinName,
			below12: below12 ? 1 : 0,
		};
	}, [selectedGuestCheckin]);

	const isUnder12CheckinExempt = useCallback((service) => (
		(
			service?.checkin_required === false ||
			service?.checkin_required === 0 ||
			service?.checkin_required === "0" ||
			service?.checkin_required === "false"
		) &&
		service?.checkin_exempt_reason === "under_12"
	), []);

	const resolveSavedBooleanFlag = useCallback((value, fallback = false) => {
		if (value === 1 || value === true || value === "1") return true;
		if (value === 0 || value === false || value === "0") return false;
		return Boolean(fallback);
	}, []);

	// In edit mode, prefer the live sidebar-selected date from context. The local
	// editBooking.date can lag behind during transitional renders.
	const effectiveSelectedDate = useMemo(() => {
		if (fromEdit && selectedDate) {
			return selectedDate;
		}
		if (fromEdit && editBooking?.date) {
			return editBooking.date;
		}
		return selectedDate;
	}, [fromEdit, selectedDate, editBooking?.date]);

	// When calendar drag or edit prefill changes slot times, re-sync member rate flags + prices
	// (effect below only ran on membership/holiday/date — not on start_time changes).
	const memberRateScheduleKey = useMemo(() => {
		const part = (svc) =>
			svc
				? `${svc.counterKey ?? ""}:${svc.start_time ?? ""}:${svc.start_date ?? ""}`
				: "";
		const main = (selectedServices || []).map(part).join("|");
		const pax = (paxes || [])
			.map((p) => (p.selectedServices || []).map(part).join("|"))
			.join("^");
		return `${main}~${pax}`;
	}, [selectedServices, paxes]);

	const selectedOutletID = selectedOutlet || bookingData?.outlet;
	const [isAddingService, setIsAddingService] = useState(false);
	const [isAutoAssigning, setIsAutoAssigning] = useState(false);
	const isAutoAssigningRef = useRef(false);
	const [showDropdown, setShowDropdown] = useState(false);
	const [showServiceDropdown, setShowServiceDropdown] = useState(false);
	const [showReplaceDropdown, setShowReplaceDropdown] = useState(false);
	const [replacingServiceKey, setReplacingServiceKey] = useState(null);
	const [therapistOptionsMap, setTherapistOptionsMap] = useState({});
	const [roomOptions, setRoomOptions] = useState([]);
	const [therapistChangeTrigger, setTherapistChangeTrigger] = useState(0);
    const [selectedServicesForEdit, setSelectedServicesForEdit] = useState([]);
    const EMPTY_OPTIONS = useMemo(() => [], []);
	const uidCounterRef = useRef(1);
	const lastAutoOpenedCustomerIdRef = useRef(null);
	const roomConflictValidationRequestRef = useRef(0);
	/** Persist room availability responses by slot key so unchanged services skip network on drag/edit. */
	const roomAvailabilityCacheRef = useRef(new Map());
	/** Room lists loaded only to check service compatibility on an unchanged edit slot. */
	const roomCompatibilityCacheRef = useRef(new Map());
	/**
	 * Original/last-validated room slot key per booking_item_id.
	 * Used so first drag only fetches room API for services whose slot actually changed.
	 */
	const roomSlotBaselineByItemIdRef = useRef(new Map());
	/** Last therapist/room availability key per counterKey (serviceId|service_at|duration). */
	const serviceAvailabilityKeyRef = useRef(new Map());
	const therapistOptionsMapRef = useRef({});
	const guestInputRefs = useRef({});
	const newlyAddedPaxIdRef = useRef(null);
	const hydratedBookingIdRef = useRef(null);
	const latestSelectedServicesRef = useRef(selectedServices);
	const latestPaxesRef = useRef(paxes);
	const latestCalendarDateSyncSignalRef = useRef(calendarDateSyncSignal);
	const lastDateSyncScrollRequestRef = useRef(null);
	const manualTherapistSelectionRef = useRef({});
	// counterKey -> therapist id cleared by an explicit date change because they were off roster.
	// Restored on a later date change when they are back on roster, unless the user picked someone.
	const rosterClearedTherapistRef = useRef({});

	useEffect(() => {
		rosterClearedTherapistRef.current = {};
	}, [editableBooking?.id]);

	useEffect(() => {
		const liveKeys = new Set([...(Array.isArray(selectedServices) ? selectedServices : []), ...(Array.isArray(paxes) ? paxes : []).flatMap((pax) =>Array.isArray(pax?.selectedServices) ? pax.selectedServices : []),].map((service) => service?.counterKey).filter((key) => key != null).map(String));
		Object.keys(rosterClearedTherapistRef.current).forEach((key) => {if (!liveKeys.has(String(key))) { delete rosterClearedTherapistRef.current[key];}});
	}, [selectedServices, paxes]);
	const prevDateTimeRef = useRef({ date: null, time: null });
	const latestCreateSyncRequestRef = useRef(0);
	const logEditDateSyncDebug = useCallback((label, details = {}) => {
		if (!fromEdit) return;
		try {
		} catch {}
	}, [fromEdit]);
	// Extended outlet business day stays on the same timeline through 5:59 AM +1.
	// Cross-day payload should start only from 6:00 AM +1 onward.
	const extendedBusinessCutoffMinutes = 6 * 60;

	useEffect(() => {
		latestSelectedServicesRef.current = selectedServices;
	}, [selectedServices]);

	useEffect(() => {
		latestPaxesRef.current = paxes;
	}, [paxes]);

	useEffect(() => {
		therapistOptionsMapRef.current = therapistOptionsMap || {};
	}, [therapistOptionsMap]);

	const buildServiceAvailabilityKey = useCallback((serviceId, serviceAt, duration) => {
		return `${serviceId ?? ""}|${serviceAt ?? ""}|${duration ?? ""}`;
	}, []);

	const shouldRefreshServiceAvailability = useCallback((counterKey, serviceId, serviceAt, duration, forceAll = false) => {
		const key = buildServiceAvailabilityKey(serviceId, serviceAt, duration);
		if (forceAll) {
			serviceAvailabilityKeyRef.current.set(counterKey, key);
			return true;
		}
		const prev = serviceAvailabilityKeyRef.current.get(counterKey);
		if (prev === key) return false;
		serviceAvailabilityKeyRef.current.set(counterKey, key);
		return true;
	}, [buildServiceAvailabilityKey]);

	const getCachedRoomAvailability = useCallback((cacheKey) => {
		if (!cacheKey) return null;
		return roomAvailabilityCacheRef.current.has(cacheKey)
			? roomAvailabilityCacheRef.current.get(cacheKey)
			: null;
	}, []);

	const setCachedRoomAvailability = useCallback((cacheKey, roomsList) => {
		if (!cacheKey) return;
		roomAvailabilityCacheRef.current.set(cacheKey, roomsList);
		// Bound cache growth across long sessions
		if (roomAvailabilityCacheRef.current.size > 80) {
			const oldestKey = roomAvailabilityCacheRef.current.keys().next().value;
			roomAvailabilityCacheRef.current.delete(oldestKey);
		}
	}, []);

	const buildRoomConflictCacheKey = useCallback((outletId, dateStr, serviceId, duration, startTime) => {
		return `${outletId}|${dateStr}|${serviceId}|${duration}|${startTime}`;
	}, []);
	useEffect(() => {
		latestCalendarDateSyncSignalRef.current = calendarDateSyncSignal;
	}, [calendarDateSyncSignal]);

	useEffect(() => {
		const customerId = customerData?.id ?? null;
		if (fromEdit) return;

		if (!customerId) {
			lastAutoOpenedCustomerIdRef.current = null;
			return;
		}

		const hasNoServices = selectedServices.length === 0 && paxes.length === 0;
		const hasAutoOpenedForCustomer = lastAutoOpenedCustomerIdRef.current === customerId;

		if (hasNoServices && !hasAutoOpenedForCustomer) {
			setShowServiceDropdown(true);
			lastAutoOpenedCustomerIdRef.current = customerId;
		}
	}, [fromEdit, customerData?.id, selectedServices.length, paxes.length]);

	const isTimeAtOrAfterExtendedBoundary = (timeStr) => {
		const timeMinutes = convertToMinutes(timeStr);
		return !Number.isNaN(timeMinutes) && timeMinutes >= extendedBusinessCutoffMinutes;
	};

	const isExtendedBoundaryCrossDayService = (serviceLike, baseDateStr = getDefaultServiceDateStr()) => {
		if (!isOutletExtended) return false;
		const startDate = serviceLike?.start_date || null;
		const startTime = serviceLike?.start_time || null;
		if (!startDate || !startTime) return false;
		if (!isTimeAtOrAfterExtendedBoundary(startTime)) return false;
		return getServiceDayOffset(startDate) > 0 || (baseDateStr ? startDate !== baseDateStr : false);
	};

	const isMixedNormalAndExtendedCrossDayService = (startDate, endDate, startTime, endTime) => {
		if (!isOutletExtended) return false;
		if (!startDate || !endDate || startDate === endDate) return false;
		const startMinutes = convertToMinutes(startTime);
		const endMinutes = convertToMinutes(endTime);
		if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) return false;
		// endMinutes === 0 means the service ends exactly at midnight — zero real duration on
		// end_date, so it's a boundary rollover, not a genuine split with presence on both days.
		if (endMinutes === 0) return false;
		return startMinutes >= extendedBusinessCutoffMinutes && endMinutes < extendedBusinessCutoffMinutes;
	};

	const buildMidnightSplitCrossDayItems = (startDate, endDate, startTime, endTime) => {
		const startMinutes = convertToMinutes(startTime);
		const endMinutes = convertToMinutes(endTime);
		if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) return null;
		return [
			{
				start_time: startTime,
				end_time: "00:00",
				duration: (24 * 60) - startMinutes,
				start_date: startDate,
				end_date: startDate,
			},
			{
				start_time: "00:00",
				end_time: endTime,
				duration: endMinutes,
				start_date: endDate,
				end_date: endDate,
			},
		];
	};

	const buildExpandedCoverageCrossDayItems = (actualDate, startTime, endTime) => {
		if (!actualDate) return null;
		const startMinutes = convertToMinutes(startTime);
		const endMinutes = convertToMinutes(endTime);
		if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) return null;
		const coveredDate = formatDateToDDMMYYYY(addDaysSafe(actualDate, -1));
		const duration = endMinutes >= startMinutes
			? endMinutes - startMinutes
			: (endMinutes + (24 * 60)) - startMinutes;
		return [
			{
				start_time: startTime,
				end_time: endTime,
				duration,
				start_date: coveredDate,
				end_date: coveredDate,
			},
			{
				start_time: startTime,
				end_time: endTime,
				duration,
				start_date: actualDate,
				end_date: actualDate,
			},
		];
	};

	const isEarlyMorningBoundaryTimeRange = (startTime, endTime) => {
		const startMinutes = convertToMinutes(startTime);
		const endMinutes = convertToMinutes(endTime);
		if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) return false;
		return startMinutes >= 0 &&
			startMinutes < extendedBusinessCutoffMinutes &&
			endMinutes > extendedBusinessCutoffMinutes;
	};

	const isEarlyMorningBoundarySplitService = (startDate, endDate, startTime, endTime) => {
		if (!startDate || !endDate || startDate !== endDate) return false;
		return isEarlyMorningBoundaryTimeRange(startTime, endTime);
	};

	const buildEarlyMorningBoundarySplitItems = (actualDate, startTime, endTime) => {
		if (!actualDate) return null;
		const startMinutes = convertToMinutes(startTime);
		const endMinutes = convertToMinutes(endTime);
		if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) return null;
		const coveredDate = formatDateToDDMMYYYY(addDaysSafe(actualDate, -1));
		return [
			{
				start_time: startTime,
				end_time: "06:00",
				duration: extendedBusinessCutoffMinutes - startMinutes,
				start_date: coveredDate,
				end_date: coveredDate,
			},
			{
				start_time: "06:00",
				end_time: endTime,
				duration: endMinutes - extendedBusinessCutoffMinutes,
				start_date: actualDate,
				end_date: actualDate,
			},
		];
	};

	const parseServiceCategoriesFromCache = (rawCacheValue) => {
		if (!rawCacheValue) return [];
		try {
			const parsed = JSON.parse(rawCacheValue);
			if (Array.isArray(parsed)) return parsed;
			return parsed?.data?.list?.category || parsed?.data?.data?.list?.category || [];
		} catch {
			return [];
		}
	};

	const resolveServiceId = (serviceLike) => {
		if (!serviceLike) return null;
		const candidates = [
			serviceLike?.id,
			serviceLike?.service_id,
			serviceLike?.service?.id,
			serviceLike?.service?.value,
			serviceLike?.service,
		];

		for (const candidate of candidates) {
			const n = Number(candidate);
			if (Number.isFinite(n)) return n;
		}
		return null;
	};

	const getCachedServiceFromSession = (serviceId) => {
		if (!Number.isFinite(serviceId)) return null;

		try {
			const keysToSearch = ["serviceList"];
			for (let i = 0; i < sessionStorage.length; i++) {
				const key = sessionStorage.key(i);
				if (!key || key === "serviceList") continue;
				if (key.startsWith("serviceList_") && !key.endsWith("_etag")) {
					keysToSearch.push(key);
				}
			}

			for (const key of keysToSearch) {
				const categories = parseServiceCategoriesFromCache(sessionStorage.getItem(key));
				for (const category of categories || []) {
					const found = (category?.services || []).find(
						(svc) => Number(svc?.id) === serviceId
					);
					if (found) return found;
				}
			}
		} catch {
			return null;
		}

		return null;
	};

	const getCachedServiceCategoryFromSession = (serviceId) => {
		if (!Number.isFinite(serviceId)) return null;

		try {
			const keysToSearch = ["serviceList"];
			for (let i = 0; i < sessionStorage.length; i++) {
				const key = sessionStorage.key(i);
				if (!key || key === "serviceList") continue;
				if (key.startsWith("serviceList_") && !key.endsWith("_etag")) {
					keysToSearch.push(key);
				}
			}

			for (const key of keysToSearch) {
				const categories = parseServiceCategoriesFromCache(sessionStorage.getItem(key));
				for (const category of categories || []) {
					const found = (category?.services || []).find(
						(svc) => Number(svc?.id) === serviceId
					);
					if (found) {
						return {
							id: category?.id ?? category?.category_id ?? found?.service_category_id ?? null,
							name: category?.name || category?.category_name || found?.service_category_name || found?.service_category || "",
						};
					}
				}
			}
		} catch {
			return null;
		}

		return null;
	};

	const resolveServiceCategoryName = (serviceLike) => {
		const existing =
			serviceLike?.service_category_name ||
			serviceLike?.service_category ||
			serviceLike?.category_name ||
			serviceLike?.category?.name;
		if (existing) return String(existing);

		const serviceId = resolveServiceId(serviceLike);
		if (!Number.isFinite(serviceId)) return "";
		return getCachedServiceCategoryFromSession(serviceId)?.name || "";
	};

	const resolveReplacementCategoryName = (newService, previousService) => {
		const newCategory = resolveServiceCategoryName(newService);
		if (newCategory) return newCategory;
		const newId = resolveServiceId(newService);
		const oldId = resolveServiceId(previousService);
		if (newId != null && oldId != null && Number(newId) === Number(oldId)) {
			return resolveServiceCategoryName(previousService);
		}
		return "";
	};

	const resolveServiceCategoryId = (serviceLike) => {
		const existing =
			serviceLike?.service_category_id ||
			serviceLike?.category_id ||
			serviceLike?.category?.id;
		const existingNumber = Number(existing);
		if (Number.isFinite(existingNumber)) return existingNumber;

		const serviceId = resolveServiceId(serviceLike);
		if (!Number.isFinite(serviceId)) return null;
		const cachedId = Number(getCachedServiceCategoryFromSession(serviceId)?.id);
		return Number.isFinite(cachedId) ? cachedId : null;
	};

	const resolveServicePriceRate = (serviceLike) => {
		if (serviceLike?.price_rate) return serviceLike.price_rate;
		const serviceId = resolveServiceId(serviceLike);
		if (!Number.isFinite(serviceId)) return null;
		const cachedService = getCachedServiceFromSession(serviceId) || servicePriceMap.get(serviceId);
		return cachedService?.price_rate || null;
	};

	// Build a quick lookup of serviceId -> { price, member_price, name, price_rate } from cached catalog
	const servicePriceMap = useMemo(() => {
		try {
			const map = new Map();
			const keysToRead = ["serviceList"];
			for (let i = 0; i < sessionStorage.length; i++) {
				const key = sessionStorage.key(i);
				if (!key || key === "serviceList") continue;
				if (key.startsWith("serviceList_") && !key.endsWith("_etag")) {
					keysToRead.push(key);
				}
			}

			keysToRead.forEach((key) => {
				const categories = parseServiceCategoriesFromCache(sessionStorage.getItem(key));
				(categories || []).forEach((cat) =>
					(cat?.services || []).forEach((svc) => {
						if (svc?.id != null) {
							map.set(Number(svc.id), {
								price: svc.price,
								member_price: svc.member_price,
								name: svc.name,
								duration: svc.duration,
								price_rate: svc.price_rate || null,
								service_category_name: cat?.name || cat?.category_name || "",
							});
						}
					})
				);
			});
			return map;
		} catch {
			return new Map();
		}
	}, []);

	const normalizeDateInput = (input) => {
		if (!input) return null;
		if (input instanceof Date) return input;
		if (typeof input === "string") {
			if (/^\d{2}-\d{2}-\d{4}$/.test(input)) {
				const [dd, mm, yyyy] = input.split("-");
				return new Date(`${yyyy}-${mm}-${dd}`);
			}
			if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
				return new Date(input);
			}
		}
		const parsed = new Date(input);
		return isNaN(parsed) ? null : parsed;
	};

	const getDefaultServiceDateStr = () => {
		const fromSelected = normalizeDateInput(effectiveSelectedDate);
		if (fromSelected) return formatDateToDDMMYYYY(fromSelected);

		const fromBooking = normalizeDateInput(bookingData?.date);
		if (fromBooking) return formatDateToDDMMYYYY(fromBooking);

		const fromItem = (() => {
			if (!itemData?.service_at) return null;
			const [datePart] = String(itemData.service_at).split(" ");
			return normalizeDateInput(datePart);
		})();
		if (fromItem) return formatDateToDDMMYYYY(fromItem);

		return formatDateToDDMMYYYY(new Date());
	};

	const getTimelineDayOffset = useCallback((timeSelection = null) => {
		const source = timeSelection || selectedTime || bookingData?.time || null;
		if (!source) return 0;

		if (typeof source?.absoluteMinutes === "number" && !Number.isNaN(source.absoluteMinutes)) {
			return Math.floor(source.absoluteMinutes / 1440);
		}

		if (typeof source?.dayOffset === "number" && !Number.isNaN(source.dayOffset)) {
			return source.dayOffset;
		}

		if (source?.isNextDay === true) {
			return 1;
		}

		if (typeof source?.label === "string" && /\(\+1\)|\+1/.test(source.label)) {
			return 1;
		}

		const parsedSelection = parseExtendedDropdownValue(source?.value || "");
		return parsedSelection?.isNextDay ? 1 : 0;
	}, [bookingData?.time, selectedTime]);

	const getTimelineBaseDateStr = useCallback((timeSelection = null) => {
		const baseDateStr = getDefaultServiceDateStr();
		const dayOffset = getTimelineDayOffset(timeSelection);
		return formatDateToDDMMYYYY(addDaysSafe(baseDateStr, dayOffset));
	}, [getTimelineDayOffset]);

	const parseDDMMYYYYToDate = (dateStr) => {
		if (!dateStr || typeof dateStr !== "string") return null;
		const [dd, mm, yyyy] = dateStr.split("-");
		if (!dd || !mm || !yyyy) return null;
		const parsed = new Date(`${yyyy}-${mm}-${dd}`);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	};

	const normalizeNumericId = useCallback((value) => {
		if (value == null || value === "") return null;
		if (typeof value === "object") {
			const nestedValue = value?.value ?? value?.id ?? value?.item_id ?? value?.room_id ?? null;
			if (nestedValue == null || nestedValue === "") return null;
			const normalizedNested = Number(nestedValue);
			return Number.isNaN(normalizedNested) ? null : normalizedNested;
		}
		const normalized = Number(value);
		return Number.isNaN(normalized) ? null : normalized;
	}, []);

	// Home draft drag stamps assigned therapists so create date/time sync won't wipe them.
	useEffect(() => {
		const onStamp = (e) => {
			const counterKey = e?.detail?.counterKey;
			const therapistId = normalizeNumericId(e?.detail?.therapistId);
			if (counterKey == null || therapistId == null) return;
			manualTherapistSelectionRef.current[counterKey] = therapistId;
		};
		window.addEventListener("stampManualTherapistSelection", onStamp);
		return () => window.removeEventListener("stampManualTherapistSelection", onStamp);
	}, [normalizeNumericId]);

	const getBookingItemId = useCallback((service) =>
		normalizeNumericId(service?.booking_item_id ?? null)
	, [normalizeNumericId]);

	const lockedBookingItemPricingById = useMemo(() => {
		if (!fromEdit || !preventChanges || !editableBooking?.booking_item) {
			return new Map();
		}
		const map = new Map();
		Object.values(editableBooking.booking_item || {}).forEach((services) => {
			if (!Array.isArray(services)) return;
			services.forEach((svc) => {
				const bookingItemId = normalizeNumericId(svc?.id ?? svc?.booking_item_id);
				if (bookingItemId == null) return;
				map.set(bookingItemId, {
					isMemberRateApplied: svc?.isMemberRateApplied,
					isMembershipApplied: svc?.isMembershipApplied,
					price: svc?.price,
				});
			});
		});
		return map;
	}, [fromEdit, preventChanges, editableBooking?.booking_item, normalizeNumericId]);

	const getLockedBookingItemPricing = useCallback((serviceLike) => {
		if (!fromEdit || !preventChanges) return null;
		const bookingItemId = normalizeNumericId(serviceLike?.booking_item_id ?? serviceLike?.id);
		if (bookingItemId == null) return null;
		return lockedBookingItemPricingById.get(bookingItemId) || null;
	}, [fromEdit, preventChanges, normalizeNumericId, lockedBookingItemPricingById]);

	const applyLockedBookingItemPricing = useCallback((serviceLike) => {
		const lockedPricing = getLockedBookingItemPricing(serviceLike);
		if (!lockedPricing) return serviceLike;
		const lockedPrice = lockedPricing.price;
		const nextService = {
			...serviceLike,
			isMemberRateApplied: lockedPricing.isMemberRateApplied,
			isMembershipApplied: lockedPricing.isMembershipApplied,
		};
		if (lockedPrice !== undefined) {
			nextService.price = lockedPrice;
		}
		return nextService;
	}, [getLockedBookingItemPricing]);

	useEffect(() => {
		if (!fromEdit || !preventChanges || lockedBookingItemPricingById.size === 0) return;

		setSelectedServices((prevServices) => {
			if (!Array.isArray(prevServices) || prevServices.length === 0) return prevServices;
			let changed = false;
			const nextServices = prevServices.map((service) => {
				const locked = getLockedBookingItemPricing(service);
				if (!locked) return service;
				const lockedPrice = locked.price;
				const priceChanged = lockedPrice !== undefined && service?.price !== lockedPrice;
				if (
					service?.isMemberRateApplied !== locked.isMemberRateApplied ||
					service?.isMembershipApplied !== locked.isMembershipApplied ||
					priceChanged
				) {
					changed = true;
					return applyLockedBookingItemPricing(service);
				}
				return service;
			});
			return changed ? nextServices : prevServices;
		});

		setPaxes((prevPaxes) => {
			if (!Array.isArray(prevPaxes) || prevPaxes.length === 0) return prevPaxes;
			let changed = false;
			const nextPaxes = prevPaxes.map((pax) => {
				const list = Array.isArray(pax?.selectedServices) ? pax.selectedServices : [];
				if (list.length === 0) return pax;
				let paxChanged = false;
				const nextSelectedServices = list.map((service) => {
					const locked = getLockedBookingItemPricing(service);
					if (!locked) return service;
					const lockedPrice = locked.price;
					const priceChanged = lockedPrice !== undefined && service?.price !== lockedPrice;
					if (
						service?.isMemberRateApplied !== locked.isMemberRateApplied ||
						service?.isMembershipApplied !== locked.isMembershipApplied ||
						priceChanged
					) {
						paxChanged = true;
						return applyLockedBookingItemPricing(service);
					}
					return service;
				});
				if (!paxChanged) return pax;
				changed = true;
				return {
					...pax,
					selectedServices: nextSelectedServices,
				};
			});
			return changed ? nextPaxes : prevPaxes;
		});
	}, [fromEdit, preventChanges, lockedBookingItemPricingById, getLockedBookingItemPricing, applyLockedBookingItemPricing, setSelectedServices, setPaxes, selectedServices, paxes]);

	const getTherapistId = useCallback((service) =>
		normalizeNumericId(
			service?.therapist ??
			service?.therapist_id ??
			service?.selected_therapist?.value ??
			service?.selected_therapist?.id ??
			null
		)
	, [normalizeNumericId]);

	const buildTherapistOptions = useCallback((staffList = []) =>
		(Array.isArray(staffList) ? staffList : []).map((staff) => ({
			value: normalizeNumericId(staff?.id),
			label: staff?.alias || staff?.name || "",
			pager: staff?.pager_number || 'N/A',
			gender: staff?.gender || "",
			is_service_provided: staff?.is_service_provided ?? true,
			is_available: staff?.is_available ?? true,
		})).filter((staff) => staff.value != null)
	, [normalizeNumericId]);

	const extractTherapistStaffList = useCallback((response) => {
		const payload = response?.data ?? response;
		return payload?.data?.list?.staffs
			|| payload?.data?.data?.list?.staffs
			|| payload?.data?.data?.data?.list?.staffs
			|| [];
	}, []);

	const buildAutoAssignTherapistRequestPayload = useCallback((serviceId, serviceAt, duration) => ({
		services: serviceId,
		duration,
		service_at: serviceAt,
		outlet: selectedOutlet,
		outlet_type: selectedOutletData?.outlet_type_id,
		availability: 0,
		pagination: 0,
		status: 1,
		role: 2,
		leave: 0,
		calendar: 1,
	}), [selectedOutlet, selectedOutletData?.outlet_type_id]);

	const buildSelectedTherapistFromOptions = useCallback((therapistId, options = [], fallbackTherapist = null) => {
		const normalizedTherapistId = normalizeNumericId(therapistId);
		if (normalizedTherapistId == null) return null;

		const matchedOption = (Array.isArray(options) ? options : []).find(
			(option) => normalizeNumericId(option?.value) === normalizedTherapistId
		);

		if (matchedOption) {
			return {
				value: normalizedTherapistId,
				label: matchedOption?.label || "",
				pager: matchedOption?.pager || "",
				gender: matchedOption?.gender || "",
			};
		}

		if (fallbackTherapist) {
			return {
				value: normalizedTherapistId,
				label: fallbackTherapist?.alias || fallbackTherapist?.name || fallbackTherapist?.label || "",
				pager: fallbackTherapist?.pager_number || fallbackTherapist?.pager || "",
				gender: fallbackTherapist?.gender || "",
			};
		}

		return null;
	}, [normalizeNumericId]);

	const getRoomId = useCallback((service) =>
		normalizeNumericId(
			service?.roomId ??
			service?.room ??
			service?.room_segments?.[0]?.room_id ??
			service?.room_items?.[0]?.item_id ??
			service?.room_items?.[0]?.room_id ??
			null
		)
	, [normalizeNumericId]);

	const getRoomName = useCallback((service) =>
		service?.roomName ??
		service?.room_name ??
		service?.room_segments?.[0]?.room_name ??
		service?.room_items?.[0]?.room_name ??
		""
	, []);

	const getDerivedRoomId = useCallback((service) => {
		const directRoomId = getRoomId(service);
		if (directRoomId != null) return directRoomId;
		return normalizeNumericId(
			selectedRooms?.[service?.counterKey]?.value ??
			selectedRooms?.[service?.counterKey]?.item_id ??
			null
		);
	}, [getRoomId, normalizeNumericId, selectedRooms]);

	const isServiceAssigned = useCallback((service) =>
		isRoomFilterActive ? getDerivedRoomId(service) != null : getTherapistId(service) != null
	, [getDerivedRoomId, getTherapistId, isRoomFilterActive]);

	const matchesServiceTarget = useCallback((candidate, serviceCounterKey, bookingItemId) =>
		String(candidate?.counterKey) === String(serviceCounterKey) ||
		(bookingItemId != null && getBookingItemId(candidate) === bookingItemId)
	, [getBookingItemId]);

	const buildSelectedTherapistOption = useCallback((therapistValue, therapistObj) => {
		if (therapistValue == null) return null;
		return {
			value: therapistValue,
			label: therapistObj?.label || therapistObj?.name || therapistObj?.alias || "",
			pager: therapistObj?.pager || therapistObj?.pager_number || "",
			gender: therapistObj?.gender || "",
		};
	}, []);

	const resolveTherapistForCreateSync = useCallback((serviceLike, therapistOptions = [], syncOptions = {}) => {
		const { allowClearWhenUnavailable = true } = syncOptions || {};
		const serviceTherapistId = normalizeNumericId(getTherapistId(serviceLike));
		let manualSelectedTherapistId = normalizeNumericId(
			manualTherapistSelectionRef.current?.[serviceLike?.counterKey]
		);

		// Calendar drag updates selectedServices but not manualTherapistSelectionRef.
		// Seed / sync the ref from live service state so later create sync keeps the assignment.
		if (
			serviceTherapistId != null &&
			serviceLike?.counterKey != null &&
			(
				manualSelectedTherapistId == null ||
				serviceTherapistId !== manualSelectedTherapistId
			) &&
			(
				serviceLike?.hasManualTherapistSelection === true ||
				serviceLike?.isAutoAssignedTherapist === false
			)
		) {
			manualTherapistSelectionRef.current[serviceLike.counterKey] = serviceTherapistId;
			manualSelectedTherapistId = serviceTherapistId;
		}

		const currentTherapistId = serviceTherapistId ?? manualSelectedTherapistId;
		if (currentTherapistId == null) {
			return { therapistId: null, manualSelectedTherapistId };
		}

		if (
			manualSelectedTherapistId != null ||
			serviceLike?.hasManualTherapistSelection === true ||
			serviceLike?.isAutoAssignedTherapist === false
		) {
			return { therapistId: currentTherapistId, manualSelectedTherapistId };
		}

		const availableTherapistIds = (Array.isArray(therapistOptions) ? therapistOptions : [])
			.map((option) => normalizeNumericId(option?.value))
			.filter((value) => value != null);

		// Stale/empty option lists (or placeholder "No Therapist Available") must not wipe
		// an already-assigned therapist during create sync / add-service refreshes.
		if (availableTherapistIds.length === 0 || allowClearWhenUnavailable === false) {
			return { therapistId: currentTherapistId, manualSelectedTherapistId };
		}

		const isStillAvailable = availableTherapistIds.some((value) => value === currentTherapistId);

		if (!isStillAvailable && serviceLike?.counterKey != null) {
			delete manualTherapistSelectionRef.current[serviceLike.counterKey];
		}

		const nextTherapistId = isStillAvailable ? currentTherapistId : null;

		return {
			therapistId: nextTherapistId,
			manualSelectedTherapistId,
		};
	}, [fromEdit, getTherapistId, normalizeNumericId]);

	const buildRoomSegments = useCallback((service, roomId, roomMeta = {}) => {
		if (roomId == null) return null;
		const duration = Number(service?.duration) || 0;
		const startTime = service?.start_time || null;
		const endTime =
			service?.end_time && service.end_time !== "00:00"
				? service.end_time
				: (startTime && duration > 0 ? calculateEndTime(startTime, duration) : "00:00");

		const segmentFacilities = Array.isArray(roomMeta?.facilities)
			? roomMeta.facilities
			: (Array.isArray(service?.room_segments?.[0]?.facilities)
				? service.room_segments[0].facilities
				: []);

		return [{
			room_id: roomId,
			item_type:
				roomMeta?.item_type ??
				roomMeta?.item ??
				service?.room_segments?.[0]?.item_type ??
				service?.room_items?.[0]?.item_type ??
				null,
			meta_service: service?.room_segments?.[0]?.meta_service ?? null,
			start_time: startTime,
			end_time: endTime,
			duration,
			priority: service?.room_segments?.[0]?.priority ?? 1,
			requested_room: service?.requested_room ?? 0,
			room_type:
				roomMeta?.room_type ??
				service?.room_segments?.[0]?.room_type ??
				null,
			facilities: segmentFacilities,
		}];
	}, []);

	const getRenderedColumnIndex = useCallback((targetId, fallbackResolver) => {
		const normalizedTargetId = normalizeNumericId(targetId);
		if (normalizedTargetId == null) return -1;

		try {
			const headerCells = Array.from(document.querySelectorAll("thead th[data-therapist-id]"));
			const visibleIndex = headerCells.findIndex(
				(th) => Number(th.getAttribute("data-therapist-id")) === Number(normalizedTargetId)
			);
			if (visibleIndex >= 0) {
				return visibleIndex;
			}
		} catch {}

		return typeof fallbackResolver === "function" ? fallbackResolver(normalizedTargetId) : -1;
	}, [normalizeNumericId]);

	const addDaysSafe = (dateInput, days) => {
		const base = dateInput instanceof Date ? dateInput : parseDDMMYYYYToDate(dateInput) || new Date();
		const clone = new Date(base);
		clone.setDate(clone.getDate() + Number(days || 0));
		return clone;
	};

	const getServiceDayOffset = (serviceLike) => {
		if (serviceLike && typeof serviceLike === "object") {
			if (typeof serviceLike?.dayOffset === "number" && !Number.isNaN(serviceLike.dayOffset)) {
				return Math.max(0, serviceLike.dayOffset);
			}
			if (typeof serviceLike?.absoluteMinutes === "number" && !Number.isNaN(serviceLike.absoluteMinutes)) {
				return Math.max(0, Math.floor(serviceLike.absoluteMinutes / 1440));
			}
		}

		const serviceDateStr = typeof serviceLike === "string"
			? serviceLike
			: serviceLike?.start_date;
		const baseDate = parseDDMMYYYYToDate(getDefaultServiceDateStr());
		const serviceDate = parseDDMMYYYYToDate(serviceDateStr);
		if (!baseDate || !serviceDate) return 0;
		const dayMs = 24 * 60 * 60 * 1000;
		return Math.max(0, Math.round((serviceDate.setHours(0, 0, 0, 0) - baseDate.setHours(0, 0, 0, 0)) / dayMs));
	};

	const getServiceAbsoluteStartMinutes = (service) => {
		if (typeof service?.absoluteMinutes === "number" && !Number.isNaN(service.absoluteMinutes)) {
			return service.absoluteMinutes;
		}
		const startTime = service?.start_time ? String(service.start_time).slice(0, 5) : "";
		if (!startTime) return 0;
		const startMinutes = convertToMinutes(startTime);
		const dayOffset = getServiceDayOffset(service);
		return (dayOffset * 1440) + startMinutes;
	};

	const getDateStrForAbsoluteMinutes = (absoluteMinutes) => {
		const signedDay = Math.floor(Number(absoluteMinutes || 0) / 1440);
		return formatDateToDDMMYYYY(addDaysSafe(getDefaultServiceDateStr(), signedDay));
	};

	const getCalendarRowIndexForService = (service, intervalMinutes = interval) => {
		const absoluteMinutes = getServiceAbsoluteStartMinutes(service);
		return Math.max(0, Math.floor(absoluteMinutes / Number(intervalMinutes || 15)));
	};

	const getServiceAtDateStr = (service) => {
		if (service?.start_date) return service.start_date;
		return formatDateToDDMMYYYY(addDaysSafe(getDefaultServiceDateStr(), getServiceDayOffset(service)));
	};

	const buildOverrideServiceAt = (service) => {
		const serviceDate = parseDDMMYYYYToDate(getServiceAtDateStr(service));
		const serviceTime = service?.start_time ? String(service.start_time).slice(0, 5) : "";
		if (!serviceDate || !serviceTime) return undefined;
		const year = serviceDate.getFullYear();
		const month = String(serviceDate.getMonth() + 1).padStart(2, "0");
		const day = String(serviceDate.getDate()).padStart(2, "0");
		return `${year}-${month}-${day} ${serviceTime}:00`;
	};

	const buildSelectedTimePayloadForService = (timeValue, serviceLike = null) => {
		const normalizedTimeValue = typeof timeValue === "string" ? timeValue.slice(0, 5) : "";
		if (!normalizedTimeValue) return null;
		const dayOffset = getServiceDayOffset(serviceLike);
		return buildTimeSelection(
			normalizedTimeValue,
			dayOffset,
			"",
			(dayOffset * 1440) + convertToMinutes(normalizedTimeValue)
		);
	};

	const getServiceTimelineMeta = (startTime, serviceLike = null) => {
		const normalizedTimeValue = typeof startTime === "string" ? startTime.slice(0, 5) : "";
		const dayOffset = getServiceDayOffset(serviceLike);
		const absoluteMinutes = normalizedTimeValue
			? (dayOffset * 1440) + convertToMinutes(normalizedTimeValue)
			: null;
		return {
			dayOffset,
			absoluteMinutes,
		};
	};

	const normalizeRoomServiceAtTime = (timeValue) =>
		typeof timeValue === "string" ? timeValue.slice(0, 5) : "";

	const getRoomAvailabilityDateStr = (serviceDateStr) => {
		if (!serviceDateStr || !isOutletExtended) return serviceDateStr;
		const baseDateStr = getDefaultServiceDateStr();
		const baseDate = parseDDMMYYYYToDate(baseDateStr);
		const serviceDate = parseDDMMYYYYToDate(serviceDateStr);
		if (!baseDate || !serviceDate) return serviceDateStr;
		return serviceDate > baseDate ? baseDateStr : serviceDateStr;
	};

	const getTherapistListServiceAt = (serviceAt) => {
		if (!serviceAt || !isOutletExtended) return serviceAt;
		const [datePart, ...timeParts] = String(serviceAt).split(" ");
		const timePart = timeParts.join(" ");
		if (!datePart || !timePart) return serviceAt;
		return `${getRoomAvailabilityDateStr(datePart)} ${timePart}`;
	};

	// Returns -1 when the therapist's column is hidden (gender/staff filter or search),
	// so callers must not scroll to a cell in that case.
	const getVisibleTherapistColIndex = (therapistId) => {
		try {
			const headerCells = Array.from(document.querySelectorAll('table thead th[data-therapist-id]'));
			return headerCells.findIndex(
				(th) => Number(th.getAttribute('data-therapist-id')) === Number(therapistId)
			);
		} catch {return -1;}
	};

	const crossesMidnight = (startTime, endTime, duration) => {
		if (!startTime) return false;
		const startMinutes = convertToMinutes(startTime);
		const endMinutes = endTime ? convertToMinutes(endTime) : null;
		const durationVal = Number(duration);
		const hasDuration = Number.isFinite(durationVal) && durationVal > 0;
		const minutesInDay = 24 * 60;

		// If start_time is "00:00" or later (meaning it's already the next day), it crosses midnight
		if (startTime === "00:00" || startMinutes === 0) {
			return true;
		}

		// If end_time lands exactly on midnight ("00:00"), the service ran through to the next
		// day (e.g. 23:00 + 60min ends at 00:00 the next day) — this must not be treated as
		// "same day", otherwise the next chained service inherits the wrong base date and loses
		// its "+1" day indicator.
		if (endMinutes === 0) {
			return true;
		}

		// If duration pushes past (or exactly to) midnight, it crosses into the next day
		if (hasDuration && startMinutes + durationVal >= minutesInDay) {
			return true;
		}

		// If end_time is less than start_time, it crossed midnight
		if (endMinutes !== null && endMinutes < startMinutes) {
			return true;
		}

		return false;
	};

	const ensureServiceDates = (
		existingStart = null,
		existingEnd = null,
		startTime = null,
		endTime = null,
		duration = null,
		preferredStartDate = null
	) => {
		// If preferredStartDate is provided (from previous service), use it as the base
		// Otherwise, use existingStart or fallback to default
		const baseDate = preferredStartDate || existingStart || getDefaultServiceDateStr();
		let startDateStr = baseDate;
		let endDateStr = existingEnd || startDateStr;

		if (!startTime) {
			return { startDateStr, endDateStr };
		}

		const startMinutes = convertToMinutes(startTime);

		// If start_time is "00:00" or any time in the early morning (00:00 to 06:00)
		if (startMinutes >= 0 && startMinutes < extendedBusinessCutoffMinutes) {
			// Early-morning clock times can belong to either the same-day timeline
			// or the explicit next-day (+1) timeline. Trust the supplied date anchor
			// instead of forcing every 00:00-05:59 service into the next day.
			startDateStr = preferredStartDate || existingStart || startDateStr;
			endDateStr = startDateStr; // Both dates are the same
		} else if (crossesMidnight(startTime, endTime, duration)) {
			// Service crosses midnight (but doesn't start in early morning)
			// Only increment end_date, start_date stays the same
			const endDateObj = addDaysSafe(startDateStr, 1);
			endDateStr = formatDateToDDMMYYYY(endDateObj);
		} else {
			// Service doesn't cross midnight, both dates are the same
			endDateStr = startDateStr;
		}

		return { startDateStr, endDateStr };
	};

	const getBaseStartDateForMainService = (index, timeSelection = null) => {
		if (timeSelection) {
			return getTimelineBaseDateStr(timeSelection);
		}
		if (index <= 0) return getTimelineBaseDateStr(timeSelection);
		const prev = selectedServices[index - 1];
		if (!prev) return getTimelineBaseDateStr(timeSelection);
		
		const prevEndTime = prev.end_time;
		const prevEndMinutes = prevEndTime ? convertToMinutes(prevEndTime) : null;
		
		// If previous service ended at or after midnight (00:00 to 06:00), use its end_date
		// This ensures services following a post-midnight service also use the next day
		if (prevEndMinutes !== null && prevEndMinutes >= 0 && prevEndMinutes < extendedBusinessCutoffMinutes) {
			// Previous service ended after midnight, use its end_date (which should be next day)
			return prev?.end_date || formatDateToDDMMYYYY(addDaysSafe(prev?.start_date || getDefaultServiceDateStr(), 1));
		}
		
		// Otherwise, use the end_date if available, or start_date
		return prev?.end_date || prev?.start_date || getTimelineBaseDateStr(timeSelection);
	};

	const getBaseStartDateForPaxService = (paxIndex, serviceIndex, timeSelection = null) => {
		if (timeSelection) {
			return getTimelineBaseDateStr(timeSelection);
		}
		if (serviceIndex <= 0) return getTimelineBaseDateStr(timeSelection);
		const prev = paxes?.[paxIndex]?.selectedServices?.[serviceIndex - 1];
		if (!prev) return getTimelineBaseDateStr(timeSelection);
		
		const prevEndTime = prev.end_time;
		const prevEndMinutes = prevEndTime ? convertToMinutes(prevEndTime) : null;
		
		// If previous service ended at or after midnight (00:00 to 06:00), use its end_date
		// This ensures services following a post-midnight service also use the next day
		if (prevEndMinutes !== null && prevEndMinutes >= 0 && prevEndMinutes < extendedBusinessCutoffMinutes) {
			// Previous service ended after midnight, use its end_date (which should be next day)
			return prev?.end_date || formatDateToDDMMYYYY(addDaysSafe(prev?.start_date || getDefaultServiceDateStr(), 1));
		}
		
		// Otherwise, use the end_date if available, or start_date
		return prev?.end_date || prev?.start_date || getTimelineBaseDateStr(timeSelection);
	};

	const resolveCreateSyncDateAnchor = (serviceStart, serviceStartDate, chainedBaseDate) => {
		const calendarBase = getDefaultServiceDateStr();
		const startMins = convertToMinutes(String(serviceStart || "").slice(0, 5));
		if (
			serviceStartDate === calendarBase &&
			startMins >= extendedBusinessCutoffMinutes
		) {
			return calendarBase;
		}
		return chainedBaseDate;
	};

	// Helper function to filter out rooms already assigned to other services at the same time
	const filterOutAssignedRooms = (availableRooms, serviceStartTime, serviceEndTime, excludeCounterKey = null) => {
		if (!availableRooms || availableRooms.length === 0) return availableRooms;
		if (!serviceStartTime || !serviceEndTime) return availableRooms;

		// Get all existing services (both self and pax) that overlap with this time slot
		const allServices = [
			...(selectedServices || []),
			...(paxes || []).flatMap((p) => p.selectedServices || []),
		];

		// Collect room IDs that are already assigned to overlapping services
		const assignedRoomIds = new Set();
		const serviceStartMinutes = convertToMinutes(serviceStartTime);
		const serviceEndMinutes = convertToMinutes(serviceEndTime);

		allServices.forEach((svc) => {
			// Skip the service we're currently adding/updating
			if (excludeCounterKey && svc.counterKey === excludeCounterKey) return;

			// Skip if service doesn't have a room assigned
			const roomId = svc.roomId || svc.room_segments?.[0]?.room_id;
			if (!roomId) return;

			// Skip if service doesn't have time information
			if (!svc.start_time || !svc.end_time) return;

			// Check if times overlap
			const svcStartMinutes = convertToMinutes(svc.start_time);
			const svcEndMinutes = convertToMinutes(svc.end_time);

			// Check if services overlap (not (end1 <= start2 || start1 >= end2))
			const overlaps = !(serviceEndMinutes <= svcStartMinutes || serviceStartMinutes >= svcEndMinutes);

			if (overlaps) {
				assignedRoomIds.add(Number(roomId));
			}
		});

		// Filter out rooms that are already assigned
		return availableRooms.filter((room) => {
			const roomId = Number(room.value || room.item_id);
			return !assignedRoomIds.has(roomId);
		});
	};

	const ROOM_BOOKING_CONFLICT_MESSAGE = "Room has another booking at this time";
	const INCOMPATIBLE_ROOM_MESSAGE = "Selected room is not compatible with this service";

	const getRoomBookingIdentifiers = useCallback((bookingLike) => {
		const bookingIds = [normalizeNumericId(bookingLike?.booking_id),normalizeNumericId(bookingLike?.bookingId),normalizeNumericId(bookingLike?.booking?.id),normalizeNumericId(bookingLike?.id),].filter((value) => value != null);
		const bookingItemIds = [normalizeNumericId(bookingLike?.booking_item_id),normalizeNumericId(bookingLike?.bookingItemId),normalizeNumericId(bookingLike?.booking_item?.id),normalizeNumericId(bookingLike?.item?.booking_item_id),normalizeNumericId(bookingLike?.item?.id),].filter((value) => value != null);
		return {
			bookingIds,
			bookingItemIds,
		};
	}, [normalizeNumericId]);

	const isCurrentEditRoomBooking = useCallback((bookingLike, serviceLike = null) => {
		if (!fromEdit) return false;

		const currentBookingId = normalizeNumericId(editableBooking?.id);
		const currentBookingItemId = getBookingItemId(serviceLike);
		const isExistingPersistedService = currentBookingItemId != null;
		const { bookingIds, bookingItemIds } = getRoomBookingIdentifiers(bookingLike);

		if (
			currentBookingItemId != null &&
			bookingItemIds.some((bookingItemId) => bookingItemId === currentBookingItemId)
		) {
			return true;
		}

		if (
			isExistingPersistedService &&
			currentBookingId != null &&
			bookingIds.some((bookingId) => bookingId === currentBookingId)
		) {
			return true;
		}

		return false;
	}, [fromEdit, editableBooking?.id, normalizeNumericId, getBookingItemId, getRoomBookingIdentifiers]);

	const getRoomBookingsExcludingCurrentEdit = useCallback((roomLike, serviceLike = null) => {
		const bookings = Array.isArray(roomLike?.bookings) ? roomLike.bookings : [];
		return bookings.filter((booking) => !isCurrentEditRoomBooking(booking, serviceLike));
	}, [isCurrentEditRoomBooking]);

	const roomHasExternalBookingConflict = useCallback((roomLike, serviceLike = null) => (
		getRoomBookingsExcludingCurrentEdit(roomLike, serviceLike).length > 0
	), [getRoomBookingsExcludingCurrentEdit]);

	const flattenRoomsList = (roomsList = []) => {
		const flatRooms = [];

		(Array.isArray(roomsList) ? roomsList : []).forEach((room) => {
			if (room?.items && Array.isArray(room.items)) {
				room.items.forEach((item) => {
					flatRooms.push({
						value: item.item_id,
						item_id: item.item_id,
						label: item.item_name || item.name,
						item_name: item.item_name || item.name,
						item_type: item.item_type || item.item,
						room_type: item.room_type ?? null,
						facilities: Array.isArray(item.facilities) ? item.facilities : [],
						room_id: room.room_id,
						room_name: room.room_name || room.name,
						room_code: room.room_code || room.code,
						bookings: Array.isArray(item.bookings) ? item.bookings : [],
					});
				});
				return;
			}

			if (room?.item_id || room?.value) {
				flatRooms.push({
					value: room.item_id || room.value,
					item_id: room.item_id || room.value,
					label: room.item_name || room.name || room.label,
					item_name: room.item_name || room.name || room.label,
					item_type: room.item_type || room.item,
					room_type: room.room_type ?? null,
					facilities: Array.isArray(room.facilities) ? room.facilities : [],
					room_id: room.room_id,
					room_name: room.room_name || room.name,
					room_code: room.room_code || room.code,
					bookings: Array.isArray(room.bookings) ? room.bookings : [],
				});
			}
		});

		return flatRooms;
	};

	const getRoomModeDistinctRoomCandidate = (serviceStartTime, serviceEndTime, excludeCounterKey = null, serviceLike = null) => {
		const roomSource = filteredItemIds?.length > 0
			? (homeRoomList || []).map((room) => ({
				...room,
				items: (Array.isArray(room?.items) ? room.items : []).filter((item) =>
					filteredItemIds.includes(item.item_id)
				),
			})).filter((room) => Array.isArray(room?.items) && room.items.length > 0)
			: (homeRoomList || []);

		const flatRooms = flattenRoomsList(roomSource);
		if (flatRooms.length === 0) return null;

		const availableRooms = filterOutAssignedRooms(
			flatRooms.filter((room) => !roomHasExternalBookingConflict(room, serviceLike)),
			serviceStartTime,
			serviceEndTime,
			excludeCounterKey
		);

		return availableRooms[0] || null;
	};

	const findRoomMetaById = (roomId) => {
		if (roomId == null || !Array.isArray(homeRoomList)) return null;

		for (const room of homeRoomList) {
			if (!Array.isArray(room?.items)) continue;
			const matchedItem = room.items.find((item) => Number(item.item_id) === Number(roomId));
			if (!matchedItem) continue;

			return {
				value: matchedItem.item_id,
				item_id: matchedItem.item_id,
				label: matchedItem.item_name || matchedItem.name,
				item_name: matchedItem.item_name || matchedItem.name,
				item_type: matchedItem.item_type || matchedItem.item || null,
				room_type: matchedItem.room_type ?? null,
				facilities: Array.isArray(matchedItem.facilities) ? matchedItem.facilities : [],
				room_id: room.room_id,
				room_name: room.room_name || room.name,
				room_code: room.room_code || room.code,
			};
		}

		return null;
	};

	const resolveRoomModeSelectedRoom = (existingService, serviceStartTime, serviceEndTime, counterKey) => {
		const existingRoomFromState = selectedRooms?.[counterKey] || null;
		const existingRoomId =
			getRoomId(existingService) ??
			normalizeNumericId(
				existingRoomFromState?.value ??
				existingRoomFromState?.item_id ??
				bookingData?.room?.value ??
				null
			);

		if (existingRoomId != null) {
			const stateMatchesExisting =
				existingRoomFromState &&
				normalizeNumericId(existingRoomFromState.value ?? existingRoomFromState.item_id) === existingRoomId;
			const bookingRoomMatchesExisting =
				normalizeNumericId(bookingData?.room?.value) === existingRoomId;
			const roomMeta =
				(stateMatchesExisting ? existingRoomFromState : null) ||
				(bookingRoomMatchesExisting ? bookingData.room : null) ||
				findRoomMetaById(existingRoomId) ||
				{
					value: existingRoomId,
					item_id: existingRoomId,
					label: existingService?.roomName || existingService?.room_name || existingRoomFromState?.label || "Room Selected",
					item_name: existingService?.roomName || existingService?.room_name || existingRoomFromState?.label || "Room Selected",
				};

			return {
				selectedRoom: existingRoomId,
				autoSelectedRoomData: roomMeta,
			};
		}

		const fallbackRoom = getRoomModeDistinctRoomCandidate(
			serviceStartTime,
			serviceEndTime,
			counterKey,
			existingService
		);
		if (fallbackRoom?.value || fallbackRoom?.item_id) {
			return {
				selectedRoom: fallbackRoom.value || fallbackRoom.item_id,
				autoSelectedRoomData: fallbackRoom,
			};
		}

		return { selectedRoom: null, autoSelectedRoomData: null };
	};

	const getAutoSelectedRoomCandidate = (roomsList, serviceStartTime, serviceEndTime, excludeCounterKey = null, serviceLike = null) => {
		const flatRooms = flattenRoomsList(roomsList);

		if (flatRooms.length === 0) {
			return { room: null, errorMessage: "" };
		}

		const availableRooms = filterOutAssignedRooms(
			flatRooms.filter((room) => !roomHasExternalBookingConflict(room, serviceLike)),
			serviceStartTime,
			serviceEndTime,
			excludeCounterKey
		);

		if (availableRooms.length > 0) {
			return { room: availableRooms[0], errorMessage: "" };
		}

		const fallbackRoom = flatRooms[0];
		const roomWithoutDraftOverlap = filterOutAssignedRooms(
			[fallbackRoom],
			serviceStartTime,
			serviceEndTime,
			excludeCounterKey
		).length > 0;

		return {
			room: fallbackRoom,
			errorMessage: (roomHasExternalBookingConflict(fallbackRoom, serviceLike) || !roomWithoutDraftOverlap)
				? ROOM_BOOKING_CONFLICT_MESSAGE
				: "",
		};
	};

	// Helper function to get the correct price based on isMemberRateApplied
	const getServicePriceFromPriceRate = (service, isMemberRateAppliedValue) => {
		// Try to get price_rate from service, or from service list cache
		let priceRate = service?.price_rate;
		
		// If price_rate is not available, try to get it from service list cache
		if (!priceRate) {
			priceRate = resolveServicePriceRate(service);
		}

		// Keep downstream service objects enriched once resolved.
		if (!service?.price_rate && priceRate && service && typeof service === "object") {
			service.price_rate = priceRate;
		}
		
		// Use service's isMembershipApplied property if available, otherwise fall back to closure value
		const serviceIsMembershipApplied = service?.isMembershipApplied !== undefined 
			? service.isMembershipApplied 
			: isMembershipApplied;
		const effectiveMembershipApplied = !isPremiumOutlet && serviceIsMembershipApplied;
		const effectiveMemberRateApplied = !isPremiumOutlet && isMemberRateAppliedValue;
		
		if (!priceRate) {
			// Fallback to old logic if price_rate is not available
			return effectiveMembershipApplied ? (service.member_price || service.price) : service.price;
		}
		
		if (effectiveMemberRateApplied) {
			// Use member_price_before_6_pm if isMemberRateApplied is true
			return priceRate?.member_price_before_6_pm ?? null;
		} else if (effectiveMembershipApplied) {
			// Use member_after_6_or_weekends if isMemberRateApplied is false but isMembershipApplied is true
			return priceRate?.member_after_6_or_weekends ?? null;
		} else {
			// Use nonMemberPrice otherwise
			return priceRate?.nonMemberPrice || null;
		}
	};

	//  ADDED: State for room capacity validation
	const [roomCapacityErrors, setRoomCapacityErrors] = useState({});
	const [roomBookingConflictErrors, setRoomBookingConflictErrors] = useState({});
	const [roomCapacityData] = useState({});
	const hasRoomCapacityData = useMemo(
		() => Object.keys(roomCapacityData || {}).length > 0,
		[roomCapacityData]
	);

	const getNextMainCounterKey = (services = []) => {
		const maxCounter = (Array.isArray(services) ? services : []).reduce((max, service) => {
			const numericKey = Number(service?.counterKey);
			return Number.isFinite(numericKey) ? Math.max(max, numericKey) : max;
		}, 0);
		return `${maxCounter + 1}`;
	};

	const getNextPaxCounterKey = (paxId, services = []) => {
		const keyPrefix = `${paxId}-`;
		const maxCounter = (Array.isArray(services) ? services : []).reduce((max, service) => {
			const key = String(service?.counterKey || "");
			if (!key.startsWith(keyPrefix)) return max;
			const numericKey = Number(key.slice(keyPrefix.length));
			return Number.isFinite(numericKey) ? Math.max(max, numericKey) : max;
		}, 0);
		return `${paxId}-${maxCounter + 1}`;
	};

	// Stable guest number from id (P2 → 2). Do not use list index after middle deletes.
	const getPaxGuestNumber = (paxLike, fallbackIndex = 0) => {
		const fromId = Number(String(paxLike?.id ?? "").replace(/\D/g, ""));
		if (Number.isFinite(fromId) && fromId > 0) return fromId;
		return fallbackIndex + 1;
	};

	const guestNumberFromLabel = (value) => {
		const match = String(value || "").trim().match(/^Guest\s*(\d+)$/i);
		if (!match) return 0;
		const num = Number(match[1]);
		return Number.isFinite(num) ? num : 0;
	};

	// Edit keeps saved names (Guest2, Guest3) while ids restart at P1.
	// Next id must clear both, so a new guest is Guest4 instead of a second Guest3.
	const allocateNextPaxId = (paxList = []) => {
		const maxNum = (Array.isArray(paxList) ? paxList : []).reduce((max, pax) => {
			const fromId = Number(String(pax?.id ?? "").replace(/\D/g, ""));
			const fromName = Math.max(
				guestNumberFromLabel(pax?.customer_name),
				guestNumberFromLabel(pax?.name)
			);
			const num = Math.max(Number.isFinite(fromId) ? fromId : 0, fromName);
			return num > max ? num : max;
		}, 0);
		return `P${maxNum + 1}`;
	};

    const overrideTimersRef = useRef({});

    // const allBookingItems = editableBooking && Object.values(editableBooking?.booking_item || {}).flat();
	// FIXED: Synchronize calendar slots with service state
	useLayoutEffect(() => {
		const syncCalendarSlots = () => {
			// While editing, existing booking items are shown via bookingsList override.
			// But newly added services/pax (no booking_item_id) still need calendar cards.
			setNewAppointmentSlots((prev) => {
				// Always preserve the main booking slot (counterKey: 1) even if no services are selected yet
				const mainBookingSlot = prev.find(slot => slot.counterKey === 1) ?? newSlot;
				logger.log("newBooking >> mainBookingSlot", mainBookingSlot);
				
				// Get customer data from global state (customerData) or main booking slot
				const currentCustomerData = customerData || mainBookingSlot?.customer || null;

				// FIXED: Create separate cards for each service and pax
				const allSlots = [];
				// If editing, only render cards for newly added services (no booking_item_id)
				const shouldRender = (svc) => !isEditBookingActive || (!svc?.booking_item_id);
				
				// Build row from canonical time/date and derive a fresh fallback column
				// from the current therapist/room lists. Home resolves the final visible
				// column again at render time, so we avoid preserving stale indices here.
				const getProjectedIndices = (service) => {
					const calculatedRowIndex = getCalendarRowIndexForService(service, interval);
					if (calculatedRowIndex === -1) {
						return { rowIndex: -1, colIndex: -1 };
					}

					if (isRoomFilterActive) {
						const derivedRoomId = getDerivedRoomId(service);
						if (derivedRoomId == null) {
							return { rowIndex: calculatedRowIndex, colIndex: -1 };
						}

						const visibleRoomHeaders = filteredItemIds?.length > 0
							? (homeRoomList || []).flatMap((room) =>
								(Array.isArray(room?.items) ? room.items : [])
									.filter((item) => filteredItemIds.includes(item.item_id))
									.map((item) => item.item_id)
							  )
							: (homeRoomList || []).flatMap((room) =>
								(Array.isArray(room?.items) ? room.items : []).map((item) => item.item_id)
							  );

						return {
							rowIndex: calculatedRowIndex,
							colIndex: visibleRoomHeaders.findIndex((itemId) => Number(itemId) === Number(derivedRoomId)),
						};
					}

					const therapistId = getTherapistId(service);
					return {
						rowIndex: calculatedRowIndex,
						colIndex: Array.isArray(homeStaffList)
							? homeStaffList.findIndex((staff) => Number(staff?.id) === Number(therapistId))
							: -1,
					};
				};
				// Create cards for main booking services (selectedServices)
				selectedServices.forEach(service => {
					// Check for therapist or room selection based on filter mode
					const derivedRoomId = getDerivedRoomId(service);
					const hasSelection = isServiceAssigned(service);
					
					if (shouldRender(service) && hasSelection) {
						const { rowIndex, colIndex } = getProjectedIndices(service);
						if (rowIndex !== -1) {
							
							allSlots.push(buildProjectedDraftSlot({
								rowIndex,
								colIndex,
								absoluteStartMinutes: getServiceAbsoluteStartMinutes(service),
								duration: Number(service.duration),
								start_time: service.start_time,
								end_time: service.end_time,
								serviceId: service.id,
								serviceName: service.name || service.service_name || "",
								serviceCategoryName: service.service_category_name || service.service_category || "",
								counterKey: service.counterKey,
								therapistId: isRoomFilterActive ? null : getTherapistId(service),
								therapistName: isRoomFilterActive ? null : service.therapist_name,
								roomId: isRoomFilterActive ? derivedRoomId : service?.roomId,
								roomData: isRoomFilterActive ? (() => {
									const parent = homeRoomList.find((r) => Array.isArray(r.items) && r.items.some(item => Number(item.item_id) === Number(derivedRoomId)));
									const item = parent?.items?.find(it => Number(it.item_id) === Number(derivedRoomId));
									if (!item) return null;
									return {
										id: item.item_id,
										name: item.item_name,
										item_type: item.item,
										room_id: parent?.room_id ?? null,
										room_name: parent?.room_name ?? "",
										room_code: parent?.room_code ?? "",
										room_type: item.room_type ?? parent?.room_type ?? parent?.capacity_type ?? null,
										facilities: Array.isArray(item.facilities) ? item.facilities : [],
										type: 'room_item'
									};
								})() : null,
								customer: currentCustomerData,
								services: [service],
								isPlaceholder: true,
								isMainBooking: service.counterKey === 1,
								service_at: `${getServiceAtDateStr(service)} ${service.start_time}`,
								isRoomFilterActive,
							}));

							// Also render split segments under the same base (defensive in case segments aren't in selectedServices array)
							try {
								const baseKey = service.splitParent || service.counterKey;
								const groupSegs = (selectedServices || []).filter((s) => (s.splitParent || s.counterKey) === baseKey && s.isSplitSegment);
								groupSegs.forEach((seg) => {
									// Check for therapist or room selection based on filter mode
									const segHasSelection = isServiceAssigned(seg);
									
									if (!shouldRender(seg) || !segHasSelection) return;
									const { rowIndex: segRow, colIndex: segCol } = getProjectedIndices(seg);
									if (segRow === -1) return;
									
									allSlots.push(buildProjectedDraftSlot({
										rowIndex: segRow,
										colIndex: segCol,
										absoluteStartMinutes: getServiceAbsoluteStartMinutes(seg),
										duration: Number(seg.duration),
										start_time: seg.start_time,
										end_time: seg.end_time,
										serviceId: seg.id,
										serviceName: seg.name || seg.service_name || "",
										counterKey: seg.counterKey,
										therapistId: isRoomFilterActive ? null : getTherapistId(seg),
										therapistName: isRoomFilterActive ? null : seg.therapist_name,
										roomId: isRoomFilterActive ? seg.roomId : null,
										roomData: isRoomFilterActive ? (() => {
											const room = homeRoomList.find((r) => r.items && r.items.some(item => item.item_id === seg.roomId));
											return room ? { ...room, matchedItem: room.items.find(item => item.item_id === seg.roomId) } : null;
										})() : null,
										customer: currentCustomerData,
										services: [seg],
										serviceCategoryName: seg.service_category_name || seg.service_category || "",
										isPlaceholder: true,
										service_at: `${getServiceAtDateStr(seg)} ${seg.start_time}`,
										isRoomFilterActive,
									}));
								});
							} catch {}
						}
					}
				});
				
								// Create cards for pax services
				paxes.forEach(pax => {
					pax.selectedServices.forEach(service => {
						// Check for therapist or room selection based on filter mode
						const derivedRoomId = getDerivedRoomId(service);
						const hasSelection = isServiceAssigned(service);
						
						if (shouldRender(service) && hasSelection) {
							const { rowIndex, colIndex } = getProjectedIndices(service);
							if (rowIndex !== -1) {
								
								allSlots.push(buildProjectedDraftSlot({
									rowIndex,
									colIndex,
									absoluteStartMinutes: getServiceAbsoluteStartMinutes(service),
									duration: Number(service.duration),
									start_time: service.start_time,
									end_time: service.end_time,
									serviceId: service.id,
									serviceName: service.name || service.service_name || "",
									serviceCategoryName: service.service_category_name || service.service_category || "",
									counterKey: service.counterKey,
									paxIndex: pax.id,
									therapistId: isRoomFilterActive ? null : getTherapistId(service),
									therapistName: isRoomFilterActive ? null : service.therapist_name,
									roomId: isRoomFilterActive ? derivedRoomId : null,
									roomData: isRoomFilterActive ? (() => {
										const room = homeRoomList.find((r) => r.items && r.items.some(item => Number(item.item_id) === Number(derivedRoomId)));
										return room ? { ...room, matchedItem: room.items.find(item => Number(item.item_id) === Number(derivedRoomId)) } : null;
									})() : null,
									customer: currentCustomerData,
									services: [service],
									isPlaceholder: true,
									isPaxBooking: true,
									service_at: `${getServiceAtDateStr(service)} ${service.start_time}`,
									isRoomFilterActive,
								}));
							}
						}
					});
				});
				
				// If no services with therapists, return the main booking slot if it exists
				if (allSlots.length === 0 && mainBookingSlot) {
					return [mainBookingSlot];
				}
				
				return allSlots;
			});
			
			// REMOVED: Automatic scrolling from syncCalendarSlots - now handled by individual functions
			// The individual functions (handleTimeClick, handleTherapistSelect) will handle their own scrolling
		};
		
		syncCalendarSlots();
	}, [selectedServices, paxes, homeStaffList, homeRoomList, isRoomFilterActive, therapistChangeTrigger, customerData, isEditBookingActive, selectedTime, selectedRooms, effectiveSelectedDate, interval, setNewAppointmentSlots, filteredItemIds, calendarDateSyncSignal?.refreshKey]);


	logger.log("newBooking >> newAppointmentSlots", newAppointmentSlots);
	logger.log("newBooking >> newSlot", newSlot);


	useEffect(() => {
		setNewSlot((prev) => ({
			...prev,
			rowIndex: newAppointmentSlots[0]?.rowIndex,
			colIndex: newAppointmentSlots[0]?.colIndex,
			absoluteStartMinutes: newAppointmentSlots[0]?.absoluteStartMinutes,
			start_time: newAppointmentSlots[0]?.start_time,
			end_time: newAppointmentSlots[0]?.end_time,
			service_at: newAppointmentSlots[0]?.service_at,
			therapistId: newAppointmentSlots[0]?.therapistId,
			roomId: newAppointmentSlots[0]?.roomId,
			roomData: newAppointmentSlots[0]?.roomData,
			customer: newAppointmentSlots[0]?.customer,
            isPlaceholder: true,
            counterKey: 1,
            uid: newAppointmentSlots[0]?.uid,
		  }));
	}, [newAppointmentSlots, setNewSlot])

	// Create booking: scroll after calendar slots are reprojected for a time-only change.
	useLayoutEffect(() => {
		if (fromEdit) return;

		const signal = calendarDateSyncSignal;
		if (
			!signal?.requestId ||
			signal.status !== "ready" ||
			signal.mode !== "create" ||
			signal.syncIntent !== "time"
		) {
			return;
		}

		const primaryService =
			(Array.isArray(selectedServices) ? selectedServices.find((svc) => !svc?.isSplitSegment) : null) ||
			(Array.isArray(selectedServices) ? selectedServices[0] : null) ||
			(Array.isArray(paxes)
				? paxes.flatMap((pax) => (Array.isArray(pax?.selectedServices) ? pax.selectedServices : []))
					.find((svc) => !svc?.isSplitSegment)
				: null) ||
			(Array.isArray(paxes)
				? paxes.flatMap((pax) => (Array.isArray(pax?.selectedServices) ? pax.selectedServices : []))[0]
				: null);

		if (!primaryService?.start_time) {
			const emptySlot = Array.isArray(newAppointmentSlots)
				? newAppointmentSlots.find((slot) => slot?.counterKey != null)
				: null;
			if (!emptySlot?.counterKey) return;

			const scrollKey = `${signal.requestId}:${signal.refreshKey}:empty`;
			if (lastDateSyncScrollRequestRef.current === scrollKey) return;
			lastDateSyncScrollRequestRef.current = scrollKey;
			scrollToNewBookingCard(emptySlot.counterKey, 50);
			return;
		}

		const projectedSlot = Array.isArray(newAppointmentSlots)
			? newAppointmentSlots.find((slot) => slot?.counterKey === primaryService.counterKey)
			: null;
		// Assigned services move on the calendar — wait until slots reflect the new time.
		if (
			isServiceAssigned(primaryService) &&
			(!projectedSlot || projectedSlot.start_time !== primaryService.start_time)
		) {
			return;
		}

		const scrollKey = `${signal.requestId}:${signal.refreshKey}:${primaryService.start_time}`;
		if (lastDateSyncScrollRequestRef.current === scrollKey) return;
		lastDateSyncScrollRequestRef.current = scrollKey;
		scrollToNewBookingCard(primaryService.counterKey, 50);
	}, [
		fromEdit,
		calendarDateSyncSignal,
		newAppointmentSlots,
		selectedServices,
		paxes,
		isServiceAssigned,
	]);

	// GET THERAPIST LIST START
	// With `withStatus`, resolves to { staffList, ok } so callers can tell an empty roster
	// apart from a failed request (getTherapistList returns HTTP errors instead of throwing).
	const fetchTherapists = async (service, service_at, service_duration, { withStatus = false } = {}) => {
		if (!effectiveSelectedDate) {
			return withStatus ? { staffList: [], ok: false } : [];
		}
		const [serviceAtDate, serviceAtTime = ""] = String(service_at || "").split(" ");
		const normalizedServiceAt = serviceAtTime? `${serviceAtDate} ${serviceAtTime.slice(0, 5)}`: service_at;
		const item = buildAutoAssignTherapistRequestPayload(
			service,
			getTherapistListServiceAt(normalizedServiceAt),
			service_duration
		);

		try {
			const data = await getTherapistList(item);
			const httpStatus = Number(data?.status ?? data?.response?.status);
			const ok = !data?.isAxiosError && httpStatus >= 200 && httpStatus < 300;
			const staffList = ok ? extractTherapistStaffList(data) : [];
			return withStatus ? { staffList, ok } : staffList;
		} catch (error) {
			console.error("Error fetching therapists:", error);
			return withStatus ? { staffList: [], ok: false } : [];
		}
	};
	// GET THERAPIST LIST END

	// GET THERAPIST LIST ON DROPDOWN TOGGLE START 
	const handleTherapistDropdownToggle = async (service, isOpen) => {
		if (!isOpen) return;
		if (!service || !effectiveSelectedDate || !selectedOutlet) return;
		setTherapistLoading(true);
		let item = {
			services: service?.id || service?.service,
			duration: service?.duration,
			service_at: getTherapistListServiceAt(`${getServiceAtDateStr(service)} ${service.start_time}`),
			outlet: selectedOutlet,
			outlet_type: selectedOutletData?.outlet_type_id,
			availability: 0,
			pagination: 0,
			status: 1,
			role: 2,
			leave: 0,
			calendar: 1,
		};

		try {
			const response = await getTherapistList(item);
			const staffList = extractTherapistStaffList(response);
			let options = (staffList).map((staff) => ({
				value: staff.id,
				label: staff.alias || staff.name,
				pager: staff.pager_number || 'N/A',
				gender: staff.gender,
				is_service_provided: staff.is_service_provided ?? true,
				is_available: staff.is_available ?? true,
			}));

			if (service?.counterKey) {
				serviceAvailabilityKeyRef.current.set(
					service.counterKey,
					buildServiceAvailabilityKey(
						service?.id || service?.service,
						item.service_at,
						service?.duration
					)
				);
			}

			setTherapistOptionsMap((prev) => ({
				...prev,
				[service.counterKey]: options,
			}));
			return staffList;
		} catch (error) {
			console.error("Error fetching therapists for dropdown:", error);
		} finally {
			setTherapistLoading(false);
		}
	};
	// GET THERAPIST LIST ON DROPDOWN TOGGLE END 

	let selectedTimeValue = selectedTime?.value || bookingData?.time?.value || "";

	// Helper function to calculate cross_day_booking_item based on dates and times
	const calculateCrossDayBookingItem = (startDate, endDate, startTime, endTime) => {
		if (!startDate || !endDate) {
			return null;
		}

		if (isEarlyMorningBoundaryTimeRange(startTime, endTime)) {
			return null;
		}

		if (isMixedNormalAndExtendedCrossDayService(startDate, endDate, startTime, endTime)) {
			return buildMidnightSplitCrossDayItems(startDate, endDate, startTime, endTime);
		}

		if (startDate === endDate) {
			return null;
		}

		// A service ending exactly at "00:00" has zero real duration on end_date — it's just the
		// date rolling over at the boundary, not a genuine split with presence on both days. Only
		// end_date should be bumped (handled by ensureServiceDates); no cross_day_booking_item needed.
		if (convertToMinutes(endTime) === 0) {
			return null;
		}

		const startMinutes = convertToMinutes(startTime);
		const endMinutes = convertToMinutes(endTime);
		const minutesInDay = 24 * 60;

		// Calculate duration for first segment (from start_time to 00:00)
		const firstSegmentDuration = minutesInDay - startMinutes;

		// Calculate duration for second segment (from 00:00 to end_time)
		const secondSegmentDuration = endMinutes;

		// First segment: from start_time to 00:00 on start_date
		const firstSegment = {
			start_time: startTime,
			end_time: "00:00",
			duration: firstSegmentDuration,
			start_date: startDate,
			end_date: startDate,
		};

		// Second segment: from 00:00 to end_time on end_date
		const secondSegment = {
			start_time: "00:00",
			end_time: endTime,
			duration: secondSegmentDuration,
			start_date: endDate,
			end_date: endDate,
		};

		return [firstSegment, secondSegment];
	};

	// ADD SERVICE START
	const addServiceToBooking = async (service, paxIndex = null, addOptions = {}) => {
		const holdAutoAssignLock = !!(addOptions || {}).holdAutoAssignLock;
		if (!holdAutoAssignLock && isAutoAssigningRef.current) return;

		if (!bookingData?.time?.value) {
			setError("Please select time first");
			return;
		}
		if (!service || typeof service !== "object") {
			console.error("Invalid service data:", service);
			return;
		}
		const {
			therapistAssignmentMode = "default",
			basePaxes = null,
			targetPaxId = null,
			serviceStartOverride = null,
			preferredStartDateOverride = null,
			resetCopiedMetadata = false,
		} = addOptions || {};

		const ownsLock = !holdAutoAssignLock;
		if (ownsLock) {
			isAutoAssigningRef.current = true;
			setIsAutoAssigning(true);
		}

		try {
		const activePaxes = Array.isArray(basePaxes) ? basePaxes : paxes;
		const roomConflictServiceLike = resetCopiedMetadata? {...service,booking_item_id: null,bookingItemId: null,}: service;

		let newCounter;
		const isPax = typeof paxIndex === "number";
		
		if (isPax) {
			const paxId = activePaxes?.[paxIndex]?.id || targetPaxId || `P${paxIndex + 1}`;
			newCounter = getNextPaxCounterKey(paxId, activePaxes?.[paxIndex]?.selectedServices || []);
		} else {
			newCounter = getNextMainCounterKey(selectedServices);
		}

		let targetMember = null;
		let serviceList = [];
		const resolvedTargetPaxId = targetPaxId || activePaxes?.[paxIndex]?.id || null;

		if (isPax) {
			targetMember = activePaxes?.[paxIndex];
			if (!targetMember) return;
			serviceList = targetMember.selectedServices;
		} else {
			serviceList = selectedServices;
		}

		// Determine start time: in edit, new service should start after the latest existing service end time
		const toMinutes = (t) => {
			if (!t) return -1;
			const [hh, mm] = String(t).split(":").map((n) => parseInt(n, 10));
			if (Number.isNaN(hh) || Number.isNaN(mm)) return -1;
			return hh * 60 + mm;
		};
		const addM = (hhmm, add) => {
			if (!hhmm) return hhmm;
			const [h, m] = String(hhmm).split(":").map((n) => parseInt(n, 10));
			if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
			const total = h * 60 + m + (Number(add) || 0);
			const nh = Math.floor((((total % (24 * 60)) + (24 * 60)) % (24 * 60)) / 60);
			const nm = ((total % 60) + 60) % 60;
			return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
		};
		let latestEnd = null;
		let latestEndService = null;
		serviceList.forEach((s) => {
			const end = s?.end_time || addM(s?.start_time, Number(s?.duration) || 0);
			if (!end) return;
			
			// Helper to check if a time is on the next day (crossed midnight)
			const isOnNextDay = (endTime, startTime) => {
				if (!endTime || !startTime) return false;
				const endMin = toMinutes(endTime);
				const startMin = toMinutes(startTime);
				// If end_time is less than start_time (and not both 0), it crossed midnight
				// Also, if end_time is in early morning (00:00-06:00) and start_time was late evening (22:00-23:59)
				return (endMin < startMin && endMin > 0) || 
				       (endMin >= 0 && endMin < extendedBusinessCutoffMinutes && startMin >= (22 * 60));
			};
			
			// Helper to compare times, accounting for midnight crossover and dates
			const compareTimes = (time1, time2, service1, service2) => {
				// First compare dates - if dates are different, the later date is later
				// Use end_date if available, otherwise fallback to start_date
				const date1Str = service1?.end_date || service1?.start_date;
				const date2Str = service2?.end_date || service2?.start_date;
				
				let datesEqual = false;
				if (date1Str && date2Str) {
					const date1 = parseDDMMYYYYToDate(date1Str);
					const date2 = parseDDMMYYYYToDate(date2Str);
					if (date1 && date2) {
						if (date1.getTime() > date2.getTime()) return true;
						if (date1.getTime() < date2.getTime()) return false;
						// Dates are equal, compare times below
						datesEqual = true;
					}
				}
				
				const min1 = toMinutes(time1);
				const min2 = toMinutes(time2);
				
				// If dates are equal, compare times directly (no adjustment needed)
				if (datesEqual) {
					return min1 > min2;
				}
				
				// If dates are different, use the "next day" adjustment for services that cross midnight
				// If service1 ended after midnight, add 24 hours for comparison
				const isService1NextDay = service1 && isOnNextDay(time1, service1.start_time);
				const adjustedMin1 = isService1NextDay ? min1 + (24 * 60) : min1;
				
				// Same for service2
				const isService2NextDay = service2 && isOnNextDay(time2, service2.start_time);
				const adjustedMin2 = isService2NextDay ? min2 + (24 * 60) : min2;
				
				return adjustedMin1 > adjustedMin2;
			};
			
			if (!latestEnd || compareTimes(end, latestEnd, s, latestEndService)) {
				latestEnd = end;
				latestEndService = s;
			}
		});
		let serviceStart = latestEnd || serviceStartOverride || selectedTimeValue;

		const serviceEnd = calculateEndTime(serviceStart, service.duration);
		// Use the previous service's end_date as preferredStartDate if it ended after midnight
		const preferredStartDate =
			latestEndService?.end_date ||
			latestEndService?.start_date ||
			preferredStartDateOverride ||
			getTimelineBaseDateStr();
		const { startDateStr, endDateStr } = ensureServiceDates(
			null,
			null,
			serviceStart,
			serviceEnd,
			service.duration,
			preferredStartDate
		);
		
		// Calculate cross_day_booking_item if service crosses days (start_date !== end_date)
		const crossDayBookingItem = calculateCrossDayBookingItem(
			startDateStr,
			endDateStr,
			serviceStart,
			serviceEnd
		);

		// ADDED: Fetch rooms and auto-select first available room
		let autoSelectedRoomData = null;
		let selectedRoom = null;
		let autoSelectedRoomError = "";

		const assignFirstAvailableRoom = async () => {
			if (!(selectedOutletID && startDateStr && (service.id || service.service) && service.duration)) return;
			try {
				const normalizedRoomStartTime = normalizeRoomServiceAtTime(serviceStart);
				const roomAvailabilityDateStr = getRoomAvailabilityDateStr(startDateStr);
				const roomPayload = {
					date: roomAvailabilityDateStr,
					service_id: service.service || service.id,
					duration: service.duration,
					service_at: `${roomAvailabilityDateStr} ${normalizedRoomStartTime}`,
				};

				const roomResponse = await getRoomsForCalendar(selectedOutletID, roomPayload);
				const roomsList = roomResponse?.data || [];

				console.log("roomsList", roomsList);

				const { room: autoRoomCandidate, errorMessage } = getAutoSelectedRoomCandidate(
					roomsList,
					serviceStart,
					serviceEnd,
					newCounter,
					roomConflictServiceLike
				);

				if (autoRoomCandidate) {
					selectedRoom = autoRoomCandidate.value || autoRoomCandidate.item_id;
					autoSelectedRoomData = autoRoomCandidate;
					autoSelectedRoomError = errorMessage;

					setSelectedRooms(prev => ({
						...prev,
						[newCounter]: autoRoomCandidate
					}));
				}
			} catch (error) {
				console.error("Error fetching rooms for auto-selection:", error);
			}
		};

		// A room-slot click keeps that room for the first service. Every later service uses that service's room list.
		let options = buildTherapistOptions(homeStaffList || []);
		if (selectedOutletID && (service.id || service.service) && service.duration) {
			try {
				const serviceAt = `${startDateStr} ${serviceStart}`;
				const staffList = await fetchTherapists(
					service?.id || service?.service,
					serviceAt,
					service?.duration
				);
				options = buildTherapistOptions(staffList);
			} catch (error) {
				console.error("Error fetching therapists for service add:", error);
			}
		}


		const latestSelectedServicesSnapshot = Array.isArray(latestSelectedServicesRef.current) ? latestSelectedServicesRef.current : selectedServices;
		const latestPaxesSnapshot = Array.isArray(latestPaxesRef.current) ? latestPaxesRef.current : activePaxes;
		const allServicesForTherapistSelection = [
			...(latestSelectedServicesSnapshot || []),
			...(latestPaxesSnapshot || []).flatMap((p) => p.selectedServices || []),
		];

		const countOverlappingAssignmentsForTherapist = (therapistId) => {
			const normalizedTherapistId = normalizeNumericId(therapistId);
			if (normalizedTherapistId == null) return 0;
			return allServicesForTherapistSelection.reduce((count, svc) => {
				const serviceTherapistId = getTherapistId(svc);
				if (serviceTherapistId == null || serviceTherapistId !== normalizedTherapistId) return count;
				if (!svc.start_time || !svc.end_time) return count;
				if (svc.counterKey === newCounter) return count;

				const s1 = convertToMinutes(serviceStart);
				const e1 = convertToMinutes(serviceEnd);
				const s2 = convertToMinutes(svc.start_time);
				const e2 = convertToMinutes(svc.end_time);

				return !(e1 <= s2 || s1 >= e2) ? count + 1 : count;
			}, 0);
		};

		const availableOptions = options.filter((opt) => {
			if (opt.value === null) return true;
			// Check if this therapist is busy with other services in the current booking
			return !allServicesForTherapistSelection.some((svc) => {
				const serviceTherapistId = getTherapistId(svc);
				if (serviceTherapistId == null || serviceTherapistId !== normalizeNumericId(opt.value)) return false;
				if (!svc.start_time || !svc.end_time) return false;
				if (svc.counterKey === newCounter) return false;

				const s1 = convertToMinutes(serviceStart);
				const e1 = convertToMinutes(serviceEnd);
				const s2 = convertToMinutes(svc.start_time);
				const e2 = convertToMinutes(svc.end_time);

				return !(e1 <= s2 || s1 >= e2); // overlapping
			});
		});
		// Auto-assign therapist from first service if available
		let autoAssignedTherapist = null;
		let hasManualTherapistSelection = false;
		const getValidTherapistOptions = (primaryOptions = [], secondaryOptions = []) => (
			[
				...(Array.isArray(primaryOptions) ? primaryOptions : []),
				...(Array.isArray(secondaryOptions) ? secondaryOptions : []),
			].filter((opt, index, list) => {
				const normalizedValue = normalizeNumericId(opt?.value);
				return (
					normalizedValue != null &&
					opt?.value !== "" &&
					list.findIndex((candidate) => normalizeNumericId(candidate?.value) === normalizedValue) === index
				);
			})
		);
		const pickBestTherapistOption = (therapistOptions = []) => {
			const validOptions = getValidTherapistOptions(therapistOptions);
			const pickLeastOverlapped = (candidates = []) => candidates
				.map((opt, index) => ({
					opt,
					index,
					overlapCount: countOverlappingAssignmentsForTherapist(opt?.value),
				}))
				.sort((a, b) => a.overlapCount - b.overlapCount || a.index - b.index)?.[0]?.opt || null;

			const skilledAndFree = validOptions.filter((opt) =>
				opt?.is_available !== false &&
				opt?.is_service_provided !== false
			);
			const freeWithoutSkills = validOptions.filter((opt) =>
				opt?.is_available !== false &&
				opt?.is_service_provided === false
			);
			const busyWithoutSkills = validOptions.filter((opt) =>
				opt?.is_available === false &&
				opt?.is_service_provided === false
			);
			const fallbackAny = validOptions.filter((opt) =>
				!skilledAndFree.some((candidate) => normalizeNumericId(candidate?.value) === normalizeNumericId(opt?.value)) &&
				!freeWithoutSkills.some((candidate) => normalizeNumericId(candidate?.value) === normalizeNumericId(opt?.value)) &&
				!busyWithoutSkills.some((candidate) => normalizeNumericId(candidate?.value) === normalizeNumericId(opt?.value))
			);

			return (
				pickLeastOverlapped(skilledAndFree) || pickLeastOverlapped(freeWithoutSkills) || pickLeastOverlapped(busyWithoutSkills) || pickLeastOverlapped(fallbackAny)
			);
		};
		const pickFallbackTherapist = (primaryOptions = [], secondaryOptions = []) => {
			const primaryMatch = pickBestTherapistOption(primaryOptions);
			if (primaryMatch) return normalizeNumericId(primaryMatch.value);
			const primaryIds = new Set( getValidTherapistOptions(primaryOptions).map((opt) => normalizeNumericId(opt?.value)));
			const secondaryOnlyOptions = getValidTherapistOptions(secondaryOptions).filter((opt) => !primaryIds.has(normalizeNumericId(opt?.value)));
			return normalizeNumericId(pickBestTherapistOption(secondaryOnlyOptions)?.value);
		};
		const pickNextServiceTherapist = (latestService) => {
			const bestAvailable = normalizeNumericId(pickBestTherapistOption(availableOptions)?.value);
			if (bestAvailable != null) return bestAvailable;
			// Only reuse the previous lane therapist when they are still free for this slot.
			const previousTherapistId = getTherapistId(latestService);
			if (
				previousTherapistId != null &&
				availableOptions.some((opt) => normalizeNumericId(opt?.value) === previousTherapistId)
			) {
				return previousTherapistId;
			}
			return null;
		};
		const shouldUseFirstAvailableAssignment =
			therapistAssignmentMode === "firstAvailable" ||
			(isRoomFilterActive && serviceList.length === 0) ||
			(isPax && serviceList.length === 0 && selectedServices.length === 0);
		
		if (shouldUseFirstAvailableAssignment) {
			autoAssignedTherapist = pickFallbackTherapist(availableOptions, options);
		} else {
			if (isPax && targetMember?.selectedServices?.length > 0) {
				const latestPaxService = targetMember.selectedServices[targetMember.selectedServices.length - 1];
				autoAssignedTherapist = pickNextServiceTherapist(latestPaxService);
			}

			// If this is not the first service, prefer a therapist who can provide the new service.
			if (!autoAssignedTherapist && !isPax && selectedServices.length > 0) {
				const latestService = selectedServices[selectedServices.length - 1];
				autoAssignedTherapist = pickNextServiceTherapist(latestService);
			}
			// If no therapist from first service, use the main booking therapist
			if (!autoAssignedTherapist && !isPax && bookingData?.therapist?.value) {
				autoAssignedTherapist = bookingData.therapist.value;
				hasManualTherapistSelection = true;
			}
		}

		if (!autoAssignedTherapist) {
			autoAssignedTherapist = pickFallbackTherapist(availableOptions, options);
		}
		const autoAssignedTherapistOption = getValidTherapistOptions(availableOptions, options).find(
			(opt) => normalizeNumericId(opt?.value) === normalizeNumericId(autoAssignedTherapist)
		);
		const storedTherapistOptions = (() => {
			if (!autoAssignedTherapistOption) return availableOptions;
			const alreadyInAvailableOptions = availableOptions.some( (opt) => normalizeNumericId(opt?.value) === normalizeNumericId(autoAssignedTherapistOption?.value));
			return alreadyInAvailableOptions ? availableOptions : [autoAssignedTherapistOption, ...availableOptions];
		})();

		// Set therapist options after auto-assignment logic
		setTherapistOptionsMap((prev) => ({
			...prev,
			[newCounter]: storedTherapistOptions.length
				? storedTherapistOptions
				: [{ label: "No Therapist Available", value: "" }],
		}));
		
		// REMOVED: Calendar slot addition moved to synchronous update with service state

		const hasTherapists = options.some(
			(t) => t.value !== null && t.value !== ""
		);
		const noTherapistAvailable = !hasTherapists;
		
		// First room-mode slot click keeps the clicked room. Any other added service uses its own room list.
		const keepClickedRoom = isRoomFilterActive && !isPax && selectedServices.length === 0 && bookingData?.room?.value;
		if (!keepClickedRoom) {
			await assignFirstAvailableRoom();
		}

		if (!selectedRoom && !autoSelectedRoomData) {
			selectedRoom = selectedRooms[newCounter]?.value || null;
			autoSelectedRoomData = selectedRooms[newCounter] || null;
		}

		if (!selectedRoom) {
			if (!isPax && selectedServices.length === 0 && bookingData?.room?.value) {
				// First service - use room from bookingData (set by calendar click in room mode)
				selectedRoom = bookingData.room.value; // This is the item_id
				autoSelectedRoomData = bookingData.room;
				
				setSelectedRooms(prev => ({
					...prev,
					[newCounter]: bookingData.room
				}));
			} else if (!isPax && selectedServices.length > 0) {
				// Second+ service: reuse the first service's room
				const firstService = selectedServices[0];
				const firstServiceRoom = selectedRooms[firstService.counterKey];
				
				if (firstServiceRoom && firstServiceRoom.value) {
					selectedRoom = firstServiceRoom.value;
					autoSelectedRoomData = firstServiceRoom;
					
					setSelectedRooms(prev => ({
						...prev,
						[newCounter]: firstServiceRoom
					}));
				}
			} else if (isPax) {
				const paxFirstService = targetMember?.selectedServices?.[0];
				const paxFirstServiceRoom = paxFirstService
					? selectedRooms[paxFirstService.counterKey]
					: null;

				if (paxFirstServiceRoom?.value) {
					selectedRoom = paxFirstServiceRoom.value;
					autoSelectedRoomData = paxFirstServiceRoom;

					setSelectedRooms(prev => ({
						...prev,
						[newCounter]: paxFirstServiceRoom
					}));
				} else {
					// A new pax has no room yet. Pick from the service availability list.
					await assignFirstAvailableRoom();
				}
			}

			// No clicked/copied room: pick the first available room from the service API.
			if (!selectedRoom && !isPax) {
				await assignFirstAvailableRoom();
			}
		}

		setRoomBookingConflictErrors((prev) => ({
			...prev,
			[newCounter]: autoSelectedRoomError,
		}));
		
		// Create selectedTherapist object if therapist exists
		const therapistId = autoAssignedTherapist || null;
		let selectedTherapist = null;
		if (therapistId) {
			const therapistFromList = Array.isArray(homeStaffList) && homeStaffList.length > 0
				? homeStaffList.find((staff) => Number(staff.id) === Number(therapistId))
				: null;
			selectedTherapist = buildSelectedTherapistFromOptions(
				therapistId,
				options,
				therapistFromList
			);
		}
		
		const isMemberRateAppliedValue = isTimeBefore6PM(serviceStart) && isMembershipApplied && !publicHoliday && isWeekday(startDateStr);
		const newServiceTimelineMeta = getServiceTimelineMeta(serviceStart, startDateStr);
		const resolvedServiceCategoryName = resolveServiceCategoryName(service);
		const resolvedServiceCategoryId = resolveServiceCategoryId(service);
		// First service in the lane defines member-rate flag for all services in that lane
		const laneServicesForLink = isPax
			? (Array.isArray(targetMember?.selectedServices) ? targetMember.selectedServices : [])
			: (Array.isArray(selectedServices) ? selectedServices : []);
		const existingChainCount = laneServicesForLink.filter((s) => isScheduleChainService(s)).length;
		const anchorMemberRate =
			!isPax && selectedServices.length > 0
				? Boolean(selectedServices[0].isMemberRateApplied)
				: isPax && targetMember.selectedServices.length > 0
					? Boolean(targetMember.selectedServices[0].isMemberRateApplied)
					: isMemberRateAppliedValue;
		const newService = {
			...service,
			counterKey: newCounter,
			uid: service.booking_item_id || service.uid || generateUid(),
			therapist: autoAssignedTherapist,
			isAutoAssignedTherapist: autoAssignedTherapist != null && !hasManualTherapistSelection,
			hasManualTherapistSelection,
			start_date: startDateStr,
			end_date: endDateStr,
			dayOffset: newServiceTimelineMeta.dayOffset,
			absoluteMinutes: newServiceTimelineMeta.absoluteMinutes,
			cross_day_booking_item: crossDayBookingItem,
			linkedToPrevious: existingChainCount > 0,
			// Ensure roomId is set so calendar slots are created in room mode
			roomId: selectedRoom || null,
			room_segments: (() => {
				const rid = normalizeNumericId(selectedRoom);
				if (rid == null) return null;
				return buildRoomSegments(
					{
						...service,
						start_time: serviceStart,
						end_time: serviceEnd,
						duration: Number(service.duration),
						requested_room: 0,
						room_segments: null,
					},
					rid,
					autoSelectedRoomData || {}
				);
			})(),
			// Add room name for display purposes
			roomName: autoSelectedRoomData?.label || autoSelectedRoomData?.item_name || (selectedRoom ? "Selected Room" : null),
			noTherapistError: noTherapistAvailable,
			// noRoomError: noRoomAvailable,
			start_time: serviceStart,
			end_time: serviceEnd,
			duration: Number(service.duration),
			requested_therapist: false, // Set to false by default, user can check/uncheck
			requested_person: 0, // Set to 0 by default
			requested_room: 0, // Auto-selected rooms should not inherit requested-room state
			requested_therapist_disabled: false, // Allow user to check/uncheck
			service_request: resetCopiedMetadata ? "" : (service.service_request || ""),
			addons: resetCopiedMetadata ? [] : (Array.isArray(service.addons) ? [...service.addons] : []),
			booking_item_id: null, // ensure explicit null so debug shows key until saved
			selected_therapist: selectedTherapist,
			isBefore6pm: isTimeBefore6PM(serviceStart),
			isMemberRateApplied: anchorMemberRate,
			isMembershipApplied: isMembershipApplied,
			// Preserve price_rate from original service object (or resolve from cache)
			price_rate: resolveServicePriceRate(service),
			// Set price based on isMemberRateApplied
			price: getServicePriceFromPriceRate(service, anchorMemberRate),
			service_category_name: resolvedServiceCategoryName,
			service_category: resolvedServiceCategoryName,
			service_category_id: resolvedServiceCategoryId,
		};

		if (isPax) {
			const currentMembers = Array.isArray(latestPaxesRef.current)
				? latestPaxesRef.current
				: (Array.isArray(paxes) ? paxes : []);
			const updatedMembers = [...currentMembers];
			const targetIndex = resolvedTargetPaxId
				? updatedMembers.findIndex((member) => member?.id === resolvedTargetPaxId)
				: paxIndex;
			if (targetIndex < 0 || !updatedMembers[targetIndex]) {
				return;
			}
			const currentSelectedServices = Array.isArray(updatedMembers[targetIndex].selectedServices)
				? updatedMembers[targetIndex].selectedServices
				: [];
			updatedMembers[targetIndex] = {
				...updatedMembers[targetIndex],
				selectedServices: [
					...currentSelectedServices,
					newService,
				],
			};
			latestPaxesRef.current = updatedMembers;
			setPaxes(updatedMembers);
			
			// FIXED: Only scroll when a specific therapist is assigned (not auto-assign)
			// This prevents unnecessary scrolling when just clicking on existing cards
			setTimeout(() => {
				if (!isRoomFilterActive && autoAssignedTherapist && autoAssignedTherapist !== null) {
					const rowIndex = getCalendarRowIndexForService({ start_time: serviceStart, start_date: startDateStr }, interval);
					const colIndex = getVisibleTherapistColIndex(autoAssignedTherapist);
					if (colIndex < 0) return;
					
					const clickedCell = document.querySelector(`[data-row="${rowIndex}"][data-col="${colIndex}"]`);
					
					if (clickedCell) {
						// Enhanced scroll with both vertical and horizontal centering
						clickedCell.scrollIntoView({
							behavior: "smooth",
							block: "center",
							inline: "center"
						});

						// Add highlight animation
						clickedCell.classList.remove('highlightedCard');
						void clickedCell.offsetWidth; // Force reflow
						clickedCell.classList.add('highlightedCard');
						
						// Remove highlight after animation
						setTimeout(() => {
							clickedCell.classList.remove('highlightedCard');
						}, 1500);
					}
				}
			}, 500); // Delay to ensure service is added and calendar is updated
		} else {
			setSelectedServices((prev) => {
				const updatedServices = [...prev, newService];
				
				// If this is the first service, update the main time to match
				if (prev.length === 0) {
					// serviceStart is already in 24-hour format, so we don't need to convert it
					const formattedTime = buildSelectedTimePayloadForService(serviceStart, startDateStr);
					
					// Update the global selected time
					setSelectedTime(formattedTime);
					
					// Update booking data
					setBookingData((prevBooking) => ({
						...prevBooking,
						time: formattedTime,
					}));
				}
				
				return updatedServices;
			});
			
			// Update noRoomSelected status after adding service
			if (isRoomFilterActive) {
				setTimeout(() => {
					const allServices = [...selectedServices, newService];
					const hasMissingRoom = allServices.some(service => {
						const serviceRoom = selectedRooms[service.counterKey];
						return !serviceRoom || !serviceRoom.value;
					});
					setNoRoomSelected(hasMissingRoom);
				}, 0);
			}
		}
		selectedTimeValue = serviceEnd;
		setError("");
		
		// Trigger booking change to update calendar UI immediately
		setBookingChanged(true);
		
		// FIXED: Only scroll when a specific therapist is assigned (not auto-assign)
		// This prevents unnecessary scrolling when just clicking on existing cards
		setTimeout(() => {
			// Only scroll if we have a specific therapist (not auto-assign)
			if (!isRoomFilterActive && autoAssignedTherapist && autoAssignedTherapist !== null) {
				const rowIndex = getCalendarRowIndexForService({ start_time: serviceStart, start_date: startDateStr }, interval);
				const colIndex = getVisibleTherapistColIndex(autoAssignedTherapist);
				if (colIndex < 0) return;
				
				// Try to find the cell multiple times with increasing delays
				const findAndScrollToCell = (attempt = 1) => {
					const clickedCell = document.querySelector(`[data-row="${rowIndex}"][data-col="${colIndex}"]`);
					
					if (clickedCell) {
						// Enhanced scroll with both vertical and horizontal centering
						clickedCell.scrollIntoView({
							behavior: "smooth",
							block: "center",
							inline: "center"
						});

						// Add highlight animation
						clickedCell.classList.remove('highlightedCard');
						void clickedCell.offsetWidth; // Force reflow
						clickedCell.classList.add('highlightedCard');
						
						// Remove highlight after animation
						setTimeout(() => {
							clickedCell.classList.remove('highlightedCard');
						}, 1500);
					} else if (attempt < 3) {
						// Retry with increasing delay if cell not found
						setTimeout(() => findAndScrollToCell(attempt + 1), 200 * attempt);
					}
				};
				
				findAndScrollToCell();
			}
		}, 800); // Increased delay to ensure calendar slots are fully synchronized
		} finally {
			if (ownsLock) {
				isAutoAssigningRef.current = false;
				setIsAutoAssigning(false);
			}
		}
	};
	const handleServiceSelect = async (service, paxIndex = null) => {
		if (isAutoAssigningRef.current) return;
		await addServiceToBooking(service, paxIndex);
	};
	// ADD SERVICE END

	// DELETE SERVICE START 
    const removeService = (counterKey, isSelf) => {
		logger.log("removeService > counterKey", counterKey);
		logger.log("removeService > isSelf", isSelf);
        if (isSelf) {
            const svcToDelete = selectedServices.find((s) => s.counterKey === counterKey);
            if (svcToDelete?.booking_item_id) {
                setDeletedBookingItemIds((prev) => ([...prev, svcToDelete.booking_item_id]));
            }

            let updated;
            if (svcToDelete && (svcToDelete.isSplitSegment || (!!svcToDelete.splitParent && svcToDelete.splitParent !== svcToDelete.counterKey))) {
                // delete only this split segment
                const baseKey = svcToDelete.splitParent || svcToDelete.counterKey;
                updated = selectedServices.filter((s) => s.counterKey !== counterKey);
                // simple: add deleted duration to immediate predecessor (or base)
                try {
                    const group = selectedServices.map((s,i)=>({s,i})).filter(({s}) => (s.splitParent || s.counterKey) === baseKey);
                    const deletedPos = group.findIndex(({s}) => s.counterKey === counterKey);
                    const baseEntry = group.find(({s}) => !s.isSplitSegment && s.counterKey === baseKey);
                    const predIdx = (deletedPos > 0) ? group[deletedPos - 1].i : (baseEntry ? baseEntry.i : -1);
                    if (predIdx > -1) {
                        const pred = updated[predIdx];
                        const addMin = Number(svcToDelete.duration || 0);
                        const newDur = Math.max(0, Number(pred.duration || 0) + addMin);
                        updated[predIdx] = {
                            ...pred,
                            duration: newDur,
                            end_time: pred?.start_time ? calculateEndTime(pred.start_time, newDur) : pred?.end_time,
                        };
                    }
                } catch {}
            } else {
                // delete base and all its segments
                const baseKey = svcToDelete ? svcToDelete.counterKey : counterKey;
                updated = selectedServices.filter((s) => s.counterKey !== baseKey && (s.splitParent || '') !== baseKey);
            }

            setSelectedServices(updated);
			
			// If we removed the first service and there are remaining services, update the main time
			if (updated.length > 0 && selectedServices[0]?.counterKey === counterKey) {
				const newFirstService = updated[0];
				// start_time is already in 24-hour format
				const formattedTime = buildSelectedTimePayloadForService(
					newFirstService.start_time,
					newFirstService.start_date
				);
				
				// Update the global selected time
				setSelectedTime(formattedTime);
				
				// Update booking data
				setBookingData((prev) => ({
					...prev,
					time: formattedTime,
				}));
			}
        } else {
			const deletedBookingItemIds = [];
			const updatedPaxes = paxes.map((pax) => {
				const filteredServices = pax.selectedServices.filter((s) => {
					if (s.counterKey === counterKey && s.booking_item_id) {
						deletedBookingItemIds.push(s.booking_item_id);
					}
					return s.counterKey !== counterKey;
				});
				return {
					...pax,
					selectedServices: filteredServices,
				};
			});
			if (deletedBookingItemIds.length > 0) {
				setDeletedBookingItemIds((prev) => [
					...prev,
					...deletedBookingItemIds,
				]);
			}
			setPaxes(updatedPaxes);
		}
	};
	// DELETE SERVICE END

	// REPLACE SERVICE START
	const handleReplaceService = (counterKey) => {
		setReplacingServiceKey(counterKey);
		setShowReplaceDropdown(true);
	};

	const handleServiceReplace = async (newService) => {
		if (replacingServiceKey) {
			// First check if it's a self service
			let serviceToReplace = selectedServices.find(
				(s) => s.counterKey === replacingServiceKey
			);
			
			if (serviceToReplace) {
				const serviceStartTime = serviceToReplace.start_time || ((fromEdit && editBooking?.time?.value) 
					? editBooking.time.value 
					: serviceToReplace.start_time);
				const serviceStartDate = serviceToReplace.start_date || ((fromEdit && editBooking?.date) 
					? formatDateToDDMMYYYY(editBooking.date) 
					: serviceToReplace.start_date);
				
				const replacedEndTime = calculateEndTime(serviceStartTime, newService.duration);
				const { startDateStr, endDateStr } = ensureServiceDates(
					serviceStartDate,
					serviceToReplace.end_date,
					serviceStartTime,
					replacedEndTime,
					newService.duration
				);
				
				// Calculate cross_day_booking_item if service crosses days (start_date !== end_date)
				const crossDayBookingItem = calculateCrossDayBookingItem(
					startDateStr,
					endDateStr,
					serviceStartTime,
					replacedEndTime
				);
				
				// Selecting a service picks a room from that service's list in both modes.
				// If the list has no room, keep the room already on this service.
				let autoSelectedRoomData = null;
				let selectedRoom = null;
				let autoSelectedRoomError = "";

				if (selectedOutletID && effectiveSelectedDate && (newService.id || newService.service) && newService.duration) {
					try {
						const normalizedRoomStartTime = normalizeRoomServiceAtTime(serviceStartTime);
						const roomAvailabilityDateStr = getRoomAvailabilityDateStr(startDateStr);
						const roomPayload = {date: roomAvailabilityDateStr,service_id: newService.service || newService.id,duration: newService.duration,service_at: `${roomAvailabilityDateStr} ${normalizedRoomStartTime}`,};
						const roomResponse = await getRoomsForCalendar(selectedOutletID, roomPayload);
						const roomsList = roomResponse?.data || [];
						const { room: autoRoomCandidate, errorMessage } = getAutoSelectedRoomCandidate(	roomsList,serviceStartTime,replacedEndTime,replacingServiceKey,serviceToReplace);
						if (autoRoomCandidate) {
							selectedRoom = autoRoomCandidate.value || autoRoomCandidate.item_id; autoSelectedRoomData = autoRoomCandidate; autoSelectedRoomError = errorMessage;
							setSelectedRooms(prev => ({...prev, [replacingServiceKey]: autoRoomCandidate}));
						}
					} catch (error) {
						console.error("Error fetching rooms for auto-selection on replace:", error);
					}
				}

				if (!selectedRoom) {
					const resolvedRoom = resolveRoomModeSelectedRoom(
						serviceToReplace,
						serviceStartTime,
						replacedEndTime,
						replacingServiceKey
					);
					selectedRoom = resolvedRoom.selectedRoom;
					autoSelectedRoomData = resolvedRoom.autoSelectedRoomData;
					if (autoSelectedRoomData) {
						setSelectedRooms((prev) => ({
							...prev,
							[replacingServiceKey]: autoSelectedRoomData,
						}));
					}
				}

				setRoomBookingConflictErrors((prev) => ({
					...prev,
					[replacingServiceKey]: autoSelectedRoomError,
				}));
				
				// Create selectedTherapist object if therapist exists
				const replaceTherapistId = serviceToReplace.therapist || null;
				let replaceSelectedTherapist = null;
				if (replaceTherapistId) {
					const therapistFromList = Array.isArray(homeStaffList) && homeStaffList.length > 0
						? homeStaffList.find((staff) => Number(staff.id) === Number(replaceTherapistId))
						: null;
					const therapistName = serviceToReplace?.selected_therapist?.label || therapistFromList?.name || therapistFromList?.alias || "";
					replaceSelectedTherapist = {
						value: Number(replaceTherapistId),
						label: therapistName,
						pager: serviceToReplace?.selected_therapist?.pager || therapistFromList?.pager_number || "",
						gender: serviceToReplace?.selected_therapist?.gender || therapistFromList?.gender || "",
					};
				}
				
				// Prefer resolved/selected room so replace never drops the assignment (both modes)
				let replaceRoomId = selectedRoom || serviceToReplace.roomId;
				let replaceRoomName = autoSelectedRoomData?.label || autoSelectedRoomData?.item_name || serviceToReplace.roomName;
				
				if (!replaceRoomId && serviceToReplace.room_segments?.[0]?.room_id) {
					replaceRoomId = serviceToReplace.room_segments[0].room_id;
				}
				
				if (!replaceRoomName && serviceToReplace.room_name) {
					replaceRoomName = serviceToReplace.room_name;
				}
				
				if (!replaceRoomId && selectedRooms[replacingServiceKey]?.value) {
					replaceRoomId = selectedRooms[replacingServiceKey].value;
				}
				if (!replaceRoomName && selectedRooms[replacingServiceKey]?.label) {
					replaceRoomName = selectedRooms[replacingServiceKey].label;
				}
				
				if (replaceRoomId && !replaceRoomName && homeRoomList && Array.isArray(homeRoomList)) {
					for (const room of homeRoomList) {
						if (Array.isArray(room.items)) {
							const matchedItem = room.items.find(item => Number(item.item_id) === Number(replaceRoomId));
							if (matchedItem) {
								replaceRoomName = matchedItem.item_name || matchedItem.name || null;
								break;
							}
						}
					}
				}

				const replacementRoomSelection = (
					autoSelectedRoomData || (replaceRoomId != null ? {
						value: replaceRoomId,
						item_id: replaceRoomId,
						label: replaceRoomName || "Room Selected",
						item_name: replaceRoomName || "Room Selected",
						item_type: serviceToReplace?.room_segments?.[0]?.item_type ?? null,
					} : null)
				);

				setSelectedRooms((prev) => {
					const next = { ...prev };
					if (replacementRoomSelection) {
						next[replacingServiceKey] = replacementRoomSelection;
					} else {
						delete next[replacingServiceKey];
					}
					return next;
				});
				
				// Replace self service
				const replacedService = {
					...newService,
					counterKey: replacingServiceKey,
					start_time: serviceStartTime,
					end_time: replacedEndTime,
					start_date: startDateStr,
					end_date: endDateStr,
					...getServiceTimelineMeta(serviceStartTime, startDateStr),
					cross_day_booking_item: crossDayBookingItem,
					therapist: serviceToReplace.therapist,
					duration: Number(newService.duration),
                    roomId: replaceRoomId || selectedRoom || null,
					roomName: replaceRoomName || autoSelectedRoomData?.label || autoSelectedRoomData?.item_name || "Selected Room",
					requested_person: serviceToReplace.requested_person,
					requested_room: isRoomFilterActive ? (serviceToReplace.room_segments?.[0]?.requested_room ?? serviceToReplace.requested_room ?? 0) : 0,
					// Set room data from auto-selected room or normalized segment shape
					room_segments: (() => {
						const rid = normalizeNumericId(replaceRoomId || selectedRoom);
						if (rid == null) return null;
						const roomMeta = replacementRoomSelection || {};
						return buildRoomSegments(
							{
								...newService,
								start_time: serviceStartTime,
								end_time: replacedEndTime,
								duration: Number(newService.duration),
								requested_room: isRoomFilterActive
									? (serviceToReplace.room_segments?.[0]?.requested_room ?? serviceToReplace.requested_room ?? 0)
									: 0,
								room_segments: serviceToReplace.room_segments,
							},
							rid,
							{
								...roomMeta,
								item_type:
									roomMeta.item_type ??
									roomMeta.item ??
									serviceToReplace?.room_segments?.[0]?.item_type ??
									null,
								room_type: roomMeta.room_type ?? serviceToReplace?.room_segments?.[0]?.room_type ?? null,
								facilities: Array.isArray(roomMeta.facilities)
									? roomMeta.facilities
									: (Array.isArray(serviceToReplace?.room_segments?.[0]?.facilities)
										? serviceToReplace.room_segments[0].facilities
										: []),
							}
						);
					})(),
					requested_therapist: serviceToReplace.requested_therapist, // Preserve user's choice
					requested_therapist_disabled: serviceToReplace.requested_therapist_disabled, // Preserve disabled state
					service_request: serviceToReplace.service_request,
					booking_item_id: serviceToReplace.booking_item_id, // Preserve id for overrides
					selected_therapist: replaceSelectedTherapist,
					isBefore6pm: isTimeBefore6PM(serviceStartTime),
					isMemberRateApplied: !isPremiumOutlet && isTimeBefore6PM(serviceStartTime) && isMembershipApplied && !publicHoliday && isWeekday(startDateStr),
					isMembershipApplied: isMembershipApplied,
					price_rate: resolveServicePriceRate(newService) || resolveServicePriceRate(serviceToReplace),
					// Set price based on isMemberRateApplied
					price: getServicePriceFromPriceRate(newService, isTimeBefore6PM(serviceStartTime) && isMembershipApplied && !publicHoliday && isWeekday(startDateStr)),
					service_category_name: resolveReplacementCategoryName(newService, serviceToReplace),
					service_category: resolveReplacementCategoryName(newService, serviceToReplace),
					service_category_id: resolveServiceCategoryId(newService),
				};

				setSelectedServices((prev) => {
                    // Remove any split segments tied to this base; revert to default edit mode
                    const baseKeyLocal = replacingServiceKey;
                    const removedKeys = new Set(
                        prev
                          .filter((s) => (s.splitParent || s.counterKey) === baseKeyLocal && s.isSplitSegment)
                          .map((s) => s.counterKey)
                    );
                    const withoutSplits = prev.filter((s) => {
                        const groupKey = (s.splitParent || s.counterKey);
                        if (groupKey !== baseKeyLocal) return true;
                        return !s.isSplitSegment;
                    });
                    const updated = withoutSplits.map((s) =>
                        s.counterKey === replacingServiceKey ? replacedService : s
                    );
                    
                    // Find the index of the replaced service
                    const replacedIndex = updated.findIndex(s => s.counterKey === replacingServiceKey);
                    
                    // Update all subsequent services after the replaced service
                    if (replacedIndex >= 0) {
                        for (let i = replacedIndex + 1; i < updated.length; i++) {
                            const prevSvc = updated[i - 1];
                            const prevEnd = calculateEndTime(prevSvc.start_time, prevSvc.duration);
                            const nextEnd = calculateEndTime(prevEnd, updated[i].duration);
                            // Use previous service's end_date as preferredStartDate to ensure correct date progression
                            const prevEndDate = prevSvc.end_date || prevSvc.start_date;
                            const seqDates = ensureServiceDates(
                                updated[i].start_date,
                                updated[i].end_date,
                                prevEnd,
                                nextEnd,
                                updated[i].duration,
                                prevEndDate
                            );
                            // Calculate cross_day_booking_item for subsequent services
                            const seqCrossDayItem = calculateCrossDayBookingItem(
                                seqDates.startDateStr,
                                seqDates.endDateStr,
                                prevEnd,
                                nextEnd
                            );
                            updated[i] = {
                                ...updated[i],
                                start_time: prevEnd,
                                end_time: nextEnd,
                                start_date: seqDates.startDateStr,
                                end_date: seqDates.endDateStr,
                                cross_day_booking_item: seqCrossDayItem,
                            };
                        }
                    }
                    
                    // Cleanup rooms for removed split segments
                    try {
                        if (removedKeys.size > 0) {
                            setSelectedRooms((prevRooms) => {
                                const next = { ...prevRooms };
                                removedKeys.forEach((k) => { if (k in next) delete next[k]; });
                                return next;
                            });
                        }
                    } catch {}
					// If this is the first service being replaced, update the main time
					if (prev.length > 0 && prev[0].counterKey === replacingServiceKey) {
						// start_time is already in 24-hour format
						const formattedTime = buildSelectedTimePayloadForService(
							replacedService.start_time,
							replacedService.start_date
						);
						
						// Update the global selected time
						setSelectedTime(formattedTime);
						
						// Update booking data
						setBookingData((prevBooking) => ({
							...prevBooking,
							time: formattedTime,
						}));
					}
					
					// FIXED: Update selectedServicesForEdit with replaced service in edit mode
					if (fromEdit) {
						setSelectedServicesForEdit((prevEdit) =>
							prevEdit.map((s) =>
								s.counterKey === replacingServiceKey ? { ...replacedService } : s
							)
						);
					}
					
					const firstMain = updated[0];
					const replaceAnchor =
						!isPremiumOutlet &&
						firstMain &&
						isTimeBefore6PM(firstMain.start_time) &&
						isMembershipApplied &&
						!publicHoliday &&
						isWeekday(firstMain.start_date || effectiveSelectedDate);
					return syncMemberRateAppliedFromFirstServices(updated, {
						anchor: replaceAnchor,
						getPrice: (s, a) => getServicePriceFromPriceRate(s, a),
					});
				});

				// setNoRoomSelected(true);

				//  Emit live override so name and height update immediately
				try {
					if (fromEdit && editableBooking?.id && replacedService?.booking_item_id) {
						emitOverrideUpdate({
							bookingId: editableBooking.id,
							itemId: replacedService.booking_item_id,
							start: replacedService.start_time,
							end: replacedService.end_time,
							therapistId: replacedService.therapist,
							therapistName: replacedService.therapist_name,
							serviceName: replacedService.name || replacedService.service_name,
							service_category: replacedService.service_category_name || replacedService.service_category || "",
							duration: Number(replacedService.duration) || 0,
						});
					}
				} catch {}
				// FIXED: Update calendar slot with new duration and timing
				// REMOVED: Calendar slot update - now handled by centralized sync
			} else {
				// Check if it's a pax service
				// Find the pax service first to get its details for room fetching
				let paxServiceToReplace = null;
				
				for (let i = 0; i < paxes.length; i++) {
					const found = paxes[i].selectedServices.find(
						(s) => s.counterKey === replacingServiceKey
					);
					if (found) {
						paxServiceToReplace = found;
						break;
					}
				}
				
				// Selecting a pax service picks a room from that service's list in both modes.
				// If the list has no room, keep the room already on this service.
				let autoSelectedRoomData = null;
				let selectedRoom = null;
				let autoSelectedRoomError = "";

				if (paxServiceToReplace && selectedOutletID && effectiveSelectedDate && (newService.id || newService.service) && newService.duration) {
					try {
						const paxServiceStartTime = paxServiceToReplace.start_time || ((fromEdit && editBooking?.time?.value) ? editBooking.time.value : paxServiceToReplace.start_time);
						const paxReplacementEnd = calculateEndTime(paxServiceStartTime, newService.duration);
						const paxServiceStartDate = paxServiceToReplace.start_date || ((fromEdit && editBooking?.date) ? formatDateToDDMMYYYY(editBooking.date) : paxServiceToReplace.start_date);
						const { startDateStr } = ensureServiceDates(paxServiceStartDate, paxServiceToReplace.end_date, paxServiceStartTime, paxReplacementEnd, newService.duration);
						const normalizedRoomStartTime = normalizeRoomServiceAtTime(paxServiceStartTime);
						const roomAvailabilityDateStr = getRoomAvailabilityDateStr(startDateStr);
						const roomPayload = {date: roomAvailabilityDateStr,service_id: newService.service || newService.id,duration: newService.duration,service_at: `${roomAvailabilityDateStr} ${normalizedRoomStartTime}`,};
						const roomResponse = await getRoomsForCalendar(selectedOutletID, roomPayload);
						const roomsList = roomResponse?.data || [];

						const { room: autoRoomCandidate, errorMessage } = getAutoSelectedRoomCandidate(roomsList,paxServiceStartTime,paxReplacementEnd,replacingServiceKey,paxServiceToReplace);

						if (autoRoomCandidate) {
							selectedRoom = autoRoomCandidate.value || autoRoomCandidate.item_id;autoSelectedRoomData = autoRoomCandidate;autoSelectedRoomError = errorMessage;
							setSelectedRooms(prev => ({...prev,[replacingServiceKey]: autoRoomCandidate}));
						}
					} catch (error) {
						console.error("Error fetching rooms for auto-selection on pax replace:", error);
					}
				}

				if (paxServiceToReplace && !selectedRoom) {
					const paxServiceStartTime = paxServiceToReplace.start_time || ((fromEdit && editBooking?.time?.value) ? editBooking.time.value : paxServiceToReplace.start_time);
					const paxReplacementEnd = calculateEndTime(paxServiceStartTime, newService.duration);
					const resolvedRoom = resolveRoomModeSelectedRoom(
						paxServiceToReplace,
						paxServiceStartTime,
						paxReplacementEnd,
						replacingServiceKey
					);
					selectedRoom = resolvedRoom.selectedRoom;
					autoSelectedRoomData = resolvedRoom.autoSelectedRoomData;
					if (autoSelectedRoomData) {
						setSelectedRooms((prev) => ({
							...prev,
							[replacingServiceKey]: autoSelectedRoomData,
						}));
					}
				}

				setRoomBookingConflictErrors((prev) => ({
					...prev,
					[replacingServiceKey]: autoSelectedRoomError,
				}));
				
				const updatedPaxes = paxes.map((pax) => {
					const paxService = pax.selectedServices.find(
						(s) => s.counterKey === replacingServiceKey
					);
					
					if (paxService) {
						const paxServiceStartTime = paxService.start_time || ((fromEdit && editBooking?.time?.value) 
							? editBooking.time.value 
							: paxService.start_time);
						const paxServiceStartDate = paxService.start_date || ((fromEdit && editBooking?.date) 
							? formatDateToDDMMYYYY(editBooking.date) 
							: paxService.start_date);
						
						const paxReplacementEnd = calculateEndTime(paxServiceStartTime, newService.duration);
						const { startDateStr, endDateStr } = ensureServiceDates(
							paxServiceStartDate,
							paxService.end_date,
							paxServiceStartTime,
							paxReplacementEnd,
							newService.duration
						);
						
						// Calculate cross_day_booking_item if service crosses days (start_date !== end_date)
						const paxCrossDayBookingItem = calculateCrossDayBookingItem(
							startDateStr,
							endDateStr,
							paxServiceStartTime,
							paxReplacementEnd
						);
						
						// Create selectedTherapist object if therapist exists
						const paxReplaceTherapistId = paxService.therapist || null;
						let paxReplaceSelectedTherapist = null;
						if (paxReplaceTherapistId) {
							const therapistFromList = Array.isArray(homeStaffList) && homeStaffList.length > 0
								? homeStaffList.find((staff) => Number(staff.id) === Number(paxReplaceTherapistId))
								: null;
							const therapistName = paxService?.selected_therapist?.label || therapistFromList?.name || therapistFromList?.alias || "";
							paxReplaceSelectedTherapist = {
								value: Number(paxReplaceTherapistId),
								label: therapistName,
								pager: paxService?.selected_therapist?.pager || therapistFromList?.pager_number || "",
								gender: paxService?.selected_therapist?.gender || therapistFromList?.gender || "",
							};
						}
						
						// Prefer resolved/selected room so pax replace never drops the assignment (both modes)
						let paxReplaceRoomId = selectedRoom || paxService.roomId;
						let paxReplaceRoomName = autoSelectedRoomData?.label || autoSelectedRoomData?.item_name || paxService.roomName;
						
						if (!paxReplaceRoomId && paxService.room_segments?.[0]?.room_id) {
							paxReplaceRoomId = paxService.room_segments[0].room_id;
						}
						
						if (!paxReplaceRoomName && paxService.room_name) {
							paxReplaceRoomName = paxService.room_name;
						}
						
						if (!paxReplaceRoomId && selectedRooms[replacingServiceKey]?.value) {
							paxReplaceRoomId = selectedRooms[replacingServiceKey].value;
						}
						if (!paxReplaceRoomName && selectedRooms[replacingServiceKey]?.label) {
							paxReplaceRoomName = selectedRooms[replacingServiceKey].label;
						}
						
						if (paxReplaceRoomId && !paxReplaceRoomName && homeRoomList && Array.isArray(homeRoomList)) {
							for (const room of homeRoomList) {
								if (Array.isArray(room.items)) {
									const matchedItem = room.items.find(item => Number(item.item_id) === Number(paxReplaceRoomId));
									if (matchedItem) {
										paxReplaceRoomName = matchedItem.item_name || matchedItem.name || null;
										break;
									}
								}
							}
						}

						const paxReplacementRoomSelection = (
							autoSelectedRoomData || (paxReplaceRoomId != null ? {
								value: paxReplaceRoomId,
								item_id: paxReplaceRoomId,
								label: paxReplaceRoomName || "Room Selected",
								item_name: paxReplaceRoomName || "Room Selected",
								item_type: paxService?.room_segments?.[0]?.item_type ?? null,
							} : null)
						);

						setSelectedRooms((prev) => {
							const next = { ...prev };
							if (paxReplacementRoomSelection) {
								next[replacingServiceKey] = paxReplacementRoomSelection;
							} else {
								delete next[replacingServiceKey];
							}
							return next;
						});
						
						const replacedService = {
							...newService,
							counterKey: replacingServiceKey,
							start_time: paxServiceStartTime,
							end_time: paxReplacementEnd,
							start_date: startDateStr,
							end_date: endDateStr,
							...getServiceTimelineMeta(paxServiceStartTime, startDateStr),
							cross_day_booking_item: paxCrossDayBookingItem,
							therapist: paxService.therapist,
							// Set room data from resolved/selected room
							roomId: paxReplaceRoomId || selectedRoom || null,
							room_segments: (() => {
								const rid = normalizeNumericId(paxReplaceRoomId || selectedRoom);
								if (rid == null) return null;
								const roomMeta = paxReplacementRoomSelection || {};
								return buildRoomSegments(
									{
										...newService,
										start_time: paxServiceStartTime,
										end_time: paxReplacementEnd,
										duration: Number(newService.duration),
										requested_room: isRoomFilterActive
											? (paxService.room_segments?.[0]?.requested_room ?? paxService.requested_room ?? 0)
											: 0,
										room_segments: paxService.room_segments,
									},
									rid,
									{
										...roomMeta,
										item_type:
											roomMeta.item_type ??
											roomMeta.item ??
											paxService?.room_segments?.[0]?.item_type ??
											null,
										room_type: roomMeta.room_type ?? paxService?.room_segments?.[0]?.room_type ?? null,
										facilities: Array.isArray(roomMeta.facilities)
											? roomMeta.facilities
											: (Array.isArray(paxService?.room_segments?.[0]?.facilities)
												? paxService.room_segments[0].facilities
												: []),
									}
								);
							})(),
							roomName: paxReplaceRoomName || autoSelectedRoomData?.label || autoSelectedRoomData?.item_name || "Selected Room",
							requested_person: paxService.requested_person,
							requested_room: isRoomFilterActive ? (paxService.room_segments?.[0]?.requested_room ?? paxService.requested_room ?? 0) : 0,
							requested_therapist: paxService.requested_therapist, // Preserve user's choice
							requested_therapist_disabled: paxService.requested_therapist_disabled, // Preserve disabled state
							service_request: paxService.service_request,
							booking_item_id: paxService.booking_item_id, // Preserve id for overrides
							selected_therapist: paxReplaceSelectedTherapist,
							isBefore6pm: isTimeBefore6PM(paxServiceStartTime),
							isMemberRateApplied: !isPremiumOutlet && isTimeBefore6PM(paxServiceStartTime) && isMembershipApplied && !publicHoliday && isWeekday(startDateStr),
							isMembershipApplied: isMembershipApplied,
							price_rate: resolveServicePriceRate(newService) || resolveServicePriceRate(paxService),
							// Set price based on isMemberRateApplied
							price: getServicePriceFromPriceRate(newService, isTimeBefore6PM(paxServiceStartTime) && isMembershipApplied && !publicHoliday && isWeekday(startDateStr)),
							service_category_name: resolveReplacementCategoryName(newService, paxService),
							service_category: resolveReplacementCategoryName(newService, paxService),
							service_category_id: resolveServiceCategoryId(newService),
						};

						// Apply the replacement in pax services
						let updatedPaxServices = pax.selectedServices.map((s) =>
							s.counterKey === replacingServiceKey ? replacedService : s
						);
						
						// Find the index of the replaced service
						const replacedPaxIndex = updatedPaxServices.findIndex(s => s.counterKey === replacingServiceKey);
						
						// Update all subsequent services after the replaced service
						if (replacedPaxIndex >= 0) {
							for (let i = replacedPaxIndex + 1; i < updatedPaxServices.length; i++) {
								const prevSvc = updatedPaxServices[i - 1];
								const prevEnd = calculateEndTime(prevSvc.start_time, prevSvc.duration);
								const nextEnd = calculateEndTime(prevEnd, updatedPaxServices[i].duration);
								// Use previous service's end_date as preferredStartDate to ensure correct date progression
								const prevEndDate = prevSvc.end_date || prevSvc.start_date;
								const seqDates = ensureServiceDates(
									updatedPaxServices[i].start_date,
									updatedPaxServices[i].end_date,
									prevEnd,
									nextEnd,
									updatedPaxServices[i].duration,
									prevEndDate
								);
								// Calculate cross_day_booking_item for subsequent services
								const seqCrossDayItem = calculateCrossDayBookingItem(
									seqDates.startDateStr,
									seqDates.endDateStr,
									prevEnd,
									nextEnd
								);
								updatedPaxServices[i] = {
									...updatedPaxServices[i],
									start_time: prevEnd,
									end_time: nextEnd,
									start_date: seqDates.startDateStr,
									end_date: seqDates.endDateStr,
									cross_day_booking_item: seqCrossDayItem,
								};
							}
						}
						
						const firstPaxSvc = updatedPaxServices[0];
						const paxReplaceAnchor =
							!isPremiumOutlet &&
							firstPaxSvc &&
							isTimeBefore6PM(firstPaxSvc.start_time) &&
							isMembershipApplied &&
							!publicHoliday &&
							isWeekday(firstPaxSvc.start_date || effectiveSelectedDate);
						const syncedPaxServices = syncMemberRateAppliedFromFirstServices(updatedPaxServices, {
							anchor: paxReplaceAnchor,
							getPrice: (s, a) => getServicePriceFromPriceRate(s, a),
						});

						const newPax = {
							...pax,
							selectedServices: syncedPaxServices,
						};

						// Emit override for pax card name/height update immediately
						try {
							if (fromEdit && editableBooking?.id && replacedService?.booking_item_id) {
								emitOverrideUpdate({
									bookingId: editableBooking.id,
									itemId: replacedService.booking_item_id,
									start: replacedService.start_time,
									end: replacedService.end_time,
									therapistId: replacedService.therapist,
									therapistName: replacedService.therapist_name,
									serviceName: replacedService.name || replacedService.service_name,
									service_category: replacedService.service_category_name || replacedService.service_category || "",
									duration: Number(replacedService.duration) || 0,
								});
							}
						} catch {}

						return newPax;
					}
					return pax;
				});
				
				setPaxes(updatedPaxes);

				// REMOVED: Duplicate calendar slot update - already handled above in the map function
			}
		}
	
	// REMOVED: Post-replacement cleanup - now handled by centralized sync
	
	setShowReplaceDropdown(false);
	setReplacingServiceKey(null);
	};
	// REPLACE SERVICE END

	// Helper: emit live override updates for calendar during edit
	const emitOverrideUpdate = (detail) => {
		try {
			if (!detail || !detail.itemId || !detail.bookingId) return;
			window.dispatchEvent(new CustomEvent('editOverrideUpdate', {
				detail,
			}));
		} catch {}
	};

	const markManualServiceTimes = (services, indices) => {
		const affected = new Set(indices || []);
		return (Array.isArray(services) ? services : []).map((svc, idx) => (affected.has(idx) ? { ...svc, hasManualTimeSelection: true } : svc));
	};

	const publishManualTimeChange = (services) => {
		const manual = (Array.isArray(services) ? services : []).filter((svc) => svc?.hasManualTimeSelection === true);
		if (manual.length === 0) return;
		try {window.dispatchEvent(new CustomEvent("editManualTimeSync", { detail: { services: manual } }));} catch {}
	};
	

	
	// TIME SELECT HANDLE START
	const enrichScheduledService = (svc, preferredStartDate = null) => {
		const startTime = svc.start_time;
		const endTime = svc.end_time || calculateEndTime(startTime, Number(svc.duration) || 0);
		const dates = ensureServiceDates(
			null,
			null,
			startTime,
			endTime,
			svc.duration,
			preferredStartDate || svc.start_date || getDefaultServiceDateStr()
		);
		const crossDayItem = calculateCrossDayBookingItem(
			dates.startDateStr,
			dates.endDateStr,
			startTime,
			endTime
		);
		const isMemberRateAppliedValue =
			!isPremiumOutlet &&
			isTimeBefore6PM(startTime) &&
			isMembershipApplied &&
			!publicHoliday &&
			isWeekday(dates.startDateStr);
		const timelineMeta = getServiceTimelineMeta(startTime, dates.startDateStr);
		const resolvedDayOffset =
			typeof svc.dayOffset === "number" && !Number.isNaN(svc.dayOffset)
				? svc.dayOffset
				: timelineMeta.dayOffset;
		const resolvedAbsoluteMinutes =
			typeof svc.absoluteMinutes === "number" && !Number.isNaN(svc.absoluteMinutes)
				? svc.absoluteMinutes
				: timelineMeta.absoluteMinutes;
		return {
			...svc,
			start_time: startTime,
			end_time: endTime,
			start_date: dates.startDateStr,
			end_date: dates.endDateStr,
			dayOffset: resolvedDayOffset,
			absoluteMinutes: resolvedAbsoluteMinutes,
			// Same-day early morning is not a calendar expansion card.
			is_calendar_expansion:
				resolvedDayOffset > 0 ||
				(typeof resolvedAbsoluteMinutes === "number" && resolvedAbsoluteMinutes >= 1440)
					? svc?.is_calendar_expansion
					: 0,
			cross_day_booking_item: crossDayItem,
			room_segments: svc.room_segments
				? svc.room_segments.map((segment) => ({
					...segment,
					start_time: startTime,
					end_time: endTime,
					duration: Number(svc.duration),
					requested_room: svc.requested_room ?? 0,
					item_type: segment.item_type ?? segment.item ?? null,
					room_type: segment.room_type ?? null,
					facilities: Array.isArray(segment.facilities) ? segment.facilities : [],
					meta_service: segment.meta_service ?? null,
					priority: segment.priority ?? 1,
				}))
				: null,
			isBefore6pm: isTimeBefore6PM(startTime),
			isMemberRateApplied: isMemberRateAppliedValue,
			isMembershipApplied: isMembershipApplied,
			price: getServicePriceFromPriceRate(svc, isMemberRateAppliedValue),
		};
	};

	const applyChainScheduleToGroup = (groupServices, movedCounterKey, newAbsoluteStartMinutes, getPreferredStartDate) => {
		// Prefer booking_item_id over UI counterKey so existing-edit time moves resolve.
		const movedService =(Array.isArray(groupServices) ? groupServices : []).find( (svc) => String(svc?.counterKey) === String(movedCounterKey)) || null;
		const movedKey =(movedService && resolveServiceScheduleKey(movedService)) || movedCounterKey;
		const { services: scheduled, affectedIndices, movedIndex } = scheduleServiceMove({
			services: ensureServiceLinkedFlags(groupServices),
			movedKey,
			newAbsoluteStartMinutes,
		});
		if (movedIndex < 0) {
			return { services: groupServices, affectedIndices: [], movedIndex: -1 };
		}
		const affectedSet = new Set(affectedIndices);
		const enriched = scheduled.map((svc, index) => {
			if (!affectedSet.has(index)) return svc;
			const preferred =
				typeof getPreferredStartDate === "function"
					? getPreferredStartDate(svc, index)
					: null;
			return enrichScheduledService(svc, preferred);
		});
		return { services: enriched, affectedIndices, movedIndex };
	};

	const markCreateChainTimeSync = (options = {}) => {
		if (fromEdit) return;
		const dateStr = formatDateToDDMMYYYY(effectiveSelectedDate || selectedDate);
		if (!dateStr) return;
		setCalendarDateSyncSignal((prev) => ({
			requestId: Number(prev?.requestId || 0) + 1,
			mode: "create",
			status: "ready",
			syncIntent: "time",
			date: dateStr,
			refreshKey: Number(prev?.refreshKey || 0) + 1,
		}));
		// Keep prevDateTimeRef aligned with booking header time only — never a non-first service clock.
		if (options?.headerTimeValue) {
			prevDateTimeRef.current = {
				date: dateStr,
				time: options.headerTimeValue,
			};
		}
	};

	const handleTimeClick = async (service, serviceCounterKey, currentTime) => {
		setRoomBookingConflictErrors((prev) => ({
			...(prev || {}),
			[serviceCounterKey]: "",
		}));
		const selectedOption = currentTime && typeof currentTime === "object"
			? currentTime
			: { value: currentTime };
		const rawSelectionValue = selectedOption?.timeValue || selectedOption?.value || currentTime;
		const parsedSelection = parseExtendedDropdownValue(selectedOption?.value || rawSelectionValue);
		const time24h = parsedSelection.value || (
			typeof rawSelectionValue === "string" && /am|pm/i.test(rawSelectionValue)
				? convertTo24HourFormat(rawSelectionValue)
				: String(rawSelectionValue || "").slice(0, 5)
		);
		const explicitDayOffset = selectedOption?.isNextDay || parsedSelection.isNextDay ? 1 : 0;

		const serviceIndex = selectedServices.findIndex(s => s.counterKey === serviceCounterKey);
		let isPaxService = false;
		let paxIndex = -1;
		let paxServiceIndex = -1;

		if (serviceIndex === -1) {
			for (let i = 0; i < paxes.length; i++) {
				const idx = paxes[i].selectedServices.findIndex(s => s.counterKey === serviceCounterKey);
				if (idx !== -1) {
					isPaxService = true;
					paxIndex = i;
					paxServiceIndex = idx;
					break;
				}
			}
			if (!isPaxService) return;
		}

		const scheduleGroup = isPaxService
			? paxes[paxIndex].selectedServices
			: selectedServices;
		const scheduleIndex = isPaxService ? paxServiceIndex : serviceIndex;
		const firstServiceInChain = Array.isArray(scheduleGroup)
			? (scheduleGroup.find((svc) => !svc?.isSplitSegment) || scheduleGroup[0] || null)
			: null;
		const previousService = scheduleIndex > 0 ? scheduleGroup[scheduleIndex - 1] : null;
		const selectedDateStr = getDefaultServiceDateStr();
		const crossDayEligible = isServiceEligibleForCrossDayTimeSelection(service, selectedDateStr, previousService
		) || isServiceEligibleForCrossDayTimeSelection(firstServiceInChain, selectedDateStr, null);

		// Same-day non-split: keep clock on selected day (no nearest → next day).
		// Cross-day / split / prev-next date: nearest relative to selected date.
		let formattedTime;
		let resolvedDayOffset;
		let newAbsoluteStartMinutes;
		if (crossDayEligible) {
			let referenceService = service;
			if (previousService && service?.linkedToPrevious !== false && previousService?.start_date && previousService?.end_date && previousService.start_date !== previousService.end_date) {
				const childClock = convertToMinutes(String(service?.start_time || "").slice(0, 5));
				if (!Number.isNaN(childClock) && childClock < 6 * 60) {
					referenceService = { ...service, start_date: previousService.end_date };
				}
			}
			const referenceAbsoluteMinutes = getViewRelativeAbsoluteStartMinutes(referenceService, selectedDateStr, getServiceAbsoluteStartMinutes(referenceService));
			formattedTime = resolveNearestTimeSelection(time24h, referenceAbsoluteMinutes, explicitDayOffset, selectedOption?.label || "" ) || buildTimeSelection(time24h, explicitDayOffset, selectedOption?.label || "");
			resolvedDayOffset = typeof formattedTime?.dayOffset === "number" && !Number.isNaN(formattedTime.dayOffset) ? formattedTime.dayOffset : explicitDayOffset;
			newAbsoluteStartMinutes = typeof formattedTime?.absoluteMinutes === "number" && !Number.isNaN(formattedTime.absoluteMinutes) ? formattedTime.absoluteMinutes : (resolvedDayOffset * 1440) + convertToMinutes(time24h);
		} else {
			// Same-day non-split: keep clock on selected day even if the option is labeled +1.
			resolvedDayOffset = scheduleIndex <= 0 && Number(explicitDayOffset) > 0 ? 1 : 0;
			formattedTime = buildTimeSelection(time24h,resolvedDayOffset, selectedOption?.label || "") || buildTimeSelection(time24h, resolvedDayOffset);
			newAbsoluteStartMinutes = typeof formattedTime?.absoluteMinutes === "number" && !Number.isNaN(formattedTime.absoluteMinutes) ? formattedTime.absoluteMinutes : (resolvedDayOffset * 1440) + convertToMinutes(time24h);
	}

		// Split segments stay on the single-card path (not part of the schedule chain).
		if (service?.isSplitSegment) {
			if (isPaxService) {
				const updatedPaxes = [...paxes];
				const paxService = updatedPaxes[paxIndex].selectedServices[paxServiceIndex];
				const newPaxEndTime = calculateEndTime(time24h, paxService.duration);
				const baseStartDatePreference = getBaseStartDateForPaxService(paxIndex, paxServiceIndex, formattedTime);
				updatedPaxes[paxIndex].selectedServices[paxServiceIndex] = {
					...enrichScheduledService(
					{
						...paxService,
						start_time: time24h,
						end_time: newPaxEndTime,
						dayOffset: resolvedDayOffset,
						absoluteMinutes: newAbsoluteStartMinutes,
					},
					baseStartDatePreference
				),
					hasManualTimeSelection: true,
				};
				setPaxes(updatedPaxes);
				publishManualTimeChange(updatedPaxes[paxIndex].selectedServices);
				scrollToServiceCard(serviceCounterKey, updatedPaxes[paxIndex].selectedServices[paxServiceIndex], interval, homeStaffList);
			} else {
				const updatedServices = [...selectedServices];
				const newEndTime = calculateEndTime(time24h, updatedServices[serviceIndex].duration);
				updatedServices[serviceIndex] = {
					...enrichScheduledService(
					{
						...updatedServices[serviceIndex],
						start_time: time24h,
						end_time: newEndTime,
						dayOffset: resolvedDayOffset,
						absoluteMinutes: newAbsoluteStartMinutes,
					},
					getBaseStartDateForMainService(serviceIndex, formattedTime)
				),
					hasManualTimeSelection: true,
				};
				setSelectedServices(updatedServices);
				publishManualTimeChange(updatedServices);
				scrollToServiceCard(serviceCounterKey, updatedServices[serviceIndex], interval, homeStaffList);
			}
			setBookingChanged(true);
			return;
		}

		if (isPaxService) {
			const group = paxes[paxIndex].selectedServices;
			const { services: scheduled, affectedIndices, movedIndex } = applyChainScheduleToGroup(
				group,
				serviceCounterKey,
				newAbsoluteStartMinutes,
				(svc) => (
					crossDayEligible
						? getDateStrForAbsoluteMinutes(svc.absoluteMinutes)
						: selectedDateStr
				)
			);

			const firstPaxLane = scheduled[0];
			const paxTimeAnchor =
				!isPremiumOutlet &&
				firstPaxLane &&
				isTimeBefore6PM(firstPaxLane.start_time) &&
				isMembershipApplied &&
				!publicHoliday &&
				isWeekday(firstPaxLane.start_date || effectiveSelectedDate);
			const syncedPaxServices = markManualServiceTimes(syncMemberRateAppliedFromFirstServices(scheduled, {anchor: paxTimeAnchor,getPrice: (s, a) => getServicePriceFromPriceRate(s, a),}),affectedIndices);

			const updatedPaxes = [...paxes];
			updatedPaxes[paxIndex] = {
				...updatedPaxes[paxIndex],
				selectedServices: syncedPaxServices,
			};
			latestPaxesRef.current = updatedPaxes;
			setPaxes(updatedPaxes);
			publishManualTimeChange(syncedPaxServices);
			markCreateChainTimeSync();

			if (fromEdit && editableBooking?.id) {
				affectedIndices.forEach((idx) => {
					const svc = syncedPaxServices[idx];
					if (!svc?.booking_item_id) return;
					emitOverrideUpdate({
						bookingId: editableBooking.id,
						itemId: svc.booking_item_id,
						start: svc.start_time,
						end: svc.end_time,
						therapistId: svc.therapist,
						therapistName: svc.therapist_name,
						service_at: buildOverrideServiceAt(svc),
						absoluteStartMinutes: svc.absoluteMinutes,
						linkedToPrevious: svc.linkedToPrevious,
						duration: svc.duration,
					});
				});
			}

			scrollToServiceCard(
				serviceCounterKey,
				syncedPaxServices[movedIndex >= 0 ? movedIndex : paxServiceIndex],
				interval,
				homeStaffList
			);
		} else {
			const { services: scheduled, affectedIndices, movedIndex } = applyChainScheduleToGroup(
				selectedServices,
				serviceCounterKey,
				newAbsoluteStartMinutes,
				(svc) => (
					crossDayEligible
						? getDateStrForAbsoluteMinutes(svc.absoluteMinutes)
						: selectedDateStr
				)
			);

			const isMovingFirstMain = serviceIndex === 0;
			if (isMovingFirstMain && !fromEdit) {
				setSelectedTime(formattedTime);
				setBookingData((prev) => ({ ...prev, time: formattedTime }));
			}

			const firstMainSvc = scheduled[0];
			const mainTimeAnchor =
				!isPremiumOutlet &&
				firstMainSvc &&
				isTimeBefore6PM(firstMainSvc.start_time) &&
				isMembershipApplied &&
				!publicHoliday &&
				isWeekday(firstMainSvc.start_date || effectiveSelectedDate);
			const syncedMainServices = markManualServiceTimes(syncMemberRateAppliedFromFirstServices(scheduled, {anchor: mainTimeAnchor,getPrice: (s, a) => getServicePriceFromPriceRate(s, a),}),affectedIndices);
			latestSelectedServicesRef.current = syncedMainServices;
			setSelectedServices(syncedMainServices);
			publishManualTimeChange(syncedMainServices);
			markCreateChainTimeSync(
				isMovingFirstMain && !fromEdit && formattedTime?.value
					? { headerTimeValue: formattedTime.value }
					: undefined
			);

			if (fromEdit && editableBooking?.id) {
				affectedIndices.forEach((idx) => {
					const svc = syncedMainServices[idx];
					if (!svc?.booking_item_id) return;
					emitOverrideUpdate({
						bookingId: editableBooking.id,
						itemId: svc.booking_item_id,
						start: svc.start_time,
						end: svc.end_time,
						therapistId: svc.therapist,
						therapistName: svc.therapist_name,
						service_at: buildOverrideServiceAt(svc),
						absoluteStartMinutes: svc.absoluteMinutes,
						linkedToPrevious: svc.linkedToPrevious,
						duration: svc.duration,
					});
				});
			}

			scrollToServiceCard(
				serviceCounterKey,
				syncedMainServices[movedIndex >= 0 ? movedIndex : serviceIndex],
				interval,
				homeStaffList
			);
		}

		setBookingChanged(true);
	};
	// TIME SELECT HANDLE END


	// --- Helper for scrolling + highlight ---
	const scrollToServiceCard = (counterKey, updatedService, interval, homeStaffList) => {
		setTimeout(() => {
			const rowIndex = getCalendarRowIndexForService(updatedService, interval);
			const bookingItemId = getBookingItemId(updatedService);
			// Compute visible column index from header DOM to respect filters/search.
			// In therapist mode a therapist hidden by filters has no column (-1), so we never
			// scroll to an unrelated empty cell.
			let colIndex = isRoomFilterActive ? 0 : -1;
			try {
				const headerCells = Array.from(document.querySelectorAll('thead th[data-therapist-id]'));
				const foundIdx = headerCells.findIndex((th) => Number(th.getAttribute('data-therapist-id')) === Number(updatedService.therapist));
				if (foundIdx >= 0) colIndex = foundIdx;
			} catch (e) {}

			const findAndScroll = (attempt = 1) => {
				const serviceCard = document.querySelector(`[data-counter-key="${counterKey}"]`);
				if (serviceCard) {
					serviceCard.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
					serviceCard.classList.remove('highlightedCard');
					void serviceCard.offsetWidth;
					serviceCard.classList.add('highlightedCard');
					setTimeout(() => serviceCard.classList.remove('highlightedCard'), 1500);
				} else {
					if (bookingItemId != null) {
					const existingBookingCard = document.querySelector(`[data-item-id="${bookingItemId}"]`);
					if (existingBookingCard) {
						existingBookingCard.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
						existingBookingCard.classList.remove('highlightedCard');
						void existingBookingCard.offsetWidth;
						existingBookingCard.classList.add('highlightedCard');
						setTimeout(() => existingBookingCard.classList.remove('highlightedCard'), 1500);
						return;
					}
					}
					if (colIndex < 0) {
						if (attempt < 4) setTimeout(() => findAndScroll(attempt + 1), 200 * attempt);
						return;
					}
					const sel = `[data-row="${rowIndex}"][data-col="${colIndex}"]`;
					const cells = document.querySelectorAll(sel);
					if (cells.length > 0) {
						const firstCell = cells[0];
						firstCell.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
						firstCell.classList.remove('highlightedCard');
						void firstCell.offsetWidth;
						firstCell.classList.add('highlightedCard');
						setTimeout(() => firstCell.classList.remove('highlightedCard'), 1500);
					}
					for (const cell of cells) {
						const slot = cell.querySelector(`[data-counter-key="${counterKey}"]`);
						if (slot) {
							cell.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
							cell.classList.remove('highlightedCard');
							void cell.offsetWidth;
							cell.classList.add('highlightedCard');
							setTimeout(() => cell.classList.remove('highlightedCard'), 1500);
							return;
						}
					}
					if (attempt < 4) setTimeout(() => findAndScroll(attempt + 1), 200 * attempt);
				}
			};

			findAndScroll();
		}, 500);
	};

	useEffect(() => {
		if (!calendarDateSyncSignal?.requestId) return;
		if (calendarDateSyncSignal?.status !== "ready") return;
		if (calendarDateSyncSignal?.mode !== (fromEdit ? "edit" : "create")) return;
		// Create + time-only scroll is handled after slot reprojection in useLayoutEffect above.
		if (!fromEdit && calendarDateSyncSignal?.syncIntent === "time") return;
		if (lastDateSyncScrollRequestRef.current === calendarDateSyncSignal.requestId) return;

		const scrollDelay = 1800;

		const primaryService =
			(Array.isArray(selectedServices) ? selectedServices.find((svc) => !svc?.isSplitSegment) : null) ||
			(Array.isArray(selectedServices) ? selectedServices[0] : null) ||
			(Array.isArray(paxes)
				? paxes.flatMap((pax) => (Array.isArray(pax?.selectedServices) ? pax.selectedServices : []))
					.find((svc) => !svc?.isSplitSegment)
				: null) ||
			(Array.isArray(paxes)
				? paxes.flatMap((pax) => (Array.isArray(pax?.selectedServices) ? pax.selectedServices : []))[0]
				: null);

		if (!primaryService?.start_time) {
			if (!fromEdit) {
				const emptySlot = Array.isArray(newAppointmentSlots)
					? newAppointmentSlots.find((slot) => slot?.counterKey != null)
					: null;
				if (emptySlot?.counterKey != null) {
					lastDateSyncScrollRequestRef.current = calendarDateSyncSignal.requestId;
					setTimeout(() => {
						scrollToNewBookingCard(emptySlot.counterKey, 50);
					}, scrollDelay);
				}
			}
			return;
		}

		lastDateSyncScrollRequestRef.current = calendarDateSyncSignal.requestId;
		const bookingItemId = getBookingItemId(primaryService);
		if (fromEdit && bookingItemId != null) {
			scrollToBookingCard(bookingItemId, 1800);
			return;
		}
		if (primaryService?.counterKey != null) {
			scrollToNewBookingCard(primaryService.counterKey, scrollDelay);
		}
	}, [calendarDateSyncSignal?.requestId, calendarDateSyncSignal?.status, calendarDateSyncSignal?.mode, fromEdit, selectedServices, paxes, newAppointmentSlots, interval, homeStaffList]);

	// THERAPIST SELECT HANDLE START
	const handleTherapistSelect = async (service, serviceCounterKey, therapist) => {
		if (!therapist) return;
		const rawVal = (therapist && typeof therapist === 'object')
			? (therapist.value ?? therapist.id ?? therapist.label ?? null)
			: therapist;
		const therapistValue = normalizeNumericId(rawVal);
		const targetItemId = getBookingItemId(service);

		// FIXED: Handle room mode properly - distinguish between room selection and therapist selection
		// In room mode, if therapist.roomId exists but there's also a therapist value, it's a therapist selection
		// Only treat as room-only selection if there's no therapist value
		const roomValue = normalizeNumericId(therapist?.roomId);
		const isRoomSelection = isRoomFilterActive && roomValue != null && therapistValue === null;
		const isTherapistSelection = therapistValue !== null;
		const isPaxService = String(serviceCounterKey).includes("-");
		const therapistObj = (typeof therapist === 'object' && therapist !== null)
			? therapist
			: homeStaffList?.find((staff) => Number(staff.id) === Number(therapistValue));
		const updatedSelectedTherapist = buildSelectedTherapistOption(therapistValue, therapistObj);
		if (isTherapistSelection && therapistValue != null) {
			manualTherapistSelectionRef.current[serviceCounterKey] = therapistValue;
		} else {
			delete manualTherapistSelectionRef.current[serviceCounterKey];
		}
		delete rosterClearedTherapistRef.current[serviceCounterKey];

		const applyPatch = (currentService) => {
			if (!matchesServiceTarget(currentService, serviceCounterKey, targetItemId)) {
				return currentService;
			}

			if (isRoomSelection) {
				return {
					...currentService,
					roomId: roomValue,
					roomName: therapist.roomName || getRoomName(currentService),
					therapist: null,
					isAutoAssignedTherapist: false,
					hasManualTherapistSelection: false,
					selected_therapist: null,
					requested_therapist: currentService.requested_therapist || false,
					requested_person: currentService.requested_person ?? 0,
					requested_room: currentService.requested_room ?? 0,
					requested_therapist_disabled: false,
				};
			}

			if (isTherapistSelection) {
				return {
					...currentService,
					therapist: therapistValue,
					isAutoAssignedTherapist: false,
					hasManualTherapistSelection: true,
					selected_therapist: updatedSelectedTherapist,
					...(isRoomFilterActive && roomValue != null ? {
						roomId: roomValue,
						roomName: therapist.roomName || getRoomName(currentService),
					} : {}),
					requested_therapist: currentService.requested_therapist || false,
					requested_person: currentService.requested_person ?? 0,
					requested_room: currentService.requested_room ?? 0,
					requested_therapist_disabled: false,
				};
			}

			return {
				...currentService,
				therapist: null,
				isAutoAssignedTherapist: false,
				hasManualTherapistSelection: false,
				selected_therapist: null,
				requested_therapist: currentService.requested_therapist || false,
				requested_person: currentService.requested_person ?? 0,
				requested_room: currentService.requested_room ?? 0,
				requested_therapist_disabled: false,
			};
		};

		if (!isPaxService) {
			setSelectedServices((prevServices) =>
				(Array.isArray(prevServices) ? prevServices : []).map(applyPatch)
			);
		} else {
			setPaxes((prevPaxes) =>
				(Array.isArray(prevPaxes) ? prevPaxes : []).map((pax) => ({
					...pax,
					selectedServices: (Array.isArray(pax.selectedServices) ? pax.selectedServices : []).map(applyPatch),
				}))
			);
		}

		if (isTherapistSelection) {
			const newSvc = {
				...service,
				therapist: therapistValue,
				selected_therapist: updatedSelectedTherapist,
				...(isRoomFilterActive && roomValue != null ? {
					roomId: roomValue,
					roomName: therapist.roomName || getRoomName(service),
				} : {}),
			};
			scrollToServiceCard(serviceCounterKey, newSvc, interval, homeStaffList);
		}
		
		// Live-override: move existing card across therapists during edit
		try {
			const itemId = getBookingItemId(service);
			const start = service?.start_time;
			const end = service?.end_time || (start ? calculateEndTime(start, service?.duration) : undefined);
			// Ensure the calendar card height reflects the first therapist (earliest with therapist) duration in split mode
			const baseKey = service.splitParent || service.counterKey;
			// Build a local snapshot of the group that includes the just-selected therapist
			const snapshot = (Array.isArray(selectedServices) ? selectedServices : []).map((s) => (
				(s.counterKey === serviceCounterKey ? { ...s, therapist: therapistValue } : s)
			));
			const groupNow = snapshot.filter((s) => (s.splitParent || s.counterKey) === baseKey);
			const withTherapist = groupNow.filter((s) => s && s.therapist !== undefined && s.therapist !== null && s.therapist !== "");
			const toMinutes = (hhmm) => {
				try { return convertToMinutes(String(hhmm || "00:00")); } catch { return 0; }
			};
			const earliest = withTherapist.length > 0 ? withTherapist.reduce((minS, s) => (toMinutes(s.start_time) < toMinutes(minS.start_time) ? s : minS), withTherapist[0]) : null;
			const firstTherapistDuration = Number((earliest ? earliest.duration : (groupNow.find((s) => !s.isSplitSegment && s.counterKey === baseKey)?.duration)) || service?.duration || 0);
			if (fromEdit && editableBooking?.id && itemId) {
				emitOverrideUpdate({
					bookingId: editableBooking.id,
					itemId,
					start,
					end,
					therapistId: therapistValue,
					therapistName: therapist?.label,
					// Override duration so card height updates to first therapist's (base service) duration
					duration: firstTherapistDuration,
				});
			}
		} catch {}
		
		// Trigger sync to update calendar slots
		setTherapistChangeTrigger(prev => prev + 1);
		// REMOVED: Calendar slot update - now handled by centralized sync
	};
	// THERAPIST SELECT HANDLE END	

	// To update therapist list on date and time changes

	// ADDED: Effect to update all services when date or time changes
	useEffect(() => {
		const selectedServicesSnapshot = latestSelectedServicesRef.current || [];
		const paxesSnapshot = latestPaxesRef.current || [];
		const activeDateSyncSignal = latestCalendarDateSyncSignalRef.current;
		const isPendingDateSyncForCurrentMode =
			activeDateSyncSignal?.status === "pending" &&
			activeDateSyncSignal?.mode === (fromEdit ? "edit" : "create");
		const isExplicitDateSyncRequest =
			isPendingDateSyncForCurrentMode && activeDateSyncSignal?.syncIntent === "date";
		const effectiveDateStrForSync =
			isPendingDateSyncForCurrentMode && activeDateSyncSignal?.date
				? activeDateSyncSignal.date
				: (effectiveSelectedDate ? formatDateToDDMMYYYY(effectiveSelectedDate) : null);
		// Skip if no services exist
		if ((!selectedServicesSnapshot || selectedServicesSnapshot.length === 0) && (!paxesSnapshot || paxesSnapshot.length === 0)) return;
		// Skip if date or time is not available
		if (!effectiveDateStrForSync || !selectedTime?.value) return;
		// In edit mode, only run this flow for an explicit date-sync request.
		if (fromEdit && !isPendingDateSyncForCurrentMode) return;

		const dateStr = effectiveDateStrForSync;
		const timeValue = selectedTime.value;

		// Create + time-only: chain scheduling is owned by DateTimeOutletSelector / handleTimeClick.
		// Skip this effect's legacy sequential re-pack so linked children are not overwritten.
		if (
			!fromEdit &&
			activeDateSyncSignal?.status === "ready" &&
			activeDateSyncSignal?.syncIntent === "time" &&
			activeDateSyncSignal?.date === dateStr
		) {
			prevDateTimeRef.current = { date: dateStr, time: timeValue };
			return;
		}

		const prevDate = prevDateTimeRef.current?.date;
		const prevTime = prevDateTimeRef.current?.time;
		const isCreateTimeOnlyChange =
			!fromEdit &&
			!isExplicitDateSyncRequest &&
			prevDate != null &&
			prevDate === dateStr &&
			prevTime != null &&
			prevTime !== timeValue;

		if (isCreateTimeOnlyChange) {
			prevDateTimeRef.current = { date: dateStr, time: timeValue };
			return;
		}
		
		// Check if date or time actually changed
		if (
			!isExplicitDateSyncRequest &&
			prevDate === dateStr &&
			prevTime === timeValue
		) {
			return; // No change, skip update
		}
		
		// Update ref
		prevDateTimeRef.current = { date: dateStr, time: timeValue };
		const createSyncRequestId = !fromEdit
			? (latestCreateSyncRequestRef.current = Number(latestCreateSyncRequestRef.current || 0) + 1)
			: null;

		const updateAllServicesOnDateTimeChange = async () => {
			const isStaleCreateSyncRequest = () =>
				!fromEdit && Number(createSyncRequestId) !== Number(latestCreateSyncRequestRef.current);
			// Explicit date change (create and edit): drop a therapist who is not on the new date's roster.
			// Otherwise the card shows "Select therapist" (no option for them) while `therapist` keeps the
			// old id and is sent in the payload. `rosterOptions` is the unfiltered staff list
			// (availability: 0), so absence means off duty, not busy. `didRefetch` is true only when the
			// roster request succeeded for this sync, so a failed request keeps the therapist.
			const shouldClearTherapistOffRoster = (service, therapistId, rosterOptions, didRefetch) => {
				if (!isExplicitDateSyncRequest || !didRefetch) return false;
				const normalizedTherapistId = normalizeNumericId(therapistId);
				if (normalizedTherapistId == null) return false;
				const isOnRoster = (Array.isArray(rosterOptions) ? rosterOptions : []).some((option) => normalizeNumericId(option?.value) === normalizedTherapistId);
				if (isOnRoster) return false;
				if (service?.counterKey != null) {delete manualTherapistSelectionRef.current[service.counterKey]; rosterClearedTherapistRef.current[service.counterKey] = normalizedTherapistId;}
				return true;
			};
			const restoreRosterClearedTherapist = (service, therapistId, rosterOptions, didRefetch) => {
				if (therapistId != null || !isExplicitDateSyncRequest || !didRefetch || service?.counterKey == null) {return therapistId;}
				const rememberedId = normalizeNumericId(rosterClearedTherapistRef.current[service.counterKey]);
				if (rememberedId == null) return therapistId;
				const isOnRoster = (Array.isArray(rosterOptions) ? rosterOptions : []).some((option) => normalizeNumericId(option?.value) === rememberedId);
				if (!isOnRoster) return therapistId;
				delete rosterClearedTherapistRef.current[service.counterKey];
				return rememberedId;
			};
			try {
				const activeSignal = latestCalendarDateSyncSignalRef.current;
				const resolvedDateStr =
					fromEdit && activeSignal?.status === "pending" && activeSignal?.mode === (fromEdit ? "edit" : "create") && activeSignal?.date
						? activeSignal.date
						: effectiveDateStrForSync;
				const dateStr = resolvedDateStr;
				const timeValue = selectedTime.value;
				const activeDateSyncSignal = activeSignal;
				const shouldFinalizeCalendarDateSync =
					activeDateSyncSignal?.status === "pending" &&
					activeDateSyncSignal?.mode === (fromEdit ? "edit" : "create") &&
					activeDateSyncSignal?.date === dateStr;
				const activeDateSyncRequestId = shouldFinalizeCalendarDateSync
					? activeDateSyncSignal?.requestId
					: null;
				logEditDateSyncDebug("dateSync:start", {
					dateStr,
					timeValue,
					activeDateSyncSignal,
					selectedServicesSnapshot: (selectedServicesSnapshot || []).map((service) => ({
						counterKey: service?.counterKey,
						start_time: service?.start_time,
						end_time: service?.end_time,
						start_date: service?.start_date,
						end_date: service?.end_date,
						therapist: getTherapistId(service),
					})),
					paxesSnapshot: (paxesSnapshot || []).map((pax) => ({
						id: pax?.id,
						selectedServices: (pax?.selectedServices || []).map((service) => ({
							counterKey: service?.counterKey,
							start_time: service?.start_time,
							end_time: service?.end_time,
							start_date: service?.start_date,
							end_date: service?.end_date,
							therapist: getTherapistId(service),
						})),
					})),
				});
				
				// Fetch service list to get updated prices based on new date/time
				let servicePriceMap = new Map();
				if (selectedOutletID && selectedOutletData?.outlet_type_id) {
					try {
						const serviceListParams = {
							outlet: selectedOutletID,
							pagination: 0,
							status: 1,
							outlet_type: selectedOutletData?.outlet_type_id,
							service_at: `${dateStr} ${timeValue}`,
						};
						
						const serviceListResponse = await getServicesList(serviceListParams);
						if (isStaleCreateSyncRequest()) return;
						const serviceCategories = serviceListResponse?.data?.data?.list?.category || [];
						
						// Build a map of serviceId -> { price, member_price, name }
						serviceCategories.forEach((category) => {
							if (category?.services && Array.isArray(category.services)) {
								category.services.forEach((svc) => {
									if (svc?.id != null) {
										servicePriceMap.set(Number(svc.id), {
											price: getServicePriceFromPriceRate(svc, isTimeBefore6PM(timeValue) && isMembershipApplied),
											member_price: svc.member_price,
											name: svc.name,
											duration: svc.duration,
											price_rate: svc.price_rate || null,
											service_category_name: category?.name || category?.category_name || "",
										});
									}
								});
							}
						});
						
						// Update sessionStorage for future use
						sessionStorage.setItem('serviceList', JSON.stringify(serviceCategories));
					} catch (err) {
						console.error("Error fetching service list on date/time change:", err);
					}
				}
				
				// Update main services sequentially. When the booking date changes,
				// rebuild the date chain from the newly selected base date instead of
				// reusing stale per-service dates from the previous day.
				let currentStartTime = timeValue;
				let currentMainBaseDate = getTimelineBaseDateStr(selectedTime);
				const updatedMainServices = [];
				
				for (const service of selectedServicesSnapshot) {
					// Update service price from fetched service list
					const serviceId = Number(service.id || service.service);
					const updatedServiceData = servicePriceMap.get(serviceId);
					
					// Use existing start_time if manually set, otherwise use sequential time
					const serviceStart = service.start_time || currentStartTime;
					const serviceEnd = calculateEndTime(serviceStart, service.duration);
					const dateSnapshot = ensureServiceDates(
						null,
						null,
						serviceStart,
						serviceEnd,
						service.duration,
						currentMainBaseDate
					);
					const serviceTimelineMeta = getServiceTimelineMeta(serviceStart, dateSnapshot.startDateStr);
					const service_at = `${dateSnapshot.startDateStr} ${serviceStart}`;
					const serviceIdForAvailability = service?.id || service?.service;
					const needsAvailabilityRefresh = shouldRefreshServiceAvailability(
						service.counterKey,
						serviceIdForAvailability,
						service_at,
						service?.duration,
						isExplicitDateSyncRequest
					);

						// Re-fetch therapist availability only when this service's slot/service changed
						// (or on explicit calendar date sync). Unchanged siblings keep cached options.
						let options;
						let didFetchRoster = false;
						if (needsAvailabilityRefresh) {
							const { staffList, ok } = await fetchTherapists(
								serviceIdForAvailability,
								service_at,
								service?.duration,
								{ withStatus: true }
							);
							if (isStaleCreateSyncRequest()) return;
							didFetchRoster = ok;
							options = buildTherapistOptions(staffList);
						} else {
							const cachedOptions = therapistOptionsMapRef.current?.[service.counterKey];
							options = Array.isArray(cachedOptions) && cachedOptions.length
								? cachedOptions
								: buildTherapistOptions(homeStaffList || []);
						}

						// Filter available therapists (not busy with other services)
						const availableOptions = options.filter((opt) => {
							const optionTherapistId = normalizeNumericId(opt?.value);
							if (optionTherapistId == null) return true;
							const allServices = [
								...(selectedServicesSnapshot || []),
								...(paxesSnapshot || []).flatMap((p) => p.selectedServices || []),
							];
							
							return !allServices.some((svc) => {
								const serviceTherapistId = getTherapistId(svc);
								if (serviceTherapistId == null || serviceTherapistId !== optionTherapistId) return false;
								if (!svc.start_time || !svc.end_time) return false;
								if (svc.counterKey === service.counterKey) return false;

								const s1 = convertToMinutes(serviceStart);
								const e1 = convertToMinutes(serviceEnd);
								const s2 = convertToMinutes(svc.start_time);
								const e2 = convertToMinutes(svc.end_time);

								return !(e1 <= s2 || s1 >= e2);
							});
						});

						// Update therapist options map
						if (needsAvailabilityRefresh) {
							setTherapistOptionsMap((prev) => ({
								...prev,
								[service.counterKey]: availableOptions.length
									? availableOptions
									: [{ label: "No Therapist Available", value: "" }],
							}));
						}
						if (isStaleCreateSyncRequest()) return;

						const {
							therapistId: resolvedTherapistId,
							manualSelectedTherapistId,
						} = resolveTherapistForCreateSync(service, availableOptions, {
							// Keep drag/user-assigned therapists after create date/time sync.
							// Availability options for the new slot may omit the current therapist
							// briefly (or due to sibling overlap filter); clearing caused multi-pax
							// draft cards to deselect after calendar drag.
							allowClearWhenUnavailable: false,
						});
						const preservedTherapist = restoreRosterClearedTherapist(service,shouldClearTherapistOffRoster(service,resolvedTherapistId,options,didFetchRoster)? null: resolvedTherapistId,options,didFetchRoster);
						// Re-fetch room availability if in room mode (only when slot changed)
						let updatedRoomId = service.roomId;
						let updatedRoomSegments = service.room_segments;
						if (needsAvailabilityRefresh && isRoomFilterActive && selectedOutletID && service?.id && service.duration) {
							try {
								const roomAvailabilityDateStr = getRoomAvailabilityDateStr(dateStr);
								const roomPayload = {
									date: roomAvailabilityDateStr,
									service_id: service.id || service.service,
									duration: service.duration,
									service_at: `${roomAvailabilityDateStr} ${serviceStart}`,
								};
								const roomCacheKey = `${selectedOutletID}|${roomPayload.service_id}|${roomPayload.duration}|${roomPayload.service_at}`;
								let roomsList = getCachedRoomAvailability(roomCacheKey);
								if (!roomsList) {
									const roomResponse = await getRoomsForCalendar(selectedOutletID, roomPayload);
									if (isStaleCreateSyncRequest()) return;
									roomsList = roomResponse?.data || [];
									setCachedRoomAvailability(roomCacheKey, roomsList);
								} else if (isStaleCreateSyncRequest()) {
									return;
								}
								// If current room is still available, keep it; otherwise clear
								if (roomsList.length > 0) {
									const flatRooms = [];
									roomsList.forEach(room => {
										if (room.items && Array.isArray(room.items)) {
											room.items.forEach(item => {
												// Only include items with empty bookings array (available)
												if (item.bookings && Array.isArray(item.bookings) && item.bookings.length === 0) {
													flatRooms.push({
														value: item.item_id,
														item_id: item.item_id,
														label: item.item_name || item.name,
														item_type: item.item_type || item.item,
														room_type: item.room_type ?? null,
														facilities: Array.isArray(item.facilities) ? item.facilities : [],
														room_id: room.room_id,
														room_name: room.room_name || room.name,
														room_code: room.room_code || room.code,
													});
												}
											});
										} else if (room.item_id || room.value) {
											// Only include if bookings array is empty (available)
											if (!room.bookings || (Array.isArray(room.bookings) && room.bookings.length === 0)) {
												flatRooms.push({
													value: room.item_id || room.value,
													item_id: room.item_id || room.value,
													label: room.item_name || room.name || room.label,
													item_type: room.item_type || room.item,
													room_type: room.room_type ?? null,
													facilities: Array.isArray(room.facilities) ? room.facilities : [],
													room_id: room.room_id,
													room_name: room.room_name || room.name,
													room_code: room.room_code || room.code,
												});
											}
										}
									});
									
									const currentRoomStillAvailable = flatRooms.some(r => 
										(r.value || r.item_id) === updatedRoomId
									);
									
									if (!currentRoomStillAvailable && updatedRoomId) {
										// Current room not available, clear it
										updatedRoomId = null;
										updatedRoomSegments = null;
										setSelectedRooms(prev => {
											const next = { ...prev };
											if (service.counterKey in next) {
												delete next[service.counterKey];
											}
											return next;
										});
									}
								}
							} catch (err) {
								console.error("Error fetching rooms on date/time change:", err);
							}
						}

						// Create selectedTherapist object if therapist exists
						const updateTherapistId = preservedTherapist || null;
						const therapistFromList = Array.isArray(homeStaffList) && homeStaffList.length > 0
							? homeStaffList.find((staff) => Number(staff.id) === Number(updateTherapistId))
							: null;
						const updateSelectedTherapist = buildSelectedTherapistFromOptions(
							updateTherapistId,
							options,
							therapistFromList
						);

						const isMemberRateAppliedValue = !isPremiumOutlet && isTimeBefore6PM(serviceStart) && isMembershipApplied && !publicHoliday && isWeekday(dateSnapshot.startDateStr);
						// Preserve price_rate from original service or update from fetched data
						const serviceWithPriceRate = updatedServiceData && updatedServiceData.price_rate 
							? { ...service, price_rate: updatedServiceData.price_rate }
							: service;
						
						const updatedService = {
							...service,
							// Update price from fetched service list if available, but preserve price_rate
							...(updatedServiceData ? {
								member_price: updatedServiceData.member_price,
								name: updatedServiceData.name || service.name,
								service_category_name: updatedServiceData.service_category_name || updatedServiceData.service_category || service.service_category_name || service.service_category || "",
								...(updatedServiceData.price_rate ? { price_rate: updatedServiceData.price_rate } : {}),
							} : {}),
							therapist: preservedTherapist,
							therapist_name: updateSelectedTherapist?.label || "",
							isAutoAssignedTherapist: service?.isAutoAssignedTherapist === true && preservedTherapist != null && manualSelectedTherapistId == null,
							hasManualTherapistSelection: manualSelectedTherapistId != null && preservedTherapist != null,
							start_time: serviceStart,
							end_time: serviceEnd,
							start_date: dateSnapshot.startDateStr,
							end_date: dateSnapshot.endDateStr,
							dayOffset: serviceTimelineMeta.dayOffset,
							absoluteMinutes: serviceTimelineMeta.absoluteMinutes,
							roomId: updatedRoomId,
							room_segments: updatedRoomSegments,
							selected_therapist: updateSelectedTherapist,
							isBefore6pm: isTimeBefore6PM(serviceStart),
							isMemberRateApplied: isMemberRateAppliedValue,
							isMembershipApplied: isMembershipApplied,
							// Update price based on isMemberRateApplied
							price: getServicePriceFromPriceRate(serviceWithPriceRate, isMemberRateAppliedValue),
						};
						
						updatedMainServices.push(updatedService);
						// Update start time for next service (sequential behavior).
						// dateSnapshot.endDateStr already correctly reflects a midnight rollover
						// (including services that end exactly at "00:00"), so it can be trusted here.
						currentStartTime = serviceEnd;
						currentMainBaseDate = dateSnapshot.endDateStr;
					}

				const firstMainDt = updatedMainServices[0];
				const mainDtAnchor =
					!isPremiumOutlet &&
					firstMainDt &&
					isTimeBefore6PM(firstMainDt.start_time) &&
					isMembershipApplied &&
					!publicHoliday &&
					isWeekday(firstMainDt.start_date || effectiveSelectedDate);
				const syncedMainServices = syncMemberRateAppliedFromFirstServices(updatedMainServices, {
					anchor: mainDtAnchor,
					getPrice: (s, a) => getServicePriceFromPriceRate(s, a),
				});
				if (isStaleCreateSyncRequest()) return;
				// Calendar drag can move services while this debounced API sync is in flight.
				// Do not overwrite newer start times / abs minutes from a stale snapshot (snap-back).
				const latestMain = latestSelectedServicesRef.current || [];
				const mainMovedSinceSnapshot = (selectedServicesSnapshot || []).some((snap) => {
					const cur = latestMain.find((s) => String(s?.counterKey) === String(snap?.counterKey));
					if (!cur) return false;
					const snapAbs = Number(snap?.absoluteMinutes);
					const curAbs = Number(cur?.absoluteMinutes);
					return (
						String(cur?.start_time || "") !== String(snap?.start_time || "") ||
						(Number.isFinite(snapAbs) ? snapAbs : null) !== (Number.isFinite(curAbs) ? curAbs : null)
					);
				});
				if (mainMovedSinceSnapshot) return;
				setSelectedServices(syncedMainServices);
				logEditDateSyncDebug("dateSync:updatedMainServices", {
					dateStr,
					updatedMainServices: updatedMainServices.map((service) => ({
						counterKey: service?.counterKey,
						start_time: service?.start_time,
						end_time: service?.end_time,
						start_date: service?.start_date,
						end_date: service?.end_date,
						therapist: getTherapistId(service),
					})),
				});

				// Update pax services
				const updatedPaxes = await Promise.all(
					paxesSnapshot.map(async (pax, paxIndex) => {
						let paxStartTime = timeValue;
						let paxBaseDate = getTimelineBaseDateStr(selectedTime);
						const updatedPaxServices = [];
						
						for (let serviceIndex = 0; serviceIndex < pax.selectedServices.length; serviceIndex++) {
							const service = pax.selectedServices[serviceIndex];
							// Update service price from fetched service list
							const serviceId = Number(service.id || service.service);
							const updatedServiceData = servicePriceMap.get(serviceId);
							
							const serviceStart = service.start_time || paxStartTime;
							const serviceEnd = calculateEndTime(serviceStart, service.duration);
							const dateSnapshot = ensureServiceDates(
								null,
								null,
								serviceStart,
								serviceEnd,
								service.duration,
								paxBaseDate
							);
							const paxTimelineMeta = getServiceTimelineMeta(serviceStart, dateSnapshot.startDateStr);
							const service_at = `${dateSnapshot.startDateStr} ${serviceStart}`;
							const paxServiceIdForAvailability = service?.id || service?.service;
							const needsPaxAvailabilityRefresh = shouldRefreshServiceAvailability(
								service.counterKey,
								paxServiceIdForAvailability,
								service_at,
								service?.duration,
								isExplicitDateSyncRequest
							);

							// Re-fetch therapist availability only when this pax service's slot changed
							let options;
							let didFetchPaxRoster = false;
							if (needsPaxAvailabilityRefresh) {
								const { staffList, ok } = await fetchTherapists(
									paxServiceIdForAvailability,
									service_at,
									service?.duration,
									{ withStatus: true }
								);
								if (isStaleCreateSyncRequest()) return pax;
								didFetchPaxRoster = ok;
								options = buildTherapistOptions(staffList);
							} else {
								const cachedOptions = therapistOptionsMapRef.current?.[service.counterKey];
								options = Array.isArray(cachedOptions) && cachedOptions.length
									? cachedOptions
									: buildTherapistOptions(homeStaffList || []);
							}

							const availableOptions = options.filter((opt) => {
								const optionTherapistId = normalizeNumericId(opt?.value);
								if (optionTherapistId == null) return true;
								const allServices = [
									...(selectedServicesSnapshot || []),
									...(paxesSnapshot || []).flatMap((p) => p.selectedServices || []),
								];
								
								return !allServices.some((svc) => {
									const serviceTherapistId = getTherapistId(svc);
									if (serviceTherapistId == null || serviceTherapistId !== optionTherapistId) return false;
									if (!svc.start_time || !svc.end_time) return false;
									if (svc.counterKey === service.counterKey) return false;

									const s1 = convertToMinutes(serviceStart);
									const e1 = convertToMinutes(serviceEnd);
									const s2 = convertToMinutes(svc.start_time);
									const e2 = convertToMinutes(svc.end_time);

									return !(e1 <= s2 || s1 >= e2);
								});
							});

							if (needsPaxAvailabilityRefresh) {
								setTherapistOptionsMap((prev) => ({
									...prev,
									[service.counterKey]: availableOptions.length
										? availableOptions
										: [{ label: "No Therapist Available", value: "" }],
								}));
							}
							if (isStaleCreateSyncRequest()) return pax;

							const {
								therapistId: resolvedTherapistId,
								manualSelectedTherapistId,
							} = resolveTherapistForCreateSync(service, availableOptions, {
								// Same as main create sync: do not wipe assigned therapists on pax
								// services when availability options omit them after a drag.
								allowClearWhenUnavailable: false,
							});
							const preservedTherapist = restoreRosterClearedTherapist(service,shouldClearTherapistOffRoster(service,resolvedTherapistId,options,didFetchPaxRoster)? null: resolvedTherapistId,options,didFetchPaxRoster);
							// Create selectedTherapist object if therapist exists
							const paxUpdateTherapistId = preservedTherapist || null;
							const therapistFromList = Array.isArray(homeStaffList) && homeStaffList.length > 0
								? homeStaffList.find((staff) => Number(staff.id) === Number(paxUpdateTherapistId))
								: null;
							const paxUpdateSelectedTherapist = buildSelectedTherapistFromOptions(
								paxUpdateTherapistId,
								options,
								therapistFromList
							);

							const paxIsMemberRateAppliedValue = !isPremiumOutlet && isTimeBefore6PM(serviceStart) && isMembershipApplied && !publicHoliday && isWeekday(dateSnapshot.startDateStr);
							// Preserve price_rate from original service or update from fetched data
							const paxServiceWithPriceRate = updatedServiceData && updatedServiceData.price_rate 
								? { ...service, price_rate: updatedServiceData.price_rate }
								: service;
							
							const updatedPaxService = {
								...service,
								// Update price from fetched service list if available, but preserve price_rate
								...(updatedServiceData ? {
									member_price: updatedServiceData.member_price,
									name: updatedServiceData.name || service.name,
									service_category_name: updatedServiceData.service_category_name || updatedServiceData.service_category || service.service_category_name || service.service_category || "",
									...(updatedServiceData.price_rate ? { price_rate: updatedServiceData.price_rate } : {}),
								} : {}),
								therapist: preservedTherapist,
								therapist_name: paxUpdateSelectedTherapist?.label || "",
								isAutoAssignedTherapist: service?.isAutoAssignedTherapist === true && preservedTherapist != null && manualSelectedTherapistId == null,
								hasManualTherapistSelection: manualSelectedTherapistId != null && preservedTherapist != null,
								start_time: serviceStart,
								end_time: serviceEnd,
								start_date: dateSnapshot.startDateStr,
								end_date: dateSnapshot.endDateStr,
								dayOffset: paxTimelineMeta.dayOffset,
								absoluteMinutes: paxTimelineMeta.absoluteMinutes,
								selected_therapist: paxUpdateSelectedTherapist,
								isBefore6pm: isTimeBefore6PM(serviceStart),
								isMemberRateApplied: paxIsMemberRateAppliedValue,
								isMembershipApplied: isMembershipApplied,
								// Update price based on isMemberRateApplied
								price: getServicePriceFromPriceRate(paxServiceWithPriceRate, paxIsMemberRateAppliedValue),
							};
							
							updatedPaxServices.push(updatedPaxService);
							// Update start time for next service (sequential behavior).
							// dateSnapshot.endDateStr already correctly reflects a midnight rollover.
							paxStartTime = serviceEnd;
							paxBaseDate = dateSnapshot.endDateStr;
						}

						const firstPaxDt = updatedPaxServices[0];
						const paxDtAnchor =
							!isPremiumOutlet &&
							firstPaxDt &&
							isTimeBefore6PM(firstPaxDt.start_time) &&
							isMembershipApplied &&
							!publicHoliday &&
							isWeekday(firstPaxDt.start_date || effectiveSelectedDate);
						const syncedPaxDt = syncMemberRateAppliedFromFirstServices(updatedPaxServices, {
							anchor: paxDtAnchor,
							getPrice: (s, a) => getServicePriceFromPriceRate(s, a),
						});

						return {
							...pax,
							selectedServices: syncedPaxDt,
						};
					})
				);

				if (isStaleCreateSyncRequest()) return;
				setPaxes(updatedPaxes);
				logEditDateSyncDebug("dateSync:updatedPaxes", {
					dateStr,
					updatedPaxes: updatedPaxes.map((pax) => ({
						id: pax?.id,
						selectedServices: (pax?.selectedServices || []).map((service) => ({
							counterKey: service?.counterKey,
							start_time: service?.start_time,
							end_time: service?.end_time,
							start_date: service?.start_date,
							end_date: service?.end_date,
							therapist: getTherapistId(service),
						})),
					})),
				});
				if (isStaleCreateSyncRequest()) return;
				setBookingChanged(true);

				if (activeDateSyncRequestId != null) {
					// Delay the ready signal slightly so the refreshed therapist/room APIs
					// and derived slot state settle before Home/Edit reproject cards.
					setTimeout(() => {
						setCalendarDateSyncSignal((prev) => {
							if (Number(prev?.requestId) !== Number(activeDateSyncRequestId)) {
								return prev;
							}
							return {
								...prev,
								status: "ready",
								refreshKey: Number(prev?.refreshKey || 0) + 1,
							};
						});
					}, 120);
				}
			} catch (err) {
				console.error("Error updating services on date/time change:", err);
				logEditDateSyncDebug("dateSync:error", {
					message: err?.message || String(err),
				});
				const activeDateSyncSignal = latestCalendarDateSyncSignalRef.current;
				if (
					activeDateSyncSignal?.status === "pending" &&
					activeDateSyncSignal?.mode === (fromEdit ? "edit" : "create")
				) {
					setCalendarDateSyncSignal((prev) => ({
						...prev,
						status: "failed",
					}));
				}
			}
		};

		// Debounce to avoid excessive API calls
		const timeoutId = setTimeout(updateAllServicesOnDateTimeChange, 300);
		return () => clearTimeout(timeoutId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
		// Note: publicHoliday is intentionally NOT in the dependency array here because:
		// - When date changes, publicHoliday is fetched asynchronously from API
		// - We don't want to run the full update when publicHoliday changes
		// - Instead, the dedicated useEffect at line 3889 handles isMemberRateApplied updates when publicHoliday changes
	}, [effectiveSelectedDate, selectedTime?.value, selectedOutletID, customerData?.id, isMembershipApplied, selectedOutletData?.outlet_type_id, calendarDateSyncSignal?.requestId, calendarDateSyncSignal?.status, calendarDateSyncSignal?.date, shouldRefreshServiceAvailability, getCachedRoomAvailability, setCachedRoomAvailability]);

    useEffect(() => {
        // In edit mode, do not auto-sequence or bulk-adjust times; edits should be per-item only
        if (fromEdit) return;
		if (!itemData?.service_at || !bookingData?.time?.value) return;
		if (!bookingChanged) return;

		const updateServicesAndSlots = async () => {
			try {
				// Helper function to check if member rate should be applied (using latest values from closure)
				const parseCustomDate = (input) => {
					if (!input || typeof input !== "string") return new Date("");
					const [datePartRaw, ...timeParts] = input.trim().split(/\s+/);
					const datePart = datePartRaw || "";
					const [day, month, year] = datePart.split("-");
					const rawTimePart = timeParts.join(" ").trim();

					if (!day || !month || !year || !rawTimePart) {
						return new Date("");
					}
					
					// Convert 12-hour format to 24-hour format if needed
					let formattedTime = rawTimePart;
					const hasMeridiem = /am|pm/i.test(rawTimePart);
					if (hasMeridiem) {
						const [time, periodRaw] = rawTimePart.split(/\s+/);
						const [hours, minutes = "00"] = (time || "").split(":");
						if (!hours) return new Date("");
						let hour24 = parseInt(hours, 10);
						const period = (periodRaw || "").toUpperCase();
						
						if (period === "PM" && hour24 !== 12) {
							hour24 += 12;
						} else if (period === "AM" && hour24 === 12) {
							hour24 = 0;
						}
						
						formattedTime = `${String(hour24).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
					} else {
						// For 24-hour format, ensure hours and minutes are properly padded
						const [hours, minutes = "00"] = rawTimePart.split(":");
						if (hours !== undefined) {
							const hour24 = parseInt(hours, 10);
							const min = parseInt(minutes, 10);
							if (!isNaN(hour24) && !isNaN(min)) {
								formattedTime = `${String(hour24).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
							}
						}
					}
					
					return new Date(`${year}-${month}-${day}T${formattedTime}`);
				};

				const dateObj = parseCustomDate(itemData.service_at);
				if (isNaN(dateObj.getTime())) {
					console.error("Invalid parsed date from itemData.service_at:",itemData.service_at);
					return;
				}

				const datePart = dateObj.toISOString().split("T")[0];
				const baseStartTime = bookingData.time.value;
				const sortedServices = [...selectedServices].sort(
					(a, b) => a.counterKey - b.counterKey
				);

				let newStartTime = baseStartTime;
				let nextMainBaseDate = getTimelineBaseDateStr(bookingData?.time || selectedTime);
				const updatedSlotsMain = [];
				const updatedServices = [];

				// Main customer slots - sequential by default, but allow manual changes
				for (const service of sortedServices) {
					const duration = Number(service.duration || 15);
					// Use existing time if manually set, otherwise use sequential time
					const serviceStart = service.start_time || newStartTime;
					const serviceEnd = calculateEndTime(serviceStart, duration);
					const dateAnchor = resolveCreateSyncDateAnchor(
						serviceStart,
						service.start_date,
						nextMainBaseDate
					);
					const dateSnapshot = ensureServiceDates(
						service.start_date,
						service.end_date,
						serviceStart,
						serviceEnd,
						service.duration,
						dateAnchor
					);
					const serviceTimelineMeta = getServiceTimelineMeta(serviceStart, dateSnapshot.startDateStr);
					const service_at = `${dateSnapshot.startDateStr} ${serviceStart}`;

					const options = Array.isArray(therapistOptionsMap?.[service.counterKey]) && therapistOptionsMap[service.counterKey].length
						? therapistOptionsMap[service.counterKey]
						: buildTherapistOptions(homeStaffList || []);

					const availableOptions = options.filter((opt) => {
						const optionTherapistId = normalizeNumericId(opt?.value);
						if (optionTherapistId == null) return true;
						// Check if this therapist is busy with other services in the current booking
						const allServices = [
							...(selectedServices || []),
							...(paxes || []).flatMap((p) => p.selectedServices || []),
						];
						
						return !allServices.some((svc) => {
							const serviceTherapistId = getTherapistId(svc);
							if (serviceTherapistId == null || serviceTherapistId !== optionTherapistId) return false;
							if (!svc.start_time || !svc.end_time) return false;
							if (svc.counterKey === service.counterKey) return false;

							const s1 = convertToMinutes(serviceStart);
							const e1 = convertToMinutes(serviceEnd);
							const s2 = convertToMinutes(svc.start_time);
							const e2 = convertToMinutes(svc.end_time);

							return !(e1 <= s2 || s1 >= e2); // overlapping
						});
					});

					// Preserve existing therapists on bookingChanged refresh. Overlap filtering is for
					// dropdown UX only — clearing Guest/pax therapists here caused deselection when
					// adding another primary service that shares the same time window.
					const {
						therapistId: preservedTherapist,
						manualSelectedTherapistId,
					} = resolveTherapistForCreateSync(service, options, { allowClearWhenUnavailable: false });
					setTherapistOptionsMap((prev) => ({
						...prev,
						[service.counterKey]: availableOptions.length
							? availableOptions
							: [{ label: "No Therapist Available", value: "" }],
					}));

					if (preservedTherapist) {
						const rowIndex = getCalendarRowIndexForService({
							start_time: serviceStart,
							start_date: dateSnapshot.startDateStr,
							dayOffset: serviceTimelineMeta.dayOffset,
							absoluteMinutes: serviceTimelineMeta.absoluteMinutes,
						});
						const therapistIndex = homeStaffList.findIndex(
							(t) => Number(t.id) === Number(preservedTherapist)
						);

						if (rowIndex !== -1 && therapistIndex !== -1) {
							updatedSlotsMain.push({
								rowIndex,
								colIndex: therapistIndex,
								duration: service.duration,
								start_time: serviceStart,
								end_time: serviceEnd,
								therapistId: preservedTherapist,
								paxIndex: service.paxIndex ?? null,
								serviceId: service.id,
								date: datePart,
								counterKey: service.counterKey,
							});
						}
					}

					// Create selectedTherapist object if therapist exists
					const timeChangeTherapistId = preservedTherapist || null;
					const therapistFromList = Array.isArray(homeStaffList) && homeStaffList.length > 0
						? homeStaffList.find((staff) => Number(staff.id) === Number(timeChangeTherapistId))
						: null;
					const timeChangeSelectedTherapist = buildSelectedTherapistFromOptions(
						timeChangeTherapistId,
						options,
						therapistFromList
					);

					const serviceIsMemberRateAppliedValue = !isPremiumOutlet && isTimeBefore6PM(serviceStart) && isMembershipApplied && !publicHoliday && isWeekday(dateSnapshot.startDateStr);
					updatedServices.push({
						...service,
						therapist: preservedTherapist || null,
						therapist_name: timeChangeSelectedTherapist?.label || "",
						isAutoAssignedTherapist: service?.isAutoAssignedTherapist === true && preservedTherapist != null && manualSelectedTherapistId == null,
						hasManualTherapistSelection: manualSelectedTherapistId != null && preservedTherapist != null,
						room_segments: service.room_segments || null,
						start_time: serviceStart,
						end_time: serviceEnd,
						start_date: dateSnapshot.startDateStr,
						end_date: dateSnapshot.endDateStr,
						dayOffset:
							typeof service.dayOffset === "number" && !Number.isNaN(service.dayOffset)
								? service.dayOffset
								: serviceTimelineMeta.dayOffset,
						absoluteMinutes:
							typeof service.absoluteMinutes === "number" && !Number.isNaN(service.absoluteMinutes)
								? service.absoluteMinutes
								: serviceTimelineMeta.absoluteMinutes,
						linkedToPrevious: service.linkedToPrevious,
						duration: service.duration,
						requested_therapist: service.requested_therapist || false, // Preserve user's choice
						requested_person: service.requested_person ?? 0,
						requested_room: service.requested_room ?? 0,
						requested_therapist_disabled: false,
						selected_therapist: timeChangeSelectedTherapist,
						isBefore6pm: isTimeBefore6PM(serviceStart),
						isMemberRateApplied: serviceIsMemberRateAppliedValue,
						isMembershipApplied: isMembershipApplied,
						price: getServicePriceFromPriceRate(service, serviceIsMemberRateAppliedValue),
					});

				// Update fallback start for the next service if it has no explicit start_time.
				// dateSnapshot.endDateStr already correctly reflects a midnight rollover.
				newStartTime = serviceEnd;
				nextMainBaseDate = dateSnapshot.endDateStr;
			}

				const firstMainSlot = updatedServices[0];
				const mainSlotAnchor =
					!isPremiumOutlet &&
					firstMainSlot &&
					isTimeBefore6PM(firstMainSlot.start_time) &&
					isMembershipApplied &&
					!publicHoliday &&
					isWeekday(firstMainSlot.start_date || effectiveSelectedDate);
				setSelectedServices(
					syncMemberRateAppliedFromFirstServices(updatedServices, {
						anchor: mainSlotAnchor,
						getPrice: (s, a) => getServicePriceFromPriceRate(s, a),
					})
				);

				// Pax slots
				const updatedSlotsPax = [];

				const updatedPaxes = paxes.map((pax) => {
					let paxStartTime = baseStartTime;
					let nextPaxBaseDate = getTimelineBaseDateStr(bookingData?.time || selectedTime);
					const paxLaneBuilt = pax.selectedServices.map((svc) => {
						const duration = Number(svc.duration || 15);
						// Use existing time if manually set, otherwise use sequential time
						const sStart = svc.start_time || paxStartTime;
						const sEnd = calculateEndTime(sStart, duration);
						const paxDateAnchor = resolveCreateSyncDateAnchor(
							sStart,
							svc.start_date,
							nextPaxBaseDate
						);
						const paxDates = ensureServiceDates(
							svc.start_date,
							svc.end_date,
							sStart,
							sEnd,
							duration,
							paxDateAnchor
						);
						const paxTimelineMeta = getServiceTimelineMeta(sStart, paxDates.startDateStr);
						// Update start time for next service (sequential behavior).
						// paxDates.endDateStr already correctly reflects a midnight rollover.
						paxStartTime = sEnd;
						nextPaxBaseDate = paxDates.endDateStr;

						const rowIndex = getCalendarRowIndexForService({
							start_time: sStart,
							start_date: paxDates.startDateStr,
							dayOffset: paxTimelineMeta.dayOffset,
							absoluteMinutes: paxTimelineMeta.absoluteMinutes,
						});
						const availablePaxOptions = Array.isArray(therapistOptionsMap?.[svc.counterKey]) && therapistOptionsMap[svc.counterKey].length
							? therapistOptionsMap[svc.counterKey]
							: buildTherapistOptions(homeStaffList || []);
						const {
							therapistId: resolvedPaxTherapistId,
							manualSelectedTherapistId: manualSelectedPaxTherapistId,
						} = resolveTherapistForCreateSync(svc, availablePaxOptions, {
							allowClearWhenUnavailable: false,
						});
						const therapistIndex = homeStaffList.findIndex(
							(t) => Number(t.id) === Number(resolvedPaxTherapistId)
						);
						const colIndex = therapistIndex >= 0 ? therapistIndex : 0;

						// Push updated slot to updatedSlotsPax if therapist exists
						if (resolvedPaxTherapistId && rowIndex !== -1) {
							updatedSlotsPax.push({
								rowIndex,
								colIndex,
								duration,
								start_time: sStart,
								end_time: sEnd,
								therapistId: resolvedPaxTherapistId,
								paxIndex: svc.paxIndex ?? pax.id ?? null,
								serviceId: svc.id,
								date: datePart,
								counterKey: svc.counterKey,
							});
						}

						// Create selectedTherapist object if therapist exists
						const paxTimeChangeTherapistId = resolvedPaxTherapistId || null;
						const paxTherapistFromList = Array.isArray(homeStaffList) && homeStaffList.length > 0
							? homeStaffList.find((staff) => Number(staff.id) === Number(paxTimeChangeTherapistId))
							: null;
						const paxTimeChangeSelectedTherapist = buildSelectedTherapistFromOptions(
							paxTimeChangeTherapistId,
							availablePaxOptions,
							paxTherapistFromList
						);

						// Return updated service, preserving key therapist-related fields
						const paxSvcIsMemberRateAppliedValue = !isPremiumOutlet && isTimeBefore6PM(sStart) && isMembershipApplied && !publicHoliday && isWeekday(paxDates.startDateStr);
						return {
							...svc,
							therapist: resolvedPaxTherapistId || null,
							therapist_name: paxTimeChangeSelectedTherapist?.label || "",
							isAutoAssignedTherapist: svc?.isAutoAssignedTherapist === true && resolvedPaxTherapistId != null && manualSelectedPaxTherapistId == null,
							hasManualTherapistSelection: manualSelectedPaxTherapistId != null && resolvedPaxTherapistId != null,
							room_segments: svc.room_segments || null,
							start_time: sStart,
							end_time: sEnd,
							start_date: paxDates.startDateStr,
							end_date: paxDates.endDateStr,
							dayOffset:
								typeof svc.dayOffset === "number" && !Number.isNaN(svc.dayOffset)
									? svc.dayOffset
									: paxTimelineMeta.dayOffset,
							absoluteMinutes:
								typeof svc.absoluteMinutes === "number" && !Number.isNaN(svc.absoluteMinutes)
									? svc.absoluteMinutes
									: paxTimelineMeta.absoluteMinutes,
							linkedToPrevious: svc.linkedToPrevious,
							duration,
							requested_person: svc.requested_person ?? 0,
							requested_therapist: svc.requested_therapist || false, // Preserve user's choice
							requested_therapist_disabled: false, 
							selected_therapist: paxTimeChangeSelectedTherapist,
							isBefore6pm: isTimeBefore6PM(sStart),
							isMemberRateApplied: paxSvcIsMemberRateAppliedValue,
							isMembershipApplied: isMembershipApplied,
							price: getServicePriceFromPriceRate(svc, paxSvcIsMemberRateAppliedValue),
						};
					});

					const firstPaxSlot = paxLaneBuilt[0];
					const paxSlotAnchor =
						!isPremiumOutlet &&
						firstPaxSlot &&
						isTimeBefore6PM(firstPaxSlot.start_time) &&
						isMembershipApplied &&
						!publicHoliday &&
						isWeekday(firstPaxSlot.start_date || effectiveSelectedDate);
					const syncedPaxLane = syncMemberRateAppliedFromFirstServices(paxLaneBuilt, {
						anchor: paxSlotAnchor,
						getPrice: (s, a) => getServicePriceFromPriceRate(s, a),
					});

					return {
						...pax,
						selectedServices: syncedPaxLane,
					};
				});

				setPaxes(updatedPaxes);

				//  REMOVED: Calendar slot update - now handled by centralized sync
			} catch (err) {
				console.error(" Error in updateServicesAndSlots:", err);
			} finally {
				setBookingChanged(false);
			}
		};

        updateServicesAndSlots();
	}, [itemData?.service_at, bookingData?.time?.value, bookingChanged, isMembershipApplied, publicHoliday, therapistOptionsMap]);

	
	useEffect(() => {
		if (!selectedServices.length && !paxes.length) return;

		const initializeRoomOptions = async () => {
			try {
				const itemServiceAtDatePart = typeof itemData?.service_at === "string"
					? itemData.service_at.split(" ")[0]
					: null;
				if (!itemServiceAtDatePart) return;
				//  OPTIMIZED: Only fetch room options for services that don't already have them
				const servicesNeedingRoomOptions = [];
				
				// Check main services
				selectedServices.forEach(service => {
					if (service.start_time && service.duration && !roomOptions[service.counterKey]) {
						servicesNeedingRoomOptions.push({
							service,
							counterKey: service.counterKey,
							service_at: `${itemServiceAtDatePart} ${service.start_time}`,
							serviceId: service.id || service.service,
							duration: service.duration
						});
					}
				});
				
				// Check pax services
				paxes.forEach(pax => {
					pax.selectedServices.forEach(service => {
						if (service.start_time && service.duration && !roomOptions[service.counterKey]) {
							servicesNeedingRoomOptions.push({
								service,
								counterKey: service.counterKey,
								service_at: `${itemServiceAtDatePart} ${service.start_time}`,
								serviceId: service.id || service.service,
								duration: service.duration
							});
						}
					});
				});
			
			} catch (error) {
				console.error("Error initializing room options:", error);
			}
		};

		initializeRoomOptions();
	}, [selectedServices, paxes, itemData?.service_at, selectedOutletID, roomOptions]);

	// Handle room dropdown toggle to fetch room list
	const handleRoomDropdownToggle = async (service, isOpen) => {
		if (!isOpen) return;	
		if (!selectedOutletID) {
			console.error("selectedOutletID is missing:", selectedOutletID);
			return;
		}
		if (!effectiveSelectedDate) {
			console.error("effectiveSelectedDate is missing:", effectiveSelectedDate);
			return;
		}

		// service.start_time is 24h (HH:MM or HH:MM:SS); strip seconds by taking HH:MM only
		const serviceStartTime = service.start_time ? service.start_time.split(':').slice(0, 2).join(':') : '';
		const serviceDateForRoomPayload = getServiceAtDateStr(service) || formatDateToDDMMYYYY(effectiveSelectedDate);
		const roomAvailabilityDateStr = getRoomAvailabilityDateStr(serviceDateForRoomPayload);
		const payload = {
			date: roomAvailabilityDateStr,
			service_id: service.service || service.id,
			duration: service.duration,
			service_at: `${roomAvailabilityDateStr} ${serviceStartTime}`,
		};

		try {
			const generalResponse = await getRoomsForCalendar(selectedOutletID, payload);
			setRoomOptions(generalResponse?.data || []);
		} catch (error) {
			console.error("Error in getRoomsForCalendar:", error);
		}
	};

	// SELECT ROOM HANDLER START 
	const handleRoomSelect = (serviceCounterKey, room) => {
		if (!room) {
			console.error("Invalid room object:", room);
			return;
		}		
		// Handle both formats: direct item from RoomDropdown or room object with value property
		let roomValue;
		if (room.value !== undefined) {
			// Room object with value property (existing format)
			roomValue = room.value;
		} else if (room.item_id !== undefined) {
			// Direct item from RoomDropdown (new format)
			roomValue = room.item_id;
		} else {
			console.error("Room object missing value or item_id property:", room);
			return;
		}
		const normalizedRoomValue = normalizeNumericId(roomValue);
		const normalizedRoomName = normalizedRoomValue === null ? null : (room.label || room.item_name || null);
		const currentService = selectedServices.find(s => s.counterKey === serviceCounterKey);
		const patchedService = currentService ? {
			...currentService,
			roomId: normalizedRoomValue,
			room_segments: buildRoomSegments(currentService, normalizedRoomValue, room),
			roomName: normalizedRoomName,
			// Mark intentional so drag prefill does not overwrite this room on Save.
			hasManualRoomSelection: true,
		} : null;
		setSelectedRooms((prevRooms) => {
			const updatedRooms = {
				...prevRooms,
				[serviceCounterKey]: room, // Store room object for specific service
			};
			//Check if any service still has no room
			const hasMissingRoom = Object.values(updatedRooms).some(
				(r) => !r || (r.value === null || r.value === undefined)
			);
			setNoRoomSelected(hasMissingRoom);
			return updatedRooms;
		});

	// Self Appointment: Update selectedServices
	setSelectedServices((prevServices) =>
		prevServices.map((service) =>
			service.counterKey === serviceCounterKey
				? (patchedService || service)
				: service
		)
	);

		// Scroll to the newly assigned room card (room mode)
		setTimeout(() => {
			scrollToServiceCard(serviceCounterKey, patchedService || {}, interval, homeStaffList);
		}, 600);

		setRoomBookingConflictErrors(prev => ({
			...prev,
			[serviceCounterKey]: normalizedRoomValue !== null && roomHasExternalBookingConflict(room, patchedService || currentService)
				? ROOM_BOOKING_CONFLICT_MESSAGE
				: ""
		}));

		// ADDED: Check room capacity if room is selected (not auto-assign)
		if (normalizedRoomValue !== null) {
			if (patchedService && patchedService.start_time && patchedService.end_time) {
				const capacityCheck = checkRoomCapacityOverlap(
					normalizedRoomValue,
					serviceCounterKey,
					patchedService.start_time,
					patchedService.end_time
				);

				// Update room capacity errors
				setRoomCapacityErrors(prev => ({
					...prev,
					[serviceCounterKey]: capacityCheck.hasOverlap ? capacityCheck.message : ""
				}));
			}
		} else {
			// Clear capacity error for auto-assign
			setRoomCapacityErrors(prev => ({
				...prev,
				[serviceCounterKey]: ""
			}));
		}

	// Emit override so room tag updates during edit
	try {
		if (fromEdit && editableBooking?.id) {
			const itemId = getBookingItemId(patchedService || currentService);
			if (itemId) {
				window.dispatchEvent(new CustomEvent('editOverrideUpdate', {
					detail: {
						bookingId: editableBooking.id,
						itemId,
						roomId: normalizedRoomValue,
						roomName: normalizedRoomName,
					}
				}));
			}
		}
	} catch {}
	};
	// SELECT ROOM HANDLER END

	// ADDED: Function to check room capacity overlaps
	const checkRoomCapacityOverlap = (roomId, serviceCounterKey, serviceStartTime, serviceEndTime) => {
		if (!roomId || !hasRoomCapacityData || !roomCapacityData[roomId]) {
			return { hasOverlap: false, message: "" };
		}

		const roomData = roomCapacityData[roomId];
		const totalCapacity = roomData.total_capacity || 0;
		
		// Count overlapping services for this room
		const allServices = [...selectedServices, ...paxes.flatMap(pax => pax.selectedServices)];
		let overlappingServicesCount = 0;
		
		allServices.forEach(service => {
			// Skip the current service being checked
			if (service.counterKey === serviceCounterKey) return;
			
			// Skip if no room assigned or different room
			if (getRoomId(service) !== Number(roomId)) return;
			
			// Skip if no time information
			if (!service.start_time || !service.end_time) return;
			
			// Check for time overlap
			const s1 = convertToMinutes(serviceStartTime);
			const e1 = convertToMinutes(serviceEndTime);
			const s2 = convertToMinutes(service.start_time);
			const e2 = convertToMinutes(service.end_time);
			
			// Check if times overlap
			if (!(e1 <= s2 || s1 >= e2)) {
				overlappingServicesCount++;
			}
		});
		
		// Total services that would be using this room at the same time
		const totalServicesInRoom = overlappingServicesCount + 1; // +1 for current service
		
		if (totalServicesInRoom > totalCapacity) {
			return {
				hasOverlap: true,
				message: `Room capacity exceeded.`,
				overlappingCount: overlappingServicesCount,
				totalCapacity: totalCapacity
			};
		}
		
		return { hasOverlap: false, message: "" };
	};

	// ADDED: useEffect to re-validate room capacity when services change
	useEffect(() => {
		if (!hasRoomCapacityData) {
			setRoomCapacityErrors((prev) => (
				Object.keys(prev || {}).length > 0 ? {} : prev
			));
			return;
		}
		// Re-validate all room capacity errors when services change
		const allServices = [...selectedServices, ...paxes.flatMap(pax => pax.selectedServices)];
		const newCapacityErrors = {};
		
		allServices.forEach(service => {
			const assignedRoomId = getRoomId(service);
			if (assignedRoomId && service.start_time && service.end_time) {
				const capacityCheck = checkRoomCapacityOverlap(
					assignedRoomId,
					service.counterKey,
					service.start_time,
					service.end_time
				);
				
				if (capacityCheck.hasOverlap) {
					newCapacityErrors[service.counterKey] = capacityCheck.message;
				}
			}
		});
		
		setRoomCapacityErrors(newCapacityErrors);
	}, [selectedServices, paxes, roomCapacityData, hasRoomCapacityData]);

	useEffect(() => {
		const servicesToValidate = [
			...(selectedServices || []),
			...(paxes || []).flatMap((pax) => pax?.selectedServices || []),
		].filter((service) => {
			const selectedRoomId = getRoomId(service);
			return (
				selectedRoomId != null &&
				selectedOutletID &&
				(service?.service || service?.id) &&
				service?.duration &&
				service?.start_time
			);
		});

		if (servicesToValidate.length === 0 || !selectedOutletID) {
			setRoomBookingConflictErrors({});
			return;
		}

		const requestId = roomConflictValidationRequestRef.current + 1;
		roomConflictValidationRequestRef.current = requestId;
		const responseCache = new Map();

		const timeoutId = setTimeout(async () => {
			const nextConflictErrors = {};

			await Promise.all(
				servicesToValidate.map(async (service) => {
					const selectedRoomId = getRoomId(service);
					const serviceDate = getServiceAtDateStr(service);
					const roomAvailabilityDateStr = getRoomAvailabilityDateStr(serviceDate);
					const startTime = String(service?.start_time || "").slice(0, 5);
					const duration = Number(service?.duration) || 0;
					const serviceId = service?.service || service?.id;

					if (!selectedRoomId || !roomAvailabilityDateStr || !startTime || !serviceId || duration <= 0) {
						return;
					}

					const cacheKey = buildRoomConflictCacheKey(
						selectedOutletID,
						roomAvailabilityDateStr,
						serviceId,
						duration,
						startTime
					);
					const itemId = getBookingItemId(service);
					const itemIdKey = itemId != null ? String(itemId) : null;
					const baselineKey = itemIdKey
						? roomSlotBaselineByItemIdRef.current.get(itemIdKey)
						: undefined;
					const slotUnchangedFromBaseline = baselineKey === cacheKey;

					let roomsListPromise = responseCache.get(cacheKey);

					if (!roomsListPromise) {
						const cachedRooms = getCachedRoomAvailability(cacheKey);
						const compatibilityRooms = roomCompatibilityCacheRef.current.get(cacheKey);
						if (cachedRooms) {
							roomsListPromise = Promise.resolve(cachedRooms);
						} else if (slotUnchangedFromBaseline && compatibilityRooms) {
							roomsListPromise = Promise.resolve(compatibilityRooms);
						} else {
							roomsListPromise = getRoomsForCalendar(selectedOutletID, {
								date: roomAvailabilityDateStr,
								service_id: serviceId,
								duration,
								service_at: `${roomAvailabilityDateStr} ${startTime}`,
							})
								.then((response) => {
									if (response?.success !== true || !Array.isArray(response?.data)) {
										return null;
									}
									const rooms = response.data;
									if (slotUnchangedFromBaseline) {
										roomCompatibilityCacheRef.current.set(cacheKey, rooms);
									} else {
										setCachedRoomAvailability(cacheKey, rooms);
									}
									return rooms;
								})
								.catch(() => null);
						}
						responseCache.set(cacheKey, roomsListPromise);
					}

					const roomsList = await roomsListPromise;
					if (roomConflictValidationRequestRef.current !== requestId) {
						return;
					}

					// A failed room request must not be treated as "room not in the list".
					if (!Array.isArray(roomsList)) {
						return;
					}

					// Advance baseline after we have availability for this (changed) slot.
					if (!slotUnchangedFromBaseline && itemIdKey) {
						roomSlotBaselineByItemIdRef.current.set(itemIdKey, cacheKey);
					}

					const matchedRoom = flattenRoomsList(roomsList).find((room) =>
						normalizeNumericId(room?.value || room?.item_id) === selectedRoomId
					);

					if (!matchedRoom) {
						nextConflictErrors[service.counterKey] = INCOMPATIBLE_ROOM_MESSAGE;
						return;
					}

					// Keep booking-conflict checks off the first unchanged edit load.
					if (slotUnchangedFromBaseline && !getCachedRoomAvailability(cacheKey)) {
						return;
					}

					const roomHasDraftOverlap = filterOutAssignedRooms(
						[matchedRoom],
						startTime,
						String(service?.end_time || "").slice(0, 5),
						service?.counterKey
					).length === 0;

					if (roomHasExternalBookingConflict(matchedRoom, service) || roomHasDraftOverlap) {
						nextConflictErrors[service.counterKey] = ROOM_BOOKING_CONFLICT_MESSAGE;
					}
				})
			);

			if (roomConflictValidationRequestRef.current !== requestId) {
				return;
			}

			setRoomBookingConflictErrors(nextConflictErrors);
		}, 250);

		return () => clearTimeout(timeoutId);
	}, [selectedServices, paxes, selectedOutletID, getRoomId, getBookingItemId, buildRoomConflictCacheKey, getCachedRoomAvailability, setCachedRoomAvailability]);

	useEffect(() => {
		if (typeof onRoomConflictChange !== "function") return;
		const messages = [
			...Object.values(roomBookingConflictErrors || {}).filter(Boolean),
			...Object.values(roomCapacityErrors || {}).filter(Boolean),
		];
		onRoomConflictChange(messages);
	}, [roomBookingConflictErrors, roomCapacityErrors, onRoomConflictChange]);

	// Focus the Guest input when a new pax is added
	useEffect(() => {
		if (newlyAddedPaxIdRef.current) {
			const paxId = newlyAddedPaxIdRef.current;
			const inputRef = guestInputRefs.current[paxId];
			
			if (inputRef) {
				// Small delay to ensure DOM is ready
				setTimeout(() => {
					inputRef.focus();
					inputRef.select();
					// Scroll into view
					inputRef.scrollIntoView({
						behavior: 'smooth',
						block: 'center'
					});
					// Clear the ref after focusing
					newlyAddedPaxIdRef.current = null;
				}, 150);
			}
		}
	}, [paxes]);

	const handleServiceRequestChange = (selected, counterKey) => {
		const requestStr = selected.join(",");
		setSelectedServices((prevServices) =>
			prevServices.map((service) =>
				service.counterKey === counterKey
					? { ...service, service_request: requestStr }
					: service
			)
		);
	};

	const handleAddPax = async () => {
		if (isAutoAssigningRef.current) return;

		const currentPaxes = Array.isArray(latestPaxesRef.current) ? latestPaxesRef.current : paxes;
		const currentSelectedServices = Array.isArray(latestSelectedServicesRef.current) ? latestSelectedServicesRef.current : selectedServices;
		const newId = allocateNextPaxId(currentPaxes);
		const newPax = {
			id: newId,
			customer_name: "",
			selectedServices: [],
			showNameInput: true,
		};
		const nextPaxes = [...currentPaxes, newPax];
		const primaryFirstService = (currentSelectedServices || []).find((service) => !service?.isSplitSegment);

		// If there is no primary service to clone, open the new pax service dropdown
		// instead of auto-focusing the pax name field.
		if (!primaryFirstService) {
			newlyAddedPaxIdRef.current = null;
			latestPaxesRef.current = nextPaxes;
			setPaxes(nextPaxes);
			setIsAddingService(newId);
			setShowDropdown(true);
			return;
		}

		isAutoAssigningRef.current = true;
		setIsAutoAssigning(true);
		try {
			// Store the newly added pax ID for focusing
			newlyAddedPaxIdRef.current = newId;
			latestPaxesRef.current = nextPaxes;
			setPaxes(nextPaxes);

			const clonedTimelineSelection = primaryFirstService?.start_time
				? buildSelectedTimePayloadForService(primaryFirstService.start_time, primaryFirstService)
				: selectedTime;
			const clonedPreferredStartDate =
				getTimelineBaseDateStr(clonedTimelineSelection) ||
				primaryFirstService?.start_date ||
				getDefaultServiceDateStr();
			await addServiceToBooking(primaryFirstService, nextPaxes.length - 1, {
				therapistAssignmentMode: "firstAvailable",
				basePaxes: nextPaxes,
				targetPaxId: newId,
				serviceStartOverride: primaryFirstService?.start_time || null,
				preferredStartDateOverride: clonedPreferredStartDate,
				resetCopiedMetadata: true,
				holdAutoAssignLock: true,
			});

			// Don't show dropdown yet
			setIsAddingService(null); // No dropdown tied to new pax yet
			setShowDropdown(false); // Ensure dropdown is closed
		} finally {
			isAutoAssigningRef.current = false;
			setIsAutoAssigning(false);
		}
	};

	const handleDeletePax = (index) => {
		const updated = [...paxes];
		const paxToDelete = updated[index];
		
		// REMOVED: Calendar slot removal - now handled by centralized sync
		
		// In edit mode, track all booking_item_ids from services in this pax for deletion
		if (fromEdit && paxToDelete?.selectedServices) {
			paxToDelete.selectedServices.forEach((service) => {
				if (service.booking_item_id) {
					setDeletedBookingItemIds((prev) => [
						...prev,
						service.booking_item_id,
					]);
				}
			});
		}
		
		// Clear the guest check-in tag for this pax when deleting
		if (paxToDelete?.id && handleSelectGuestCheckin) {
			handleSelectGuestCheckin(null, paxToDelete.id);
		}
		
		updated.splice(index, 1); // remove 1 pax
		setPaxes(updated);
		
		// ADDED: Scroll to appropriate location after pax deletion
		setTimeout(() => {
			const sidebarContent = document.querySelector('.sideBarContent');
			if (sidebarContent) {
				// If there are remaining paxes, scroll to the last one
				if (updated.length > 0) {
					const paxDataElements = sidebarContent.querySelectorAll('[class*="paxData"]');
					if (paxDataElements.length > 0) {
						const lastPaxData = paxDataElements[paxDataElements.length - 1];
						const paxBlock = lastPaxData.closest('div');
						if (paxBlock) {
							paxBlock.scrollIntoView({
								behavior: 'smooth',
								block: 'center'
							});
						}
					} else {
						// Try alternative approach - look for elements with guest icon
						const guestElements = sidebarContent.querySelectorAll('img[src*="guest"]');
						if (guestElements.length > 0) {
							const lastGuestElement = guestElements[guestElements.length - 1];
							const paxBlock = lastGuestElement.closest('div');
							if (paxBlock) {
								paxBlock.scrollIntoView({
									behavior: 'smooth',
									block: 'center'
								});
							}
						}
					}
				} else {
					// If no paxes left, scroll to the main service section
					const mainServiceSection = sidebarContent.querySelector('.serviceContainer');
					if (mainServiceSection) {
						mainServiceSection.scrollIntoView({
							behavior: 'smooth',
							block: 'center'
						});
					}
				}
			}
		}, 500); // Increased delay to ensure DOM is fully updated
	};

	const updateMemberName = (index, name) => {
		const updated = [...paxes];
		updated[index].customer_name = name;
		setPaxes(updated);
	};	

	const normalizeDateForComparison = (dateValue) => {
		if (!dateValue || typeof dateValue !== "string") return null;
		const trimmed = dateValue.trim();
		if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
			return trimmed;
		}
		if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
			const [year, month, day] = trimmed.split("-");
			return `${day}-${month}-${year}`;
		}
		return null;
	};

	const buildServicePayload = (service) => {
		const normalizePayloadDateForBoundary = (dateStr, timeStr) => {
			if (!dateStr || !timeStr || !isOutletExtended) return dateStr;
			const timeMinutes = convertToMinutes(timeStr);
			if (Number.isNaN(timeMinutes) || timeMinutes >= extendedBusinessCutoffMinutes) {
				return dateStr;
			}
			const dateOffset = getServiceDayOffset(dateStr);
			if (dateOffset <= 0) {
				return dateStr;
			}
			return formatDateToDDMMYYYY(addDaysSafe(dateStr, -1));
		};

		const buildPayloadCrossDayBookingItem = (startDate, endDate, startTime, endTime) => {
			if (!startDate || !endDate) {
				return null;
			}

			const startMinutes = convertToMinutes(startTime);
			const endMinutes = convertToMinutes(endTime);
			if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) {
				return null;
			}

			if (isEarlyMorningBoundaryTimeRange(startTime, endTime)) {
				return null;
			}

			if (isMixedNormalAndExtendedCrossDayService(startDate, endDate, startTime, endTime)) {
				return buildMidnightSplitCrossDayItems(startDate, endDate, startTime, endTime);
			}

			if (isOutletExtended && startDate === endDate) {
				if (!isExtendedBoundaryCrossDayService({
					start_date: startDate,
					end_date: endDate,
					start_time: startTime,
				})) {
					return null;
				}

				const singleSegmentDuration = endMinutes > startMinutes
					? endMinutes - startMinutes
					: (endMinutes + (24 * 60)) - startMinutes;

				return [{
					start_time: startTime,
					end_time: endTime,
					duration: singleSegmentDuration,
					start_date: startDate,
					end_date: endDate,
				}];
			}

			if (startDate === endDate) {
				return null;
			}

			if (endMinutes === 0) {
				return null;
			}

			if (!isOutletExtended) {
				const minutesInDay = 24 * 60;
				const firstSegmentDuration = minutesInDay - startMinutes;
				const secondSegmentDuration = endMinutes;

				return [
					{
						start_time: startTime,
						end_time: "00:00",
						duration: firstSegmentDuration,
						start_date: startDate,
						end_date: startDate,
					},
					{
						start_time: "00:00",
						end_time: endTime,
						duration: secondSegmentDuration,
						start_date: endDate,
						end_date: endDate,
					},
				];
			}

			const boundaryAbsoluteMinutes = 1440 + extendedBusinessCutoffMinutes;
			const startAbsoluteMinutes = startMinutes < extendedBusinessCutoffMinutes
				? 1440 + startMinutes
				: startMinutes;
			let endAbsoluteMinutes = endMinutes < extendedBusinessCutoffMinutes
				? 1440 + endMinutes
				: endMinutes;

			if (endAbsoluteMinutes <= startAbsoluteMinutes) {
				endAbsoluteMinutes += 1440;
			}

			if (
				startAbsoluteMinutes >= boundaryAbsoluteMinutes ||
				endAbsoluteMinutes <= boundaryAbsoluteMinutes
			) {
				return null;
			}

			return [
				{
					start_time: startTime,
					end_time: "06:00",
					duration: boundaryAbsoluteMinutes - startAbsoluteMinutes,
					start_date: startDate,
					end_date: startDate,
				},
				{
					start_time: "06:00",
					end_time: endTime,
					duration: endAbsoluteMinutes - boundaryAbsoluteMinutes,
					start_date: endDate,
					end_date: endDate,
				},
			];
		};

		const normalizeTherapistId = (raw) => {
			if (raw == null) return null;
			if (typeof raw === 'object') {
				const val = raw?.value ?? raw?.id ?? raw?.label ?? null;
				if (val == null) return null;
				const n = Number(val);
				return Number.isNaN(n) ? null : n;
			}
			const n = Number(raw);
			return Number.isNaN(n) ? null : n;
		};

		const svcId = Number(service.id || service.service);
		const catalog = servicePriceMap.get(svcId);
		const resolvedMemberPrice = (service?.member_price ?? catalog?.member_price ?? null);
		const resolvedStdPrice = (service?.price ?? catalog?.price ?? null);

		// Don't include price for split segments
		const includePrice = !service.isSplitSegment;

		// Use service.price directly since it's already been calculated correctly with isMemberRateApplied taken into account
		// Fallback to old logic only if service.price is not available
		const servicePrice = service?.price != null ? service.price : (includePrice ? ((isMembershipApplied && resolvedMemberPrice != null) ? resolvedMemberPrice : resolvedStdPrice) : null);
		const finalPrice = isMembershipApplied ? resolvedMemberPrice : resolvedStdPrice;
		// Normalize room_segments so start_time/end_time always match service times
		// (avoids stale times when time was changed but room_segments wasn't updated)
		const normalizedRoomId = getRoomId(service);
		const normalizedRoomSegments = service.room_segments && service.start_time && service.end_time
			? service.room_segments.map((segment) => ({
				...segment,
				room_id: normalizeNumericId(segment?.room_id) ?? normalizedRoomId,
				start_time: service.start_time,
				end_time: service.end_time,
				duration: Number(service.duration),
				item_type: segment.item_type ?? segment.item ?? service?.room_items?.[0]?.item_type ?? null,
				meta_service: segment.meta_service ?? null,
				priority: segment.priority ?? 1,
				requested_room: segment.requested_room ?? service?.requested_room ?? 0,
				room_type: segment.room_type ?? service?.room_segments?.[0]?.room_type ?? null,
				facilities: Array.isArray(segment.facilities)
					? segment.facilities
					: (Array.isArray(service?.room_segments?.[0]?.facilities) ? service.room_segments[0].facilities : []),
			}))
			: (normalizedRoomId != null && service.start_time && service.end_time
				? buildRoomSegments(service, normalizedRoomId, {
					item_type:
						service?.room_segments?.[0]?.item_type ??
						service?.room_segments?.[0]?.item ??
						service?.room_items?.[0]?.item_type ??
						null,
					room_type: service?.room_segments?.[0]?.room_type ?? null,
					facilities: service?.room_segments?.[0]?.facilities,
				})
				: (service.room_segments ?? null));

		const rawStartDate = service.start_date || getDefaultServiceDateStr();
		const rawEndDate = service.end_date || service.start_date || getDefaultServiceDateStr();
		const preserveMixedCrossDayDates = isMixedNormalAndExtendedCrossDayService(
			rawStartDate,
			rawEndDate,
			service.start_time,
			service.end_time
		);
		// Never preserve the old "identical clock window duplicated across two dates" shape.
		// That pattern is not a genuine split — only real split bookings (different times per
		// segment) should ever carry a cross_day_booking_item or is_calendar_expansion: 1.
		const preservePureExpandedDates = false;
		const isEarlyMorningBoundaryPayload = isEarlyMorningBoundaryTimeRange(
			service.start_time,
			service.end_time
		);
		const shouldPreserveActualDates = preserveMixedCrossDayDates;
		let normalizedStartDate;
		let normalizedEndDate;
		if (isEarlyMorningBoundaryPayload) {
			const sameDate = rawEndDate || rawStartDate;
			normalizedStartDate = sameDate;
			normalizedEndDate = sameDate;
		} else {
			normalizedStartDate = shouldPreserveActualDates
				? rawStartDate
				: normalizePayloadDateForBoundary(rawStartDate, service.start_time);
			normalizedEndDate = shouldPreserveActualDates
				? rawEndDate
				: normalizePayloadDateForBoundary(rawEndDate, service.end_time);
		}
		// Normalize addons to [{id, name, quantity}, ...] format
		const normalizedAddons = (() => {
			const raw = service.addons;
			if (!Array.isArray(raw) || raw.length === 0) return [];
			return raw
				.map((a) => ({
					id: Number(a.id ?? a.addon_id ?? a.item_id ?? 0),
					name: String(a.name ?? a.addon_name ?? a.item_name ?? ""),
					quantity: Number(a.quantity ?? 1) || 1,
				}))
				.filter((a) => a.id && a.name);
		})();

		const payload = {
			service: svcId,
			start_time: service.start_time,
			end_time: service.end_time,
			start_date: normalizedStartDate,
			end_date: normalizedEndDate,
			duration: Number(service.duration),
			therapist: getTherapistId(service) ?? normalizeTherapistId(service.therapist),
			// room: service.room ?? null,
			room_segments: normalizedRoomSegments,
			requested_person: service.requested_person ?? 0,
			requested_room: service.requested_room ?? 0,
			price: isPremiumOutlet ? finalPrice : (includePrice ? servicePrice : null),
			quantity: "1",
			service_request: service.service_request || "",
			isMemberRateApplied: !isPremiumOutlet && (service.isMemberRateApplied ?? false),
			isMembershipApplied: service.isMembershipApplied ?? false,
			is_calendar_expansion: 0,
			commission: { tier: null, title: null, rate: "0.00" },
			addons: normalizedAddons,
		};
		//  Edit mode: ensure split segments reuse the base booking_item_id
		if (fromEdit) {
			const baseKey = service.splitParent || service.counterKey;
			const allCurrentServices = [
				...(selectedServices || []),
				...(paxes || []).flatMap((pax) => pax?.selectedServices || []),
			];
			const baseSvc = allCurrentServices.find((s) => !s.isSplitSegment && String(s.counterKey) === String(baseKey));
			const idCandidate = service.booking_item_id || baseSvc?.booking_item_id || service.parent_booking_item_id || null;
			if (idCandidate != null) {
				payload.id = idCandidate;
			} else {
				// no-op
			}
		}

		if (preservePureExpandedDates) {
			payload.cross_day_booking_item = buildExpandedCoverageCrossDayItems(
				payload.end_date,
				payload.start_time,
				payload.end_time
			);
		} else if (payload.start_date && payload.end_date) {
			payload.cross_day_booking_item = buildPayloadCrossDayBookingItem(
				payload.start_date,
				payload.end_date,
				payload.start_time,
				payload.end_time
			);
		}

		const payloadStartMinutes = convertToMinutes(payload.start_time);
		const isSameDayPayloadRange =
			!!payload.start_date &&
			!!payload.end_date &&
			normalizeDateForComparison(payload.start_date) === normalizeDateForComparison(payload.end_date);
		const isMixedBoundaryCrossDayPayload = isMixedNormalAndExtendedCrossDayService(
			payload.start_date,
			payload.end_date,
			payload.start_time,
			payload.end_time
		);
		// Same calendar day after 06:00: no cross_day payload.
		// Extended outlet only: also clear when not a true mixed normal+extended midnight cross
		// (never use !isMixedBoundary when outlet is not extended — it would strip normal midnight splits).
		if (
			!fromEdit &&
			!Number.isNaN(payloadStartMinutes) &&
			payloadStartMinutes >= extendedBusinessCutoffMinutes &&
			(isSameDayPayloadRange || (isOutletExtended && !isMixedBoundaryCrossDayPayload))
		) {
			payload.cross_day_booking_item = null;
		}
		payload.is_calendar_expansion = Array.isArray(payload.cross_day_booking_item) && payload.cross_day_booking_item.length > 0
			? 1
			: 0;

		return payload;
	};
	
	const buildItemsFromAllServices = () => {
		// Fallbacks for when customerData loads late during network fluctuations
		const mainCustomerName = customerData?.name?.trim() || customerData?.name || customerData?.firstname || "Guest";

		// Get guest check-in tag for primary customer (use selectedGuestCheckin directly from context)
		const customerGuestCheckin = getGuestCheckinPayload("customer");

		const selfServices = selectedServices.map((service) => {
			const p = buildServicePayload(service);
			const below12Payload = customerGuestCheckin.below12 === 1 ? { below_12: 1 } : {};
			return {
				...p,
				customer_name: mainCustomerName,
				primary: 1,
				item_number: 1,
				guest_checkin_id: customerGuestCheckin.guestCheckinId,
				guest_checkin_name: customerGuestCheckin.guestCheckinName,
				...below12Payload,
			};
		});

		const paxServices = paxes.flatMap((pax) => {
			// Get guest check-in tag for this pax (use selectedGuestCheckin directly from context)
			const paxGuestCheckin = getGuestCheckinPayload(pax.id);
			
			return pax.selectedServices.map((service) => {
				const p = buildServicePayload(service);
				const below12Payload = paxGuestCheckin.below12 === 1 ? { below_12: 1 } : {};
				// Fallbacks for when pax data loads late during network fluctuations
				const paxCustomerName = pax.customer_name || pax.name || `Guest${getPaxGuestNumber(pax)}`;
				return {
					...p,
					customer_name: paxCustomerName,
					item_number: getPaxGuestNumber(pax) + 1,
					guest_checkin_id: paxGuestCheckin.guestCheckinId,
					guest_checkin_name: paxGuestCheckin.guestCheckinName,
					...below12Payload,
				};
			});
		});

		const allServices = [...selfServices, ...paxServices];

		allServices.forEach((service) => {
			if (!service.cross_day_booking_item) {
				service.cross_day_booking_item = null;
			}
		});

		return allServices;
	};


	useEffect(() => {
		const isGroup = paxes.length > 0;
		const newBookingType = isGroup ? 2 : 1;

		setBookingType(newBookingType);
		setItemData((prev) => ({
			...prev,
			booking_type: newBookingType,
		}));
		// Also update editedItemData if in edit mode

		if (fromEdit) {
			setEditedItemData((prev) => ({
				...prev,
				booking_type: newBookingType,
			}));
		}
	}, [paxes, fromEdit, setEditedItemData]);

	
	// For Edit page
    useEffect(() => {
		const bookingId = editableBooking?.id ?? null;
		const bookingItems = editableBooking?.booking_item;
        if (!bookingId || !bookingItems || typeof bookingItems !== "object") {
			return;
		}
		// FIXED: Only run this effect in edit mode
		if (!fromEdit || !isEditBookingActive) {
			return;
		}
		if (hydratedBookingIdRef.current === bookingId) {
			return;
		}
		hydratedBookingIdRef.current = bookingId;
		roomSlotBaselineByItemIdRef.current.clear();
		roomCompatibilityCacheRef.current.clear();

		let paxIdCounter = 1;

		const selfServices = [];
		const paxMap = {};
		const initialSelectedRooms = {};

		// Define main customer name from first booking_item key
		const bookingItemEntries = Object.entries(bookingItems);

		// Find the primary customer by checking for items with primary === 1
		// This fixes the issue where numeric guest names cause incorrect ordering
		let primaryCustomerKey = null;
		for (const [customerNameKey, services] of bookingItemEntries) {
			if (!Array.isArray(services)) continue;
			const hasPrimaryItem = services.some(item => item.primary === 1 || item.primary === true);
			if (hasPrimaryItem) {
				primaryCustomerKey = customerNameKey;
				break;
			}
		}
		// Fallback to first entry if no primary customer found
		if (!primaryCustomerKey && bookingItemEntries.length > 0) {
			primaryCustomerKey = bookingItemEntries[0][0];
		}

        bookingItemEntries.forEach(([customerNameKey, services], index) => {
			if (!Array.isArray(services)) return;

			const isSelf = customerNameKey === primaryCustomerKey;
			const currentPaxId = isSelf ? null : `P${paxIdCounter}`;

			services.forEach((svc, i) => {
				const roomItem = Array.isArray(svc.room_items) && svc.room_items.length > 0 ? svc.room_items[0] : null;
				// const room_id = roomItem?.room_id || null;
				const room_name = roomItem?.room_name || "";
				
				if (typeof svc?.service_at !== "string" || !svc.service_at.includes(" ")) {
					return;
				}
				const [datePart, timePart] = svc.service_at.split(" ");
				if (!datePart || !timePart) {
					return;
				}
				const [year, month, day] = datePart.split("-");
				if (!year || !month || !day) {
					return;
				}
				const counterKey = isSelf
					? `${i + 1}`
					: `${currentPaxId}-${i + 1}`;
				
				// Calculate start_date and end_date from cross_day_booking_item if available
				let startDate = `${day}-${month}-${year}`;
				let endDate = `${day}-${month}-${year}`;
				if (Array.isArray(svc.cross_day_booking_item) && svc.cross_day_booking_item.length > 0) {
					// Use start_date from first object
					startDate = svc.cross_day_booking_item[0]?.start_date || startDate;
					// Use end_date from last object
					endDate = svc.cross_day_booking_item[svc.cross_day_booking_item.length - 1]?.end_date || endDate;
				}

				// FIXED: Create selectedTherapist object only if therapist_id exists, and look up missing data from homeStaffList
				// Always create selectedTherapist when therapist_id exists, even if homeStaffList is not yet populated
				// Check for therapist_id in multiple possible field names (API might use 'therapist' or 'therapist_id')
				const therapistId = svc.therapist_id || svc.therapist || null;
				let selectedTherapist = null;
				if (therapistId) {
					// Try to find therapist in homeStaffList to get complete data
					// Use optional chaining and provide fallbacks to ensure selectedTherapist is always created
					const therapistFromList = Array.isArray(homeStaffList) && homeStaffList.length > 0
						? homeStaffList.find((staff) => Number(staff.id) === Number(therapistId))
						: null;
					// Get therapist name from multiple possible sources
					// Prioritize: API response therapist_name > homeStaffList name/alias > fallback to "Therapist" placeholder
					const therapistName = svc?.therapist_name 
						|| therapistFromList?.name 
						|| therapistFromList?.alias 
						|| (svc?.therapist && typeof svc.therapist === 'string' ? svc.therapist : null)
						|| "Therapist"; // Fallback placeholder if nothing available
					selectedTherapist = {
						value: Number(therapistId),
						label: therapistName,
						pager: svc?.therapist_pager_number || svc?.pager_number || therapistFromList?.pager_number || "",
						gender: svc?.gender || therapistFromList?.gender || "",
					};
				}
				
				// Try to get price_rate from service data or fetch from cache if not available
				let priceRate = svc.price_rate || null;
				if (!priceRate && svc.service_id) {
					try {
						const serviceId = Number(svc.service_id);
						if (Number.isFinite(serviceId)) {
							const cacheKeys = ["serviceList"];
							for (let i = 0; i < sessionStorage.length; i++) {
								const key = sessionStorage.key(i);
								if (!key || key === "serviceList") continue;
								if (key.startsWith("serviceList_") && !key.endsWith("_etag")) {
									cacheKeys.push(key);
								}
							}

							for (const key of cacheKeys) {
								const categories = parseServiceCategoriesFromCache(sessionStorage.getItem(key));
								for (const category of categories || []) {
									const foundService = (category?.services || []).find(
										(s) => Number(s?.id) === serviceId
									);
									if (foundService?.price_rate) {
										priceRate = foundService.price_rate;
										break;
									}
								}
								if (priceRate) break;
							}
						}
					} catch {}
				}

				const lockedPricing = getLockedBookingItemPricing(svc);
				const savedMembershipApplied = lockedPricing
					? lockedPricing.isMembershipApplied
					: resolveSavedBooleanFlag(svc?.isMembershipApplied, isMembershipApplied);
				const savedMemberRateApplied = lockedPricing
					? lockedPricing.isMemberRateApplied
					: resolveSavedBooleanFlag(
						svc?.isMemberRateApplied,
						isTimeBefore6PM(timePart.slice(0, 5)) && savedMembershipApplied && !publicHoliday && isWeekday(startDate)
					);
				const parsedService = {
					service: Number(svc.service_id),
                    booking_item_id: svc.id ?? svc.booking_item_id ?? null,
					name: svc.service_name || svc.name || svc.service || "",
					service_category_name: svc.service_category_name || svc.service_category || resolveServiceCategoryName(svc),
					duration: Number(svc.duration) || 0,
					start_time: timePart.slice(0, 5),
					start_date: startDate,
					end_time: calculateEndTime(
						timePart.slice(0, 5),
						svc.duration
					),
					end_date: endDate,
					therapist: svc.therapist_id || svc.therapist || null,
					therapist_name: svc?.therapist || svc?.therapist_name || "",
					requested_therapist: svc.requested_person === 1,
					requested_person: svc.requested_person ?? 0,
					requested_room: roomItem?.requested_room ?? svc.requested_room ?? 0,
					requested_therapist_disabled: false, // Allow user to check/uncheck in edit mode
					service_request: svc.service_request,
					selected_therapist: selectedTherapist,
					room_segments: svc.room_segments || svc.room_items || null,
					room_name: room_name || svc.room_name || "",
					// price: svc.parent_booking_item_id ? null : svc?.price,
					counterKey,
                    uid: svc.id || `${svc.service_id || ''}_${counterKey}`,
					splited: svc.splited || null,
					parent_booking_item_id: svc.parent_booking_item_id || null,
					cross_day_booking_item: svc.cross_day_booking_item || null,
					// Preserve price_rate from service data or cache
					price_rate: priceRate,
					isMembershipApplied: savedMembershipApplied,
					isBefore6pm: isTimeBefore6PM(timePart.slice(0, 5)),
					isMemberRateApplied: savedMemberRateApplied,
					price: lockedPricing?.price ?? getServicePriceFromPriceRate({ ...svc, price_rate: priceRate }, savedMemberRateApplied),
					guest_checkin_id: svc.guest_checkin_id || null,
					guest_checkin_name: (svc.guest_first_name || svc.guest_checkin_last_name) 
						? `${svc.guest_first_name || ''} ${svc.guest_checkin_last_name || ''}`.trim() 
						: null,
					below_12: Number(svc.below_12) === 1 || isUnder12CheckinExempt(svc) ? 1 : 0,
					// Load addons from API when editing (editableBooking.booking_item[customerKey][].addons) - normalize to {id, name, quantity} format
					addons: (() => {
						const raw = svc.addons;
						if (!Array.isArray(raw) || raw.length === 0) return [];
						return raw.map((a) => ({
							id: Number(a.id ?? a.addon_id ?? a.item_id ?? 0),
							name: String(a.name ?? a.addon_name ?? a.item_name ?? ""),
							quantity: Number(a.quantity ?? 1) || 1,
						})).filter((a) => a.id && a.name);
					})(),
				};
				const normalizedHydratedRoomId = getRoomId(parsedService);
				if (normalizedHydratedRoomId != null) {
					parsedService.roomId = normalizedHydratedRoomId;
					parsedService.roomName = getRoomName(parsedService);
					initialSelectedRooms[counterKey] = {
						value: normalizedHydratedRoomId,
						item_id: normalizedHydratedRoomId,
						label: parsedService.roomName || svc.room_name || "Room Selected",
						item_name: parsedService.roomName || svc.room_name || "Room Selected",
						item_type: parsedService?.room_segments?.[0]?.item_type ?? svc?.room_items?.[0]?.item_type ?? null,
					};
				}

				// FIXED: For pax services in edit mode, if no therapist was assigned, set to auto-assign
				if (!isSelf && !parsedService.therapist) {
					parsedService.therapist = null; // Ensure auto-assign for pax services without therapists
				}

				if (isSelf) {
					selfServices.push(parsedService);
				} else {
					if (!paxMap[currentPaxId]) {
						paxMap[currentPaxId] = {
							id: currentPaxId,
							customer_name: customerNameKey,
							selectedServices: [],
							showNameInput: true,
						};
					}
					paxMap[currentPaxId].selectedServices.push(parsedService);
				}

				const formatted = `${day}-${month}-${year} ${timePart.slice(
					0,
					5
				)}`;

				// Seed room baseline from API slot (before drag prefill) so first drag
				// only hits room API for services whose time actually changed.
				const hydrateRoomId = getRoomId(parsedService);
				const hydrateServiceId = parsedService.service || svc.service_id;
				const hydrateDuration = Number(parsedService.duration) || 0;
				const hydrateItemId = parsedService.booking_item_id;
				if (
					fromEdit &&
					hydrateItemId != null &&
					hydrateRoomId != null &&
					selectedOutletID &&
					startDate &&
					hydrateServiceId &&
					hydrateDuration > 0 &&
					parsedService.start_time
				) {
					const baselineKey = buildRoomConflictCacheKey(
						selectedOutletID,
						getRoomAvailabilityDateStr(startDate),
						hydrateServiceId,
						hydrateDuration,
						String(parsedService.start_time).slice(0, 5)
					);
					roomSlotBaselineByItemIdRef.current.set(String(hydrateItemId), baselineKey);
				}

				// Therapist options: lazy-load on Staff dropdown open only (no warm-up on edit/drag).
			});

			if (!isSelf) paxIdCounter++;
		});

		setSelectedServices(selfServices);
		setSelectedServicesForEdit(selfServices);
		setPaxes(Object.values(paxMap));
		setSelectedRooms(initialSelectedRooms);
		// Note: selectedServices here is still the previous state (React batches updates). Check next render or use useEffect to verify addons in state.
		// Populate selectedGuestCheckin from guest_checkin_id and guest_checkin_name in edit mode
		if (fromEdit && setSelectedGuestCheckin) {
			const guestCheckinUpdates = {};

			// Check customer-level guest checkin from first self service
			if (selfServices.length > 0) {
				const firstSelfService = selfServices[0];
				if (Number(firstSelfService.below_12) === 1) {
					guestCheckinUpdates['customer'] = {
						item: BELOW_12_TAG_OPTION,
					};
				} else if (firstSelfService.guest_checkin_id && firstSelfService.guest_checkin_name && firstSelfService.guest_checkin_name.trim()) {
					// Parse guest_checkin_name into first_name and last_name
					const nameParts = firstSelfService.guest_checkin_name.trim().split(/\s+/);
					const firstName = nameParts[0] || '';
					const lastName = nameParts.slice(1).join(' ') || '';
					
					guestCheckinUpdates['customer'] = {
						item: {
							id: firstSelfService.guest_checkin_id,
							first_name: firstName,
							last_name: lastName,
							name: firstName, // For backward compatibility
							lastname: lastName, // For backward compatibility
						}
					};
				}
			}

			// Check pax-level guest checkin from each pax's services
			Object.values(paxMap).forEach((pax) => {
				if (pax.selectedServices && pax.selectedServices.length > 0) {
					const firstPaxService = pax.selectedServices[0];
					if (Number(firstPaxService.below_12) === 1) {
						guestCheckinUpdates[pax.id] = {
							item: BELOW_12_TAG_OPTION,
						};
					} else if (firstPaxService.guest_checkin_id && firstPaxService.guest_checkin_name && firstPaxService.guest_checkin_name.trim()) {
						// Parse guest_checkin_name into first_name and last_name
						const nameParts = firstPaxService.guest_checkin_name.trim().split(/\s+/);
						const firstName = nameParts[0] || '';
						const lastName = nameParts.slice(1).join(' ') || '';
						
						guestCheckinUpdates[pax.id] = {
							item: {
								id: firstPaxService.guest_checkin_id,
								first_name: firstName,
								last_name: lastName,
								name: firstName, // For backward compatibility
								lastname: lastName, // For backward compatibility
							}
						};
					}
				}
			});

			// Update selectedGuestCheckin if we found any guest checkin data
			if (Object.keys(guestCheckinUpdates).length > 0) {
				setSelectedGuestCheckin((prev) => ({
					...prev,
					...guestCheckinUpdates,
				}));
			}
		}
		// FIXED: Include editableBooking?.id in dependencies to ensure effect runs when re-entering edit mode
		// even if editableBooking object reference is the same
	}, [editableBooking?.id, editableBooking?.booking_item, isMembershipApplied, publicHoliday, fromEdit, setSelectedGuestCheckin, isEditBookingActive, homeStaffList, resolveSavedBooleanFlag, getLockedBookingItemPricing]);

	useEffect(() => {
		if (!fromEdit || !isEditBookingActive || !editableBooking?.id) {
			hydratedBookingIdRef.current = null;
			serviceAvailabilityKeyRef.current.clear();
			roomSlotBaselineByItemIdRef.current.clear();
			roomCompatibilityCacheRef.current.clear();
		}
	}, [fromEdit, isEditBookingActive, editableBooking?.id]);

	// Clear room availability cache when outlet changes (slot keys are outlet-scoped).
	useEffect(() => {
		roomAvailabilityCacheRef.current.clear();
		roomCompatibilityCacheRef.current.clear();
		serviceAvailabilityKeyRef.current.clear();
		roomSlotBaselineByItemIdRef.current.clear();
	}, [selectedOutletID]);
	
	// Keep sidebar therapist labels in sync with the stored therapist id for both
	// create and edit flows, even when date changes reorder therapist columns.
	useEffect(() => {
		if (!Array.isArray(homeStaffList) || homeStaffList.length === 0) return;

		const syncServiceTherapistLabel = (svc) => {
			const therapistId = getTherapistId(svc);
			if (therapistId == null) return svc;

			const currentSelectedTherapistId = normalizeNumericId(svc?.selected_therapist?.value ?? svc?.selected_therapist?.id);
			const hasValidSelectedTherapist =
				currentSelectedTherapistId === therapistId &&
				typeof svc?.selected_therapist?.label === "string" &&
				svc.selected_therapist.label.trim() !== "";

			if (hasValidSelectedTherapist) {
				return svc;
			}

			const therapistOptions = Array.isArray(therapistOptionsMap?.[svc?.counterKey]) ? therapistOptionsMap[svc.counterKey] : [];
			const therapistFromList = homeStaffList.find((staff) => Number(staff.id) === Number(therapistId));
			const rebuiltSelectedTherapist = buildSelectedTherapistFromOptions(
				therapistId,
				therapistOptions,
				therapistFromList
			);

			if (!rebuiltSelectedTherapist) {
				return svc;
			}

			return {
				...svc,
				therapist: therapistId,
				therapist_name: rebuiltSelectedTherapist.label || svc?.therapist_name || "",
				selected_therapist: rebuiltSelectedTherapist,
			};
		};

		let mainChanged = false;
		const syncedMainServices = selectedServices.map((svc) => {
			const nextSvc = syncServiceTherapistLabel(svc);
			if (nextSvc !== svc) mainChanged = true;
			return nextSvc;
		});

		if (mainChanged) {
			setSelectedServices(syncedMainServices);
		}

		let paxChanged = false;
		const syncedPaxes = paxes.map((pax) => {
			const syncedPaxServices = (Array.isArray(pax?.selectedServices) ? pax.selectedServices : []).map((svc) => {
				const nextSvc = syncServiceTherapistLabel(svc);
				if (nextSvc !== svc) paxChanged = true;
				return nextSvc;
			});

			if (syncedPaxServices === pax?.selectedServices) {
				return pax;
			}

			return {
				...pax,
				selectedServices: syncedPaxServices,
			};
		});

		if (paxChanged) {
			setPaxes(syncedPaxes);
		}
	}, [homeStaffList, therapistOptionsMap, selectedServices, paxes, getTherapistId, normalizeNumericId, buildSelectedTherapistFromOptions]);

	// ADDED: Update isMemberRateApplied for all existing services when isMembershipApplied or publicHoliday changes
	// Anchor = first service in each lane; all services in that lane match the anchor (recomputed from first's time)
	// This effect runs AFTER publicHoliday is updated from the API, ensuring we use the latest value
	useEffect(() => {
		if (fromEdit && preventChanges) return;
		// Skip if no services exist
		if ((!selectedServices || selectedServices.length === 0) && (!paxes || paxes.length === 0)) return;
		// Update main services
		setSelectedServices((prevServices) => {
			if (!prevServices.length) return prevServices;
			const first = prevServices[0];
			const anchor = !isPremiumOutlet && isTimeBefore6PM(first.start_time) && isMembershipApplied && !publicHoliday && isWeekday(first.start_date || effectiveSelectedDate);
			return syncMemberRateAppliedFromFirstServices(prevServices, {
				anchor,
				getPrice: (s, a) => getServicePriceFromPriceRate(s, a),
			}).map((service, i) => ({
				...service,
				isBefore6pm: isTimeBefore6PM(prevServices[i].start_time),
				isMembershipApplied: isMembershipApplied,
			}));
		});

		// Update pax services (each pax lane has its own first-service anchor)
		setPaxes((prevPaxes) =>
			prevPaxes.map((pax) => {
				const list = pax.selectedServices;
				if (!list.length) return pax;
				const first = list[0];
				const anchor = !isPremiumOutlet && isTimeBefore6PM(first.start_time) && isMembershipApplied && !publicHoliday && isWeekday(first.start_date || effectiveSelectedDate);
				return {
					...pax,
					selectedServices: syncMemberRateAppliedFromFirstServices(list, {
						anchor,
						getPrice: (s, a) => getServicePriceFromPriceRate(s, a),
					}).map((service, i) => ({
						...service,
						isBefore6pm: isTimeBefore6PM(list[i].start_time),
						isMembershipApplied: isMembershipApplied,
					})),
				};
			})
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMembershipApplied, publicHoliday, effectiveSelectedDate, memberRateScheduleKey, isPremiumOutlet, fromEdit, preventChanges]);

	// Keep displayed prices in sync with current member-rate flags after schedule changes
	// (e.g. drag/drop in edit flow updates start_time -> member-rate can flip -> price must follow).
	useEffect(() => {
		if (fromEdit && preventChanges) return;
		if ((!selectedServices || selectedServices.length === 0) && (!paxes || paxes.length === 0)) return;

		setSelectedServices((prevServices) => {
			if (!Array.isArray(prevServices) || prevServices.length === 0) return prevServices;
			let changed = false;
			const nextServices = prevServices.map((service) => {
				const recalculatedPrice = getServicePriceFromPriceRate(
					service,
					Boolean(service?.isMemberRateApplied)
				);
				if (service?.price !== recalculatedPrice) {
					changed = true;
					return {
						...service,
						price: recalculatedPrice,
					};
				}
				return service;
			});
			return changed ? nextServices : prevServices;
		});

		setPaxes((prevPaxes) => {
			if (!Array.isArray(prevPaxes) || prevPaxes.length === 0) return prevPaxes;
			let changed = false;
			const nextPaxes = prevPaxes.map((pax) => {
				const list = Array.isArray(pax?.selectedServices) ? pax.selectedServices : [];
				if (list.length === 0) return pax;
				let paxChanged = false;
				const nextServices = list.map((service) => {
					const recalculatedPrice = getServicePriceFromPriceRate(
						service,
						Boolean(service?.isMemberRateApplied)
					);
					if (service?.price !== recalculatedPrice) {
						paxChanged = true;
						return {
							...service,
							price: recalculatedPrice,
						};
					}
					return service;
				});
				if (!paxChanged) return pax;
				changed = true;
				return {
					...pax,
					selectedServices: nextServices,
				};
			});
			return changed ? nextPaxes : prevPaxes;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [memberRateScheduleKey, isMembershipApplied, publicHoliday, effectiveSelectedDate, fromEdit, preventChanges, isPremiumOutlet]);

	useEffect(() => {
		const updated = buildItemsFromAllServices();
		setItems(updated);

		// Calculate cross_day_booking: 1 if:
		// - Any service has start_date !== end_date (service crosses days), OR
		// - Services have different start_date values (services span different days)
		let crossDayBooking = 0;
		if (updated && updated.length > 0) {
			// Check if any service has start_date !== end_date (service crosses days)
			const hasCrossDayService = updated.some(service => 
				service?.start_date && 
				service?.end_date && 
				service.start_date !== service.end_date
			);
			const hasCrossDayBookingItems = updated.some((service) =>
				Array.isArray(service?.cross_day_booking_item) &&
				service.cross_day_booking_item.length > 0
			);
			
			// Check if services have different start_date values (services on different days)
			const firstStartDate = updated[0]?.start_date;
			const hasDifferentStartDates = updated.some(service => 
				service?.start_date && 
				service.start_date !== firstStartDate
			);

			const hasExtendedBoundaryService = updated.some((service) =>
				isExtendedBoundaryCrossDayService(service)
			);
			
			crossDayBooking = (hasCrossDayService || hasCrossDayBookingItems || hasDifferentStartDates || hasExtendedBoundaryService) ? 1 : 0;
		}

		// Update itemData with cross_day_booking
		setItemData((prev) => ({
			...prev,
			cross_day_booking: crossDayBooking,
		}));

		if (fromEdit) {
			setEditedItemData((prev) => ({
				...prev,
				items: JSON.stringify(updated),
				cross_day_booking: crossDayBooking,
			}));
		}
	}, [selectedServices, paxes, fromEdit, isMembershipApplied, selectedGuestCheckin, customerData, getGuestCheckinPayload]);

	// Auto-fill room data when isRoomFilterActive is true and newAppointmentSlots contains room data
	useEffect(() => {
		if (isRoomFilterActive && newAppointmentSlots && newAppointmentSlots.length > 0) {
			const slotWithRoomData = newAppointmentSlots.find(slot => slot.roomData);
			if (slotWithRoomData && slotWithRoomData.roomData) {
				const roomData = slotWithRoomData.roomData;
				// Normalize id/name from matchedItem when present (homeRoomList.items)
				const normalizedRoomId = roomData?.matchedItem?.item_id ?? roomData?.id;
				const normalizedRoomName = roomData?.matchedItem?.item_name ?? roomData?.name;
				
				// Pre-fill room for the first service if no services have rooms selected yet
				if (selectedServices.length > 0 && Object.keys(selectedRooms).length === 0) {
					const firstService = selectedServices[0];
					const roomObject = {
						value: normalizedRoomId,
						label: normalizedRoomName,
						room_id: roomData.room_id,
						room_name: roomData.room_name,
						room_code: roomData.room_code,
						item_type: roomData?.matchedItem?.item_type || roomData?.matchedItem?.item || roomData.item_type,
						room_type: roomData?.matchedItem?.room_type ?? null,
						facilities: Array.isArray(roomData?.matchedItem?.facilities) ? roomData.matchedItem.facilities : [],
					};
					
					setSelectedRooms(prev => ({
						...prev,
						[firstService.counterKey]: roomObject
					}));
					
					// Also update the service with room_segments
					setSelectedServices(prev => 
						prev.map(service => 
							service.counterKey === firstService.counterKey
								? {
									...service,
									// persist roomId so calendar sync includes this card in room view
									roomId: normalizedRoomId,
									room_segments: (() => {
										const rid = normalizeNumericId(normalizedRoomId);
										if (rid == null) return null;
										return buildRoomSegments(service, rid, roomObject);
									})(),
									roomName: normalizedRoomName
								}
								: service
						)
					);
					
					setNoRoomSelected(false);
				}
			}
		}
	}, [isRoomFilterActive, newAppointmentSlots, selectedServices, selectedRooms, setNoRoomSelected, buildRoomSegments, normalizeNumericId]);


	// Unique id generator for client-only services
	const generateUid = () => {
		try { if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID(); } catch {}
		return `${Date.now()}_${uidCounterRef.current++}`;
	};
    // Determine if booking is in-progress (checked-in)
    const normalizeStatus = (s) => String(s || '').toLowerCase();
    const bookingStatus = normalizeStatus(
        editableBooking?.booking_status || editableBooking?.status || editableBooking?.Status
    );
    const isInProgress = (
        bookingStatus.includes('check-in') ||
        bookingStatus.includes('checked-in') ||
        bookingStatus.includes('in progress')
    );

    // Add split segment for a service (max 3 segments total per base)
    const onAddSplit = (baseService, paxIndex = null) => {
        if (!baseService) return;
        const baseKey = baseService.splitParent || baseService.counterKey;
        const isPaxService = paxIndex !== null;
        
        // Get the group based on whether it's a pax or main service
        const group = isPaxService
            ? (paxes[paxIndex]?.selectedServices || []).filter(
                (s) => (s.splitParent || s.counterKey) === baseKey
            )
            : (selectedServices || []).filter(
                (s) => (s.splitParent || s.counterKey) === baseKey
            );
            
        if (group.length >= 3) {
            // max reached
            return;
        }
        
        // Default split should have no start time and 0 duration
        const defaultDuration = 0;

        // Generate counter key based on whether it's pax or main service
        const newCounter = isPaxService
            ? getNextPaxCounterKey(baseService.counterKey.split('-')[0], paxes[paxIndex]?.selectedServices || [])
            : getNextMainCounterKey(selectedServices);
        
        // Determine the correct parent_booking_item_id for the split service
        const getParentBookingItemId = (originalService) => {
            // If the original service already has a parent_booking_item_id, use it
            if (originalService.parent_booking_item_id) {
                return originalService.parent_booking_item_id;
            }
            // Otherwise, use the original service's booking_item_id as the parent
            return originalService.booking_item_id;
        };
        
        // Create selectedTherapist object (null for split segments as therapist is null)
        // Split segments don't have therapists initially, so selectedTherapist is null
        const splitSelectedTherapist = null;
        
        const newSvc = {
            service: Number(baseService.service || baseService.id),
            id: Number(baseService.id || baseService.service),
            name: baseService.name,
            service_category_name: baseService.service_category_name || baseService.service_category || resolveServiceCategoryName(baseService),
            duration: defaultDuration,
            start_time: '',
            start_date: baseService.start_date || getDefaultServiceDateStr(),
            end_time: '',
            end_date: baseService.end_date || baseService.start_date || getDefaultServiceDateStr(),
            therapist: null,
            room: baseService.room || null,
			room_name: baseService.room_name || null,
            room_segments: baseService.room_segments || null,
            requested_person: 0,
            requested_therapist: false,
            requested_therapist_disabled: false,
            booking_item_id: null,
            parent_booking_item_id: getParentBookingItemId(baseService),
            counterKey: newCounter,
            uid: generateUid(),
            splitParent: baseKey,
            isSplitSegment: true,
            splited: "true",
            selected_therapist: splitSelectedTherapist,
        };
        
        if (isPaxService) {
            // Add split segment to pax services
            setPaxes((prev) => {
                const updated = [...prev];
                const pax = { ...updated[paxIndex] };
                const list = Array.isArray(pax.selectedServices) ? [...pax.selectedServices] : [];
                const idx = list.findIndex((s) => s.counterKey === baseService.counterKey);
                if (idx > -1) {
                    const main = { ...list[idx] };
                    const groupBase = Number(main.groupBaseDuration || main.duration || 0);
                    if (!main.groupBaseDuration) main.groupBaseDuration = groupBase;
                    // Mark the original service as splited
                    main.splited = "true";
                    list[idx] = main;
                }
                list.push(newSvc);

                pax.selectedServices = list;
                updated[paxIndex] = pax;
                return updated;
            });
        } else {
            // Add split segment to main services (existing logic)
            setSelectedServices((prev) => {
                const list = Array.isArray(prev) ? [...prev] : [];
                const idx = list.findIndex((s) => s.counterKey === baseService.counterKey);
                if (idx > -1) {
                    const main = { ...list[idx] };
                    const groupBase = Number(main.groupBaseDuration || main.duration || 0);
                    if (!main.groupBaseDuration) main.groupBaseDuration = groupBase;
                    // Mark the original service as splited
                    main.splited = "true";
                    list[idx] = main;
                }
                list.push(newSvc);

                return list;
            });
        }
        setBookingChanged(true);
    };

    return (
		<>
			{/* Self Booking Section */}
			<div className={styles.serviceContainer}>
				{selectedServices.length === 0 && (
					<div
						className={styles.customerSearch}
						onClick={() => setShowServiceDropdown(!showServiceDropdown)}>
						<p>Select service</p>
						<img src={DropArrow} alt="arrow" className="arrowIcon" />
					</div>
				)}
				{/* Service Dropdown */}
				{showServiceDropdown && selectedServices.length === 0 && (
					<ServiceDropdown
						bookingGroupId="main"
						onSelect={(service) => {
							handleServiceSelect(service);
							setShowServiceDropdown(false);
						}}
						onClose={() => setShowServiceDropdown(false)}
					/>
				)}
				{error && <p className={styles.errorMessage}>{error}</p>}

				{/* Already selected services */}
				{selectedServices.length > 0 && (
					<>
                        {selectedServices.filter(s => !s.isSplitSegment).map((service, index) => {
                            const baseKey = service.splitParent || service.counterKey;
                            const group = (selectedServices || []).filter(
                                (s) => (s.splitParent || s.counterKey) === baseKey
                            );
                            const canAddSplit = fromEdit && isInProgress && group.length < 3;
                            return (
                            <ServiceCard
                                key={service.uid || `${service.service || service.id}_${service.booking_item_id || service.counterKey}`}
                                service={service}
                                segments={group}
                                index={index}
                                allowNextDayTimeOptions={
                                    // Always show early-morning clocks; day placement is decided in handleTimeClick.
                                    // +1 is only applied when the service/chain is already cross-day or user picks +1 on first service.
                                    true
                                }
                                therapistOptions={therapistOptionsMap[service.counterKey] || EMPTY_OPTIONS}
                                therapistOptionsAll={therapistOptionsMap}
                                handleTherapistSelect={handleTherapistSelect}
                                handleTherapistDropdownToggle={handleTherapistDropdownToggle}
                                handleRoomSelect={handleRoomSelect}
                                handleRoomDropdownToggle={handleRoomDropdownToggle}
                                handleServiceRequestChange={handleServiceRequestChange}
                                removeService={removeService}
                                selectedRooms={selectedRooms}
                                fromEdit={fromEdit}
                                bookingId={editableBooking?.id}
                                isInProgress={isInProgress}
                                canAddSplit={canAddSplit}
                                onAddSplit={() => onAddSplit(service)}
                                updateService={(counterKey, updates) => {
                                    setSelectedServices((prev) =>
                                        prev.map((s) =>
                                            s.counterKey === counterKey
                                                ? { ...s, ...updates }
                                                : s
                                        )
                                    );
                                 }}
                                //  roomOptions={roomOptions?.[service.counterKey] || EMPTY_OPTIONS}
                                 roomOptions={roomOptions}
                                 isRoomFilterActive={isRoomFilterActive}
                                 onReplaceService={handleReplaceService}
                                 showReplaceDropdown={showReplaceDropdown && replacingServiceKey === service.counterKey}
                                 setShowReplaceDropdown={setShowReplaceDropdown}
                                 replacingServiceKey={replacingServiceKey}
                                 handleServiceReplace={handleServiceReplace}
                                 onTimeClick={handleTimeClick}
                                 onPaymentInProgressAttempt={onPaymentInProgressAttempt}
                                 onSplitDurationChange={(editedKey, newDuration) => {
                                    try {
                                        let nextState = null;
                                        setSelectedServices((prev) => {
                                            const list = Array.isArray(prev) ? [...prev] : [];
                                            const baseKeyLocal = baseKey;
                                            const group = list.map((s,i)=>({s,i})).filter(({s}) => (s.splitParent || s.counterKey) === baseKeyLocal);
                                            if (group.length === 0) return prev;
                                            const baseEntry = group.find(({s}) => !s.isSplitSegment && s.counterKey === baseKeyLocal);
                                            if (!baseEntry) return prev;
                                            const baseIdx = baseEntry.i;
                                            const baseSvc = list[baseIdx];
                                            const baseDur = Number(baseSvc.groupBaseDuration ?? baseSvc.duration ?? 0);
                                            const addM = (hhmm, minutesToAdd) => {
                                                if (!hhmm) return hhmm;
                                                const [h, m] = String(hhmm).split(":").map((n) => parseInt(n, 10));
                                                const total = h * 60 + m + (Number(minutesToAdd) || 0);
                                                const nh = Math.floor(((total % (24 * 60)) + (24 * 60)) % (24 * 60) / 60);
                                                const nm = ((total % 60) + 60) % 60;
                                                return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
                                            };
                                            const editedEntry = group.find(({s}) => s.counterKey === editedKey);
                                            if (!editedEntry) return prev;
                                            // Use the immediate predecessor within the group; fallback to main if none
                                            const editedPos = group.findIndex(({s}) => s.counterKey === editedKey);
                                            if (editedPos === -1) return prev;
                                            const predecessorIdx = (editedPos > 0) ? group[editedPos - 1].i : baseIdx;
                                            // update edited (treat blank as 0 for math, keep UI blank)
                                            const cleared = (newDuration == null || newDuration === "");
                                            const val = cleared ? 0 : Math.max(0, Number(newDuration) || 0);
                                            list[editedEntry.i] = {
                                                ...list[editedEntry.i],
                                                duration: cleared ? null : val,
                                                end_time: addM(list[editedEntry.i].start_time, val)
                                            };
                                            // compute remaining for predecessor (one level up)
                                            const sumExceptPred = group
                                                .map(({i}) => i)
                                                .filter(i => i !== predecessorIdx)
                                                .reduce((acc, gi) => acc + Number(list[gi].duration || 0), 0);
                                            let required = baseDur - sumExceptPred;
                                            if (required < 0) {
                                                // clamp edited down so total fits
                                                const editedCurrent = Number(list[editedEntry.i].duration || 0);
                                                const reduceBy = Math.min(editedCurrent, -required);
                                                const newEdited = editedCurrent - reduceBy;
                                                list[editedEntry.i] = {
                                                    ...list[editedEntry.i],
                                                    duration: newEdited,
                                                    end_time: addM(list[editedEntry.i].start_time, newEdited)
                                                };
                                                required = 0;
                                            }
                                            list[predecessorIdx] = {
                                                ...list[predecessorIdx],
                                                duration: required,
                                                end_time: addM(list[predecessorIdx].start_time, required)
                                            };
                                            // After predecessor duration recalculated, move edited segment to start immediately after predecessor
                                            if (!cleared) {
                                                const predEnd = addM(list[predecessorIdx].start_time, Number(list[predecessorIdx].duration) || 0);
                                                list[editedEntry.i] = {
                                                    ...list[editedEntry.i],
                                                    start_time: predEnd,
                                                    end_time: addM(predEnd, val)
                                                };
                                            }
                                            nextState = list;
                                            return list;
                                        });
                                        setBookingChanged(true);
                                        // Emit override to update height based on earliest therapist, debounced to avoid flicker while typing
                                        try {
                                            if (fromEdit && editableBooking?.id) {
                                                const baseKeyLocal2 = baseKey;
                                                if (overrideTimersRef.current[baseKeyLocal2]) {
                                                    clearTimeout(overrideTimersRef.current[baseKeyLocal2]);
                                                }
                                                overrideTimersRef.current[baseKeyLocal2] = setTimeout(() => {
                                                    const snapshot = nextState || selectedServices;
                                                    const groupNow = (snapshot || []).filter((s) => (s.splitParent || s.counterKey) === baseKeyLocal2);
                                                    const baseNow = groupNow.find((s) => !s.isSplitSegment && (s.counterKey === baseKeyLocal2));
                                                    const withTherapist = groupNow.filter(s => s && s.therapist !== undefined && s.therapist !== null && s.therapist !== "");
                                                    const toMinutes = (hhmm) => { try { return convertToMinutes(String(hhmm || "00:00")); } catch { return 0; } };
                                                    const earliest = withTherapist.length > 0 ? withTherapist.reduce((minS, s) => (toMinutes(s.start_time) < toMinutes(minS.start_time) ? s : minS), withTherapist[0]) : null;
                                                    const itemId = baseNow?.booking_item_id || null;
                                                    const dur = Number((earliest?.duration != null ? earliest.duration : baseNow?.duration) || 0);
                                                    const st = earliest?.start_time || baseNow?.start_time || null;
                                                    const en = st ? calculateEndTime(st, dur) : undefined;
                                                    if (itemId) {
                                                        window.dispatchEvent(new CustomEvent('editOverrideUpdate', {
                                                            detail: {
                                                                bookingId: editableBooking.id,
                                                                itemId,
                                                                start: st,
                                                                end: en,
                                                                duration: dur,
                                                            }
                                                        }));
                                                    }
                                                }, 100);
                                            }
                                        } catch {}
                                    } catch {}
                                 }}
                            />
                            );})}
							
						{preventChanges ? "" : 
						<div className={`${styles.dFlex} ${styles.buttonWrap} ${styles.serviceActionButtons}`}>
							{paxes.length === 0 && (
								<button
									onClick={handleAddPax}
									disabled={isAutoAssigning}
									className={`${styles.serviceActionBtn} ${styles.serviceActionSecondary}`}>
									Add Pax
								</button>
							)}
							<button
								onClick={() => {
									if (isAutoAssigningRef.current) return;
									setShowServiceDropdown(true);
								}}
								disabled={isAutoAssigning}
								className={`${styles.serviceActionBtn} ${styles.serviceActionPrimary}`}>
								Add Service
							</button>
						</div>}
						
					</>
				)}

				{/* Dropdown on second service addition */}
				{showServiceDropdown && selectedServices.length > 0 && (
					<ServiceDropdown
						bookingGroupId="main"
						onSelect={(service) => {
							handleServiceSelect(service);
							setShowServiceDropdown(false);
						}}
						onClose={() => setShowServiceDropdown(false)}
					/>
				)}


			</div>

			{/* Group Booking Section */}
			{paxes.map((pax, paxIndex) => (
				<div key={pax.id} className={styles.paxBlock}>
					{/* Show name input always */}
					{pax.showNameInput &&
						<div className={`${styles.paxData}`}>
						<div className={`${styles.dFlex} ${styles.justifyBetween}`}>
							<div
								className={`${styles.dFlex} ${styles.justifyBetween}`}>
								<img src={Guesticon} alt="guestIcon" />
								<input
									ref={(el) => {
										if (el) {
											guestInputRefs.current[pax.id] = el;
											// Focus if this is the newly added pax
											if (newlyAddedPaxIdRef.current === pax.id) {
												setTimeout(() => {
													el.focus();
													el.select();
													// Scroll into view
													el.scrollIntoView({
														behavior: 'smooth',
														block: 'center'
													});
													// Clear the ref after focusing
													newlyAddedPaxIdRef.current = null;
												}, 100);
											}
										} else {
											delete guestInputRefs.current[pax.id];
										}
									}}
									type="text"
									placeholder={`Guest${getPaxGuestNumber(pax, paxIndex)} (Optional)`}
									value={pax.customer_name || ""}
									onChange={(e) =>
										updateMemberName(
											paxIndex,
											e.target.value
										)
									}
									className={styles.groupiconFormControl}
								/>
							</div>
							{preventChanges ? "" : 
							<div
								className={`${styles.delIconBtn} icon`}
								onClick={() => handleDeletePax(paxIndex)}>
								<TrashIcon/>
							</div>
							}
						</div>

						<TaggingDropdown
							selectedLabel="Not Tagged"
							selectedOption={getSelectedGuestCheckin ? getSelectedGuestCheckin(pax.id) : null}
							onSelect={(user) => handleSelectGuestCheckin && handleSelectGuestCheckin(user, pax.id)}
							onDropdownToggle={handleGetGuestCheckinList}
							options={guestCheckinList || []}
							placeholder="Search name"
							className={styles.taggingDropdownPax}
							guestCheckinRemoteList={guestCheckinRemoteList}
							onGuestCheckinSearchQueryChange={onGuestCheckinSearchQueryChange}
							onGuestCheckinLoadMore={onGuestCheckinLoadMore}
							guestCheckinHasMore={guestCheckinHasMore}
							guestCheckinLoadingMore={guestCheckinLoadingMore}
						/>

					</div>
					}

				{pax.selectedServices.filter(s => !s.isSplitSegment).map((service, index) => {
					const baseKey = service.splitParent || service.counterKey;
					const group = (pax.selectedServices || []).filter(
						(s) => (s.splitParent || s.counterKey) === baseKey
					);
					const canAddSplit = fromEdit && isInProgress && group.length < 3;
					return (
					<ServiceCard
						key={service.counterKey}
						service={service}
						segments={group}
						index={index}
						allowNextDayTimeOptions={
							true
						}
						therapistOptions={therapistOptionsMap?.[service.counterKey] || []}
						therapistOptionsAll={therapistOptionsMap}
						handleTherapistSelect={handleTherapistSelect}
						handleTherapistDropdownToggle={handleTherapistDropdownToggle}
						handleRoomDropdownToggle={handleRoomDropdownToggle}
						isInProgress={isInProgress}
						canAddSplit={canAddSplit}
						onAddSplit={() => onAddSplit(service, paxIndex)}
							handleRoomSelect={(key, room) => {
								if (!room) {
									console.error("Invalid room object:", room);
									return;
								}
								
								// Allow null value for auto-assign
								if (room.value === undefined) {
									console.error("Room object missing value property:", room);
									return;
								}
								
								// Update selectedRooms state for UI display
								setSelectedRooms((prevRooms) => ({
									...prevRooms,
									[key]: room,
								}));
								
							// Update pax service object
							const updated = [...paxes];
							const target = updated[paxIndex].selectedServices.find(
								(s) => s.counterKey === key
							);
							if (target) {
									// Persist chosen room id so calendar sync includes this card in room view
									target.roomId = room.value;
									// Mark intentional so drag prefill does not overwrite this room on Save.
									target.hasManualRoomSelection = true;
								const startTime = target.start_time || "00:00";
								const duration = Number(target.duration) || 0;
								// Calculate end_time if not available
								const endTime = target.end_time && target.end_time !== "00:00:00" 
									? target.end_time 
									: (startTime && duration > 0 ? calculateEndTime(startTime, duration) : "00:00");
								
								target.room_segments = room.value === null ? null : [{
									room_id: room.value,
									item_type: room.item_type || room.item || null,
									meta_service: null,
									start_time: startTime,
									end_time: endTime,
									duration: duration,
									priority: 1,
									requested_room: target.requested_room ?? 0,
									room_type: room.room_type ?? null,
									facilities: Array.isArray(room.facilities) ? room.facilities : [],
								}];
								target.roomName = room.value === null ? null : room.label; // Clear room name for auto-assign
									
									setRoomBookingConflictErrors(prev => ({
										...prev,
										[key]: room.value !== null && roomHasExternalBookingConflict(room, target)
											? ROOM_BOOKING_CONFLICT_MESSAGE
											: ""
									}));

									// ADDED: Check room capacity for pax services
									if (room.value !== null && target.start_time && target.end_time) {
										const capacityCheck = checkRoomCapacityOverlap(
											room.value,
											key,
											target.start_time,
											target.end_time
										);
										
										setRoomCapacityErrors(prev => ({
											...prev,
											[key]: capacityCheck.hasOverlap ? capacityCheck.message : ""
										}));
									} else {
										setRoomCapacityErrors(prev => ({
											...prev,
											[key]: ""
										}));
									}

								// Emit override so room tag updates during edit
								try {
									if (fromEdit && editableBooking?.id && target?.booking_item_id) {
										window.dispatchEvent(new CustomEvent('editOverrideUpdate', {
											detail: {
												bookingId: editableBooking.id,
												itemId: target.booking_item_id,
												roomId: room.value,
												roomName: room.value === null ? null : room.label,
											}
										}));
									}
								} catch {}
									}
									setPaxes(updated);

									// Scroll to the pax room card in room mode
									setTimeout(() => {
										const targetNow = updated[paxIndex].selectedServices.find((s) => s.counterKey === key) || {};
										const updatedSvc = {
											...targetNow,
											roomId: room.value,
										};
										scrollToServiceCard(key, updatedSvc, interval, homeStaffList);
									}, 600);
							}}
							handleServiceRequestChange={(selected, key) => {
								const updated = [...paxes];
								const target = updated[paxIndex].selectedServices.find(
									(s) => s.counterKey === key
								);
								if (target)
									target.service_request = selected.join(",");
								setPaxes(updated);
							}}
							selectedRooms={selectedRooms}
							autoAssignError=""
							isSelfBooking={false}
							removeService={removeService}
							fromEdit={fromEdit}
							bookingId={editableBooking?.id}
							updateService={(counterKey, updates) => {
								const updated = [...paxes];
								const idx = updated[paxIndex].selectedServices.findIndex(
									(s) => s.counterKey === counterKey
								);
								if (idx > -1) {
									updated[paxIndex].selectedServices[idx] = {
										...updated[paxIndex].selectedServices[
											idx
										],
										...updates,
									};
									setPaxes(updated);
								}
							}}
							// roomOptions={roomOptions[service.counterKey] || []}
							roomOptions={roomOptions}
							isRoomFilterActive={isRoomFilterActive}
							onReplaceService={(counterKey) => {
								// Handle pax service replacement
								setReplacingServiceKey(counterKey);
								setShowReplaceDropdown(true);
							}}
						showReplaceDropdown={showReplaceDropdown && replacingServiceKey === service.counterKey}
						setShowReplaceDropdown={setShowReplaceDropdown}
						replacingServiceKey={replacingServiceKey}
						handleServiceReplace={handleServiceReplace}
						onTimeClick={handleTimeClick}
						onPaymentInProgressAttempt={onPaymentInProgressAttempt}
						onSplitDurationChange={(editedKey, newDuration) => {
							try {
								let nextState = null;
								setPaxes((prev) => {
									const updated = [...prev];
									const pax = { ...updated[paxIndex] };
									const list = Array.isArray(pax.selectedServices) ? [...pax.selectedServices] : [];
									const baseKeyLocal = baseKey;
									const group = list.map((s,i)=>({s,i})).filter(({s}) => (s.splitParent || s.counterKey) === baseKeyLocal);
									if (group.length === 0) return prev;
									const baseEntry = group.find(({s}) => !s.isSplitSegment && s.counterKey === baseKeyLocal);
									if (!baseEntry) return prev;
									const baseIdx = baseEntry.i;
									const baseSvc = list[baseIdx];
									const baseDur = Number(baseSvc.groupBaseDuration ?? baseSvc.duration ?? 0);
									const addM = (hhmm, minutesToAdd) => {
										if (!hhmm) return hhmm;
										const [h, m] = String(hhmm).split(":").map((n) => parseInt(n, 10));
										const total = h * 60 + m + (Number(minutesToAdd) || 0);
										const nh = Math.floor(((total % (24 * 60)) + (24 * 60)) % (24 * 60) / 60);
										const nm = ((total % 60) + 60) % 60;
										return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
									};
									const editedEntry = group.find(({s}) => s.counterKey === editedKey);
									if (!editedEntry) return prev;
									// Use the immediate predecessor within the group; fallback to main if none
									const editedPos = group.findIndex(({s}) => s.counterKey === editedKey);
									if (editedPos === -1) return prev;
									const predecessorIdx = (editedPos > 0) ? group[editedPos - 1].i : baseIdx;
									// update edited (treat blank as 0 for math, keep UI blank)
									const cleared = (newDuration == null || newDuration === "");
									const val = cleared ? 0 : Math.max(0, Number(newDuration) || 0);
									list[editedEntry.i] = {
										...list[editedEntry.i],
										duration: cleared ? null : val,
										end_time: addM(list[editedEntry.i].start_time, val)
									};
									// compute remaining for predecessor (one level up)
									const sumExceptPred = group
										.map(({i}) => i)
										.filter(i => i !== predecessorIdx)
										.reduce((acc, gi) => acc + Number(list[gi].duration || 0), 0);
									let required = baseDur - sumExceptPred;
									if (required < 0) {
										// clamp edited down so total fits
										const editedCurrent = Number(list[editedEntry.i].duration || 0);
										const reduceBy = Math.min(editedCurrent, -required);
										const newEdited = editedCurrent - reduceBy;
										list[editedEntry.i] = {
											...list[editedEntry.i],
											duration: newEdited,
											end_time: addM(list[editedEntry.i].start_time, newEdited)
										};
										required = 0;
									}
									list[predecessorIdx] = {
										...list[predecessorIdx],
										duration: required,
										end_time: addM(list[predecessorIdx].start_time, required)
									};
									// After predecessor duration recalculated, move edited segment to start immediately after predecessor
									if (!cleared) {
										const predEnd = addM(list[predecessorIdx].start_time, Number(list[predecessorIdx].duration) || 0);
										list[editedEntry.i] = {
											...list[editedEntry.i],
											start_time: predEnd,
											end_time: addM(predEnd, val)
										};
									}
									pax.selectedServices = list;
									updated[paxIndex] = pax;
									nextState = list;
									return updated;
								});
								setBookingChanged(true);
								// Emit override to update height based on earliest therapist, debounced to avoid flicker while typing
								try {
									if (fromEdit && editableBooking?.id) {
										const baseKeyLocal2 = baseKey;
										if (overrideTimersRef.current[baseKeyLocal2]) {
											clearTimeout(overrideTimersRef.current[baseKeyLocal2]);
										}
										overrideTimersRef.current[baseKeyLocal2] = setTimeout(() => {
											const paxSnapshot = paxes[paxIndex];
											const snapshot = nextState || paxSnapshot?.selectedServices || [];
											const groupNow = (snapshot || []).filter((s) => (s.splitParent || s.counterKey) === baseKeyLocal2);
											const baseNow = groupNow.find((s) => !s.isSplitSegment && (s.counterKey === baseKeyLocal2));
											const withTherapist = groupNow.filter(s => s && s.therapist !== undefined && s.therapist !== null && s.therapist !== "");
											const toMinutes = (hhmm) => { try { return convertToMinutes(String(hhmm || "00:00")); } catch { return 0; } };
											const earliest = withTherapist.length > 0 ? withTherapist.reduce((minS, s) => (toMinutes(s.start_time) < toMinutes(minS.start_time) ? s : minS), withTherapist[0]) : null;
											const itemId = baseNow?.booking_item_id || null;
											const dur = Number((earliest?.duration != null ? earliest.duration : baseNow?.duration) || 0);
											const st = earliest?.start_time || baseNow?.start_time || null;
											const en = st ? calculateEndTime(st, dur) : undefined;
											if (itemId) {
												window.dispatchEvent(new CustomEvent('editOverrideUpdate', {
													detail: {
														bookingId: editableBooking.id,
														itemId,
														start: st,
														end: en,
														duration: dur,
													}
												}));
											}
										}, 100);
									}
								} catch {}
							} catch {}
						}}
					/>
				);})}

					{/* Show dropdown when adding a service to this pax */}
					{showDropdown && isAddingService === pax.id && (
						<ServiceDropdown
							bookingGroupId={pax.id}
							onSelect={(service) => {
								handleServiceSelect(service, paxIndex);
								setShowDropdown(false);
							}}
							onClose={() => setShowDropdown(false)}
						/>
					)}

					{/* Add service & Add pax buttons below input */}
					{preventChanges ? "" : 
						<div
						className={`${styles.dFlex} ${styles.buttonWrap} ${styles.serviceActionButtons}`}>
						<button
							onClick={handleAddPax}
							disabled={isAutoAssigning}
							className={`${styles.serviceActionBtn} ${styles.serviceActionSecondary}`}>
							Add Pax
						</button>
						<button
							onClick={() => {
								if (isAutoAssigningRef.current) return;
								setIsAddingService(pax.id);
								setShowDropdown(true);
							}}
							disabled={isAutoAssigning}
							className={`${styles.serviceActionBtn} ${styles.serviceActionPrimary}`}>
							Add Service
						</button>
					</div>
					}
				</div>
			))}


		</>
	);
};

export default ServiceSelector;